import { withSiteMediaSection } from './ziaFormsSiteMedia';

export type ZiaFormsCategoryId =
  | 'electrical'
  | 'electrical-safety-audits'
  | 'occidental'
  | 'incident-near-miss'
  | 'equipment-inspections'
  | 'safety-programs'
  | 'daily-field-reports'
  | 'crew-sign-off'
  | 'work-at-heights'
  | 'lifting-rigging'
  | 'excavation'
  | 'energy-control'
  | 'confined-space'
  | 'environmental'
  /** Hot-shot / DOT hazmat transport + oilfield waste trip tickets (not EPA e-Manifest replacement). */
  | 'hazmat-transport';

export type ZiaFormFieldType =
  | 'text'
  | 'multiline'
  | 'number'
  | 'date'
  | 'datetime'
  | 'select'
  | 'checkbox-group'
  | 'yes-no'
  | 'yes-no-na'
  | 'pass-fail-na'
  | 'sat-unsat-na'
  | 'attachment'
  | 'signature'
  | 'participants'
  /** Multi-row atmospheric / H₂S gas readings table (JSON string of rows). */
  | 'gas-readings-log'
  /** Related Zia Form templates — chips open linked templates and stamp their titles. */
  | 'linked-forms';

export interface ZiaFormOption {
  value: string;
  label: string;
}

export interface ZiaFormField {
  id: string;
  label: string;
  type: ZiaFormFieldType;
  required?: boolean;
  helperText?: string;
  placeholder?: string;
  options?: ZiaFormOption[];
  /** Template IDs that can be opened from a linked-forms field. */
  linkedTemplateIds?: string[];
}

export interface ZiaFormSection {
  id: string;
  title: string;
  description?: string;
  fields: ZiaFormField[];
}

/**
 * Role lens — who actually fills the form out in the field:
 *  crew    = field hand / operator (pre-task + pre-use checks, first reports)
 *  foreman = supervisor / permit issuer (permits, MOC, crew sign-offs)
 *  hse     = safety professional (audits, plan reviews, investigations)
 * Forms may serve several roles (BBS is deliberately everyone — hands
 * observing hands is the methodology).
 */
export type ZiaFormsAudience = 'crew' | 'foreman' | 'hse' | 'company_man' | 'driver';

export interface ZiaFormTemplate {
  id: string;
  title: string;
  categoryId: ZiaFormsCategoryId;
  description: string;
  sourceMockupFamily: string;
  /** Role lens tags — defaults to all roles when omitted. */
  audience?: ZiaFormsAudience[];
  sections: ZiaFormSection[];
}

export interface ZiaFormsCategory {
  id: ZiaFormsCategoryId;
  title: string;
  description: string;
  accent: string;
}

/**
 * Sky cyan for every electrical pack — crew Electrical Forms and
 * inspector Electrical Safety Audits. Safety Programs stay Zia Forms gold.
 */
export const ZIA_FORMS_ELECTRICAL_ACCENT = '#38bdf8';
export const ZIA_FORMS_SAFETY_PROGRAMS_ACCENT = '#fbbf24';

export const ZIA_FORMS_CATEGORIES: ZiaFormsCategory[] = [
  {
    id: 'electrical',
    title: 'Electrical Forms',
    description: 'Permits, arc flash PPE, and energized-work checklists.',
    accent: ZIA_FORMS_ELECTRICAL_ACCENT,
  },
  {
    id: 'electrical-safety-audits',
    title: 'Electrical Safety Audits',
    description: 'Safety representative audits for oilfield electrical work, LOTO, arc flash controls, energized-work readiness, employee vehicles, tools, PPE, and sign-offs.',
    accent: ZIA_FORMS_ELECTRICAL_ACCENT,
  },
  {
    id: 'occidental',
    title: 'Occidental Forms',
    description: 'OXY location forms for electrical safety, LOTO, energized work, arc flash approvals, risk assessments, job safety planning, and field audits.',
    accent: '#dc2626',
  },
  {
    id: 'incident-near-miss',
    title: 'Incident / Near Miss',
    description: 'Near-miss, first-report, and witness-statement forms.',
    accent: '#f97316',
  },
  {
    id: 'equipment-inspections',
    title: 'Equipment Inspections',
    description: 'Harnesses, lanyards, SRLs, cranes, pumps, hoses, pressure tests, and equipment checks.',
    accent: '#22c55e',
  },
  {
    id: 'safety-programs',
    title: 'Field Safety Inspections',
    description:
      'For the safety guy / HSE — field observations, BBS, stop-work, critical-task checks, and program audits while crews work in the field.',
    accent: ZIA_FORMS_SAFETY_PROGRAMS_ACCENT,
  },
  {
    id: 'daily-field-reports',
    title: 'Daily Field Reports',
    description: 'Daily work summaries and field activity notes.',
    accent: '#f59e0b',
  },
  {
    id: 'crew-sign-off',
    title: 'Crew Sign-Off',
    description: 'Crew acknowledgements, SSE mentorship, visitor orientation, and signatures.',
    accent: '#a855f7',
  },
  {
    id: 'work-at-heights',
    title: 'Work at Heights',
    description: 'Fall rescue, clearance calculations, and scaffold / ladder / access inspections.',
    // Indigo — not gold or electrical cyan, so those inspector accents stay unique.
    accent: '#818cf8',
  },
  {
    id: 'lifting-rigging',
    title: 'Lifting & Rigging',
    description: 'Pre-lift checklists, standard lift plans, and critical lift plans for crane and equipment lifts.',
    accent: '#f59e0b',
  },
  {
    id: 'excavation',
    title: 'Excavation & Trenching',
    description: 'Trenching entry, excavation approval, ground disturbance / one-call, and hydrovac / daylighting.',
    accent: '#b45309',
  },
  {
    id: 'energy-control',
    title: 'Energy Control (LOTO)',
    description: 'Lockout / tagout energy-control forms per OSHA 1910.147.',
    accent: '#ef4444',
  },
  {
    id: 'confined-space',
    title: 'Confined Space',
    description: 'Permit-required confined space entry, atmospheric / H₂S gas testing logs, attendant duties, and rescue.',
    accent: '#0ea5e9',
  },
  {
    id: 'environmental',
    title: 'Environmental / Spills',
    description: 'Spill and environmental release reports — volume, media, notifications, and cleanup.',
    accent: '#10b981',
  },
  {
    id: 'hazmat-transport',
    title: 'Hazmat & Waste Transport',
    description:
      'Company trip tickets for oilfield waste, plus short go/no-go checks that official DOT papers and ER info are present. Does not create shipping papers or EPA e-Manifest — leave those legal documents to shipper/generator systems.',
    accent: '#f43f5e',
  },
];

const yesNoNaOptions: ZiaFormOption[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'na', label: 'N/A' },
];

const yesNoOptions: ZiaFormOption[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const passFailNaOptions: ZiaFormOption[] = [
  { value: 'pass', label: 'Pass' },
  { value: 'fail', label: 'Fail' },
  { value: 'na', label: 'N/A' },
];

const satUnsatNaOptions: ZiaFormOption[] = [
  { value: 'satisfactory', label: 'Sat' },
  { value: 'unsatisfactory', label: 'Unsat' },
  { value: 'na', label: 'N/A' },
];

const safeAtRiskNaOptions: ZiaFormOption[] = [
  { value: 'safe', label: 'Safe' },
  { value: 'at-risk', label: 'At-risk' },
  { value: 'na', label: 'N/A' },
];

const ZIA_FORM_TEMPLATE_DEFS: ZiaFormTemplate[] = [
  {
    id: 'energized-electrical-work-permit',
    title: 'Energized Electrical Work Permit',
    categoryId: 'electrical',
    audience: ['crew', 'foreman'],
    description: 'Permit workflow for energized electrical tasks, hazard controls, PPE, approvals, and signatures.',
    sourceMockupFamily: 'ZiaWellNav field safety form template',
    sections: [
      {
        id: 'general-details',
        title: 'General Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'preparedBy', label: 'Prepared By', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company Working For', type: 'text' },
          { id: 'facilityName', label: 'Location / Facility Name', type: 'text', required: true },
          { id: 'department', label: 'Department', type: 'text' },
          { id: 'supervisorLead', label: 'Supervisor / Lead Name', type: 'text' },
          { id: 'workDescription', label: 'Description of Work', type: 'multiline', required: true },
        ],
      },
      {
        id: 'energy-controls',
        title: 'Control of Hazardous Energy',
        fields: [
          { id: 'voltLevel', label: 'Volt Level of Task', type: 'text', required: true },
          { id: 'atpvEnergy', label: 'Energy / ATPV', type: 'text' },
          { id: 'permitInPlace', label: 'Permit in Place', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'operationsNotified', label: 'Operations Notified', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'upstreamDisconnect', label: 'Next Upstream Disconnecting Device', type: 'text' },
          { id: 'unusualCircumstances', label: 'Unusual Circumstances', type: 'multiline' },
          { id: 'canDeEnergize', label: 'Can Equipment Be De-Energized?', type: 'yes-no', required: true },
          { id: 'equipmentIsolated', label: 'Equipment Isolated', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'jsaCompleted', label: 'JSA Completed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'flashBoundary', label: 'Flash Boundary', type: 'text' },
          { id: 'qualifiedWorker', label: 'Qualified / Trained Worker', type: 'yes-no', required: true },
          { id: 'energizedWorkJustification', label: 'Energized Work Justification', type: 'multiline', helperText: 'Explain why the work cannot reasonably be completed de-energized.' },
          { id: 'deEnergizeFirstReview', label: 'De-energize-first decision reviewed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'lotoIsolationPlanReviewed', label: 'LOTO / isolation plan reviewed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'testBeforeTouchVerified', label: 'Test-before-touch completed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'electricalStopWorkAuthorityReviewed', label: 'Stop-work authority reviewed with crew?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'employeesPerformingWork', label: 'Employee(s) Performing Work', type: 'participants' },
        ],
      },
      {
        id: 'special-equipment',
        title: 'Special Equipment Required',
        fields: [
          {
            id: 'requiredEquipment',
            label: 'Required Equipment',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'voltage-rated-gloves', label: 'Voltage rated gloves with leather protectors' },
              { value: 'insulated-sleeves', label: 'Insulated sleeves' },
              { value: 'high-voltage-voltmeter', label: 'High voltage voltmeter' },
              { value: 'voltage-rated-tools', label: 'Voltage rated tools' },
              { value: 'arc-face-shield', label: 'Electric arc face shield' },
              { value: 'safety-grounds', label: 'Safety grounds' },
              { value: 'arc-flash-clothing', label: 'Electrical arc flash clothing' },
              { value: 'mitigation-plan', label: 'Mitigation plan' },
              { value: 'insulating-blankets', label: 'Insulating blankets' },
              { value: 'voltage-detector', label: 'Voltage detector' },
              { value: 'fall-protection', label: 'Fall protection' },
              { value: 'barrier-tape', label: 'Barrier tape / barricades' },
            ],
          },
          { id: 'comments', label: 'Comments', type: 'multiline' },
          { id: 'attachments', label: 'Attachments', type: 'attachment' },
        ],
      },
      {
        id: 'restricted-boundary-approval',
        title: 'Restricted Boundary Entry / Approval',
        description: 'Approval level depends on voltage and the restricted approach boundary in the work area.',
        fields: [
          {
            id: 'boundaryVoltageRange',
            label: 'Restricted Boundary Voltage Range',
            type: 'select',
            required: true,
            options: [
              { value: '50-300v', label: '50-300V' },
              { value: '300-600v', label: '300-600V' },
              { value: '600-15100v', label: '600-15,100V' },
              { value: 'over-15100v', label: 'Over 15,100V' },
            ],
          },
          { id: 'restrictedBoundaryEntered', label: 'Will employees enter the restricted boundary?', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'supervisorApproval', label: 'Supervisor / Lead Approval', type: 'signature' },
          { id: 'operationsApproval', label: 'Operations Approval', type: 'signature' },
          { id: 'boundaryNotes', label: 'Boundary / Approval Notes', type: 'multiline' },
        ],
      },
      {
        id: 'signoff',
        title: 'Comments & Sign-Off',
        fields: [
          { id: 'finalComments', label: 'Final Comments', type: 'multiline' },
          { id: 'operationsRepSignature', label: 'Operations Rep Signature', type: 'signature' },
          { id: 'electricianSignature', label: 'Electrician Signature', type: 'signature', required: true },
          { id: 'foremanSignature', label: 'Foreman Signature', type: 'signature' },
          { id: 'projectManagerSignature', label: 'Project Manager Signature', type: 'signature' },
        ],
      },
    ],
  },
  {
    id: 'arc-flash-shock-ppe-checklist',
    title: 'Arc Flash & Shock PPE Checklist',
    categoryId: 'electrical',
    audience: ['crew'],
    description: 'Inspection checklist for gloves, sleeves, hood/face shield, arc clothing, and PPE sign-off.',
    sourceMockupFamily: 'ZiaWellNav field safety form template',
    sections: [
      {
        id: 'general-details',
        title: 'General Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'supervisorLead', label: 'Supervisor / Lead', type: 'text' },
          { id: 'companyWorkingFor', label: 'Company Working For', type: 'text' },
          { id: 'facilityName', label: 'Location / Facility Name', type: 'text' },
          { id: 'preparedBy', label: 'Prepared By', type: 'text', required: true },
          { id: 'qualifiedWorkers', label: 'Workers Qualified / Trained?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'arcFlashLabelReviewed', label: 'Arc flash label or hazard information reviewed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'ppeMatchesExposure', label: 'PPE selected for the expected exposure?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'hearingProtection', label: 'Hearing Protection Needed?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'rubber-gloves',
        title: 'Rubber Insulating Gloves & Leather Protectors',
        description: 'Inspect for correct voltage, holes, punctures, cracks, tears, metal particles, oil, and chemical contamination.',
        fields: [
          { id: 'glovesCorrectVoltage', label: 'Correct voltage rating', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'glovesHolesPunctures', label: 'No holes or punctures', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'glovesCracks', label: 'No cracks', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'glovesRipsTears', label: 'No rips or tears', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'glovesContamination', label: 'No oil or chemical contamination', type: 'pass-fail-na', options: passFailNaOptions },
        ],
      },
      {
        id: 'rubber-sleeves',
        title: 'Rubber Sleeves',
        fields: [
          { id: 'sleevesCorrectVoltage', label: 'Correct voltage rating', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'sleevesHolesPunctures', label: 'No holes or punctures', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'sleevesCracks', label: 'No cracks', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'sleevesRipsTears', label: 'No rips or tears', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'sleevesContamination', label: 'No oil or chemical contamination', type: 'pass-fail-na', options: passFailNaOptions },
        ],
      },
      {
        id: 'hood-face-shield',
        title: 'Hood / Face Shield',
        fields: [
          { id: 'hoodNoDamage', label: 'No damage', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'hoodNoOilChemical', label: 'No oil or chemical contamination', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'hoodNoHoles', label: 'No holes or deterioration', type: 'pass-fail-na', options: passFailNaOptions },
        ],
      },
      {
        id: 'arc-clothing',
        title: 'Jacket / Bib Overalls',
        fields: [
          { id: 'jacketPunctures', label: 'No holes or punctures', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'jacketDeterioration', label: 'No deterioration', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'jacketContamination', label: 'No oil or chemical saturation', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'jacketRipsTears', label: 'No rips or tears', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'jacketHardware', label: 'Zipper / hardware serviceable', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'comments', label: 'Comments', type: 'multiline' },
          { id: 'attachments', label: 'Attachments', type: 'attachment' },
        ],
      },
      {
        id: 'signoff',
        title: 'Comments & Sign-Off',
        fields: [
          { id: 'additionalComments', label: 'Additional Comments', type: 'multiline' },
          { id: 'completionDateTime', label: 'Completion Date & Time', type: 'datetime' },
          { id: 'auditorName', label: 'Auditor Name', type: 'text', required: true },
          { id: 'auditorSignature', label: 'Auditor Signature', type: 'signature', required: true },
        ],
      },
    ],
  },
  {
    id: 'hot-crossing-phase-holder-live-line-checklist',
    title: 'Hot Crossing / Phase Holder / Live-Line Checklist',
    categoryId: 'electrical',
    audience: ['crew', 'foreman'],
    description: 'Live-line crossing checklist for phase holders, line ownership, voltage, barricades, tools, and crew sign-off.',
    sourceMockupFamily: 'ZiaWellNav field safety form template',
    sections: [
      {
        id: 'general-details',
        title: 'General Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'preparedBy', label: 'Prepared By', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company Working For', type: 'text' },
          { id: 'facilityName', label: 'Location / Facility Name', type: 'text', required: true },
          { id: 'crossingOwner', label: 'Owner of Line / Voltage Crossing', type: 'text', required: true },
          { id: 'crossingVoltage', label: 'Voltage Crossing Verified', type: 'text', required: true },
          { id: 'lineOwnerPermission', label: 'Owner Permission to Cross?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
        ],
      },
      {
        id: 'pre-planning',
        title: 'Pre-Planning / Authorization',
        fields: [
          { id: 'prePlanningMeeting', label: 'Pre-planning meeting completed on site?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'safeWorkPracticeReviewed', label: 'Electrical safe-work practice reviewed with crew?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'stopWorkAuthorityReviewed', label: 'Stop-work authority reviewed before live-line work?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'oneCallRequired', label: 'One Call required / completed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'hotWorkPermitComplete', label: 'Hot work permit complete?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'barricadesExclusionZone', label: 'Barricades / exclusion zone installed?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'handVoiceSignalsReviewed', label: 'Hand and voice signals reviewed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'staffQualified', label: 'Staff qualified for live-line crossing work?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
        ],
      },
      {
        id: 'tools-equipment',
        title: 'Tools / Equipment Inspection',
        fields: [
          { id: 'phaseHolderInspected', label: 'Phase holder inspected prior to use?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'groundMenGloves', label: 'Ground men wearing proper class gloves?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'hotStickInspected', label: 'Hot stick inspected?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'shotgunsInspected', label: 'Shotguns inspected?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'insulatedTools', label: 'Insulated tools inspected before use?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'dielectricTested', label: 'Equipment dielectric tested?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'chicagoGripsWireSocks', label: 'Chicago grips / wire socks checked?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'certificationsChecked', label: 'Certifications checked?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'line-clearance',
        title: 'Line Clearance / Crossing Controls',
        fields: [
          { id: 'correctCoveragePerPhase', label: 'Correct coverage per phase?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'phaseHolderClearance', label: 'Phase holders maintain line clearance?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'additionalHighVoltageReview', label: 'Additional high-voltage review completed when required?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'lineClearanceNotes', label: 'Line Clearance Notes', type: 'multiline' },
          { id: 'attachments', label: 'Attachments', type: 'attachment' },
        ],
      },
      {
        id: 'signoff',
        title: 'Foreman / Crew Sign-Off',
        fields: [
          { id: 'additionalComments', label: 'Additional Comments', type: 'multiline' },
          { id: 'foremanOnSiteName', label: 'Foreman On-Site Name', type: 'text', required: true },
          { id: 'employeeCount', label: 'Employee Count', type: 'number' },
          { id: 'crewMembers', label: 'Employees Performing Work', type: 'participants' },
          { id: 'foremanSignature', label: 'Foreman Signature', type: 'signature', required: true },
        ],
      },
    ],
  },
  {
    id: 'oilfield-electrical-safety-audit',
    title: 'Oilfield Electrical Safety Audit',
    categoryId: 'electrical-safety-audits',
    audience: ['hse'],
    description: 'Safety representative audit for oilfield electrical jobsite controls, shock and arc hazards, pre-task planning, evidence, and sign-off.',
    sourceMockupFamily: 'ZiaWellNav electrical safety audit form template',
    sections: [
      {
        id: 'audit-details',
        title: 'Electrical Audit Details',
        fields: [
          { id: 'dateTime', label: 'Audit Date & Time', type: 'datetime', required: true },
          { id: 'facilityName', label: 'Facility / Location', type: 'text', required: true },
          { id: 'operatorCustomer', label: 'Operator / Customer', type: 'text', required: true },
          { id: 'crewCompany', label: 'Crew / Contractor Company', type: 'text', required: true },
          { id: 'electricalScope', label: 'Electrical Work Scope', type: 'multiline', required: true },
          { id: 'safetyRepresentative', label: 'Safety Representative', type: 'text', required: true },
          { id: 'workersObserved', label: 'Workers Observed', type: 'participants', required: true },
          { id: 'voltageEquipmentType', label: 'Voltage / Equipment Type', type: 'text', required: true },
        ],
      },
      {
        id: 'pre-task-controls',
        title: 'Pre-Task / Jobsite Controls',
        fields: [
          { id: 'jsaReviewed', label: 'JSA / pre-task plan reviewed with crew?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'qualifiedWorkersVerified', label: 'Qualified electrical workers verified?', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'permitsReviewed', label: 'Required permits reviewed?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'barricadesSet', label: 'Barricades / access controls in place?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'housekeepingAccess', label: 'Housekeeping / access notes', type: 'multiline' },
        ],
      },
      {
        id: 'hazards-findings',
        title: 'Shock / Arc Hazard Findings',
        fields: [
          { id: 'shockHazardsObserved', label: 'Shock hazards observed?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'arcFlashHazardsObserved', label: 'Arc flash hazards observed?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          {
            id: 'riskSeverity',
            label: 'Highest Finding Severity',
            type: 'select',
            required: true,
            options: [
              { value: 'low', label: 'Low' },
              { value: 'medium', label: 'Medium' },
              { value: 'high', label: 'High' },
              { value: 'critical', label: 'Critical' },
            ],
          },
          {
            id: 'riskLikelihood',
            label: 'Likelihood',
            type: 'select',
            required: true,
            options: [
              { value: 'unlikely', label: 'Unlikely' },
              { value: 'possible', label: 'Possible' },
              { value: 'likely', label: 'Likely' },
              { value: 'imminent', label: 'Imminent' },
            ],
          },
          { id: 'findingSummary', label: 'Finding Summary', type: 'multiline', required: true },
          { id: 'immediateActionTaken', label: 'Immediate Action Taken', type: 'multiline' },
          { id: 'correctiveActionNeeded', label: 'Corrective Action Needed?', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'recommendedCorrectiveAction', label: 'Recommended Corrective Action', type: 'multiline' },
          { id: 'recommendedDueDate', label: 'Recommended Due Date', type: 'date' },
          { id: 'evidenceAttachment', label: 'Photos / Evidence', type: 'attachment' },
        ],
      },
      {
        id: 'signoff',
        title: 'Safety Representative / Supervisor Sign-Off',
        fields: [
          { id: 'safetyRepresentativeSignature', label: 'Safety Representative Signature', type: 'signature', required: true },
          { id: 'supervisorOnLocation', label: 'Supervisor on location?', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'supervisorName', label: 'Supervisor Name', type: 'text' },
          { id: 'supervisorSignature', label: 'Supervisor Signature', type: 'signature' },
          { id: 'supervisorNotPresentNotes', label: 'Supervisor not present / notified notes', type: 'multiline' },
        ],
      },
    ],
  },
  {
    id: 'electrical-loto-field-audit',
    title: 'Electrical LOTO Field Audit',
    categoryId: 'electrical-safety-audits',
    audience: ['hse'],
    description: 'Safety representative audit for electrical lockout/tagout execution, zero-voltage verification, worker understanding, findings, and sign-off.',
    sourceMockupFamily: 'ZiaWellNav electrical safety audit form template',
    sections: [
      {
        id: 'loto-details',
        title: 'LOTO Audit Details',
        fields: [
          { id: 'dateTime', label: 'Audit Date & Time', type: 'datetime', required: true },
          { id: 'facilityName', label: 'Facility / Location', type: 'text', required: true },
          { id: 'equipmentCircuit', label: 'Equipment / Circuit Being Controlled', type: 'text', required: true },
          {
            id: 'energySources',
            label: 'Energy Sources Present',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'electrical', label: 'Electrical' },
              { value: 'mechanical', label: 'Mechanical' },
              { value: 'hydraulic', label: 'Hydraulic' },
              { value: 'pneumatic', label: 'Pneumatic' },
              { value: 'chemical', label: 'Chemical' },
              { value: 'thermal', label: 'Thermal' },
              { value: 'stored-energy', label: 'Stored / residual energy' },
            ],
          },
          { id: 'authorizedEmployee', label: 'Authorized Employee Responsible', type: 'text', required: true },
          { id: 'safetyRepresentative', label: 'Safety Representative', type: 'text' },
        ],
      },
      {
        id: 'loto-execution',
        title: 'LOTO Execution',
        fields: [
          { id: 'lotoProcedureAvailable', label: 'Written LOTO procedure available?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'locksTagsApplied', label: 'Locks and tags applied correctly?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'zeroVoltageVerified', label: 'Zero-voltage verification completed?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'storedEnergyReleased', label: 'Stored / residual energy released or controlled?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'groupLotoUsed', label: 'Group LOTO used?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'groupLotoResponsiblePerson', label: 'Group LOTO responsible person', type: 'text' },
          { id: 'workerUnderstandingVerified', label: 'Workers understand LOTO boundaries and release steps?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
        ],
      },
      {
        id: 'deficiencies-action',
        title: 'Deficiencies / Corrective Action',
        fields: [
          { id: 'deficienciesFound', label: 'Deficiencies Found?', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'deficiencyDescription', label: 'Deficiency Description', type: 'multiline' },
          { id: 'immediateActionTaken', label: 'Immediate Action Taken', type: 'multiline' },
          { id: 'correctiveActionNeeded', label: 'Corrective Action Needed?', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'recommendedCorrectiveAction', label: 'Recommended Corrective Action', type: 'multiline' },
          { id: 'evidenceAttachment', label: 'Photos / Evidence', type: 'attachment' },
        ],
      },
      {
        id: 'signoff',
        title: 'Authorized Employee / Safety Representative Sign-Off',
        fields: [
          { id: 'authorizedEmployeeAcknowledgement', label: 'Authorized Employee Acknowledgement', type: 'signature', required: true },
          { id: 'safetyRepresentativeSignature', label: 'Safety Representative Signature', type: 'signature', required: true },
          { id: 'supervisorOnLocation', label: 'Supervisor on location?', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'supervisorName', label: 'Supervisor Name', type: 'text' },
          { id: 'supervisorSignature', label: 'Supervisor Signature', type: 'signature' },
          { id: 'supervisorNotPresentNotes', label: 'Supervisor not present / notified notes', type: 'multiline' },
        ],
      },
    ],
  },
  {
    id: 'energized-work-arc-flash-readiness-audit',
    title: 'Energized Work / Arc Flash Readiness Audit',
    categoryId: 'electrical-safety-audits',
    audience: ['hse'],
    description: 'Safety representative audit for energized-work justification, arc flash labels, approach boundaries, PPE, tools, emergency planning, and sign-off.',
    sourceMockupFamily: 'ZiaWellNav electrical safety audit form template',
    sections: [
      {
        id: 'work-details',
        title: 'Energized Work Details',
        fields: [
          { id: 'dateTime', label: 'Audit Date & Time', type: 'datetime', required: true },
          { id: 'facilityName', label: 'Facility / Location', type: 'text', required: true },
          { id: 'equipmentDescription', label: 'Equipment / Circuit Description', type: 'text' },
          { id: 'workDescription', label: 'Work Description', type: 'multiline', required: true },
          { id: 'qualifiedElectricalWorker', label: 'Qualified Electrical Worker', type: 'text', required: true },
          { id: 'safetyRepresentative', label: 'Safety Representative', type: 'text' },
        ],
      },
      {
        id: 'energized-work-review',
        title: 'Energized Work Review',
        fields: [
          { id: 'energizedWorkJustification', label: 'Energized Work Justification', type: 'multiline', required: true },
          {
            id: 'permitStatus',
            label: 'Energized Work Permit Status',
            type: 'select',
            required: true,
            options: [
              { value: 'not-required', label: 'Not required' },
              { value: 'draft', label: 'Draft / pending' },
              { value: 'approved', label: 'Approved' },
              { value: 'missing', label: 'Missing' },
            ],
          },
          { id: 'arcFlashLabelPresent', label: 'Arc flash label present and readable?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'incidentEnergyOrPpeCategory', label: 'Incident Energy / PPE Category Known?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'shockBoundaryReviewed', label: 'Shock boundary reviewed?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'arcBoundaryReviewed', label: 'Arc flash boundary reviewed?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'restrictedApproachControlled', label: 'Restricted approach controlled?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
        ],
      },
      {
        id: 'ppe-tools-emergency',
        title: 'PPE / Tools / Emergency Controls',
        fields: [
          { id: 'voltageRatedGlovesReady', label: 'Voltage-rated gloves inspected and ready?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'arcRatedPpeReady', label: 'Arc-rated PPE selected and ready?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'insulatedToolsReady', label: 'Insulated / rated tools ready?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'observerStandbyAssigned', label: 'Observer / standby assigned?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'emergencyPlanReviewed', label: 'Emergency response plan reviewed?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'stopWorkConcerns', label: 'Stop-work concerns present?', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'controlNotes', label: 'Control Notes', type: 'multiline' },
          { id: 'evidenceAttachment', label: 'Photos / Evidence', type: 'attachment' },
        ],
      },
      {
        id: 'signoff',
        title: 'Safety Representative / Supervisor Sign-Off',
        fields: [
          { id: 'safetyRepresentativeSignature', label: 'Safety Representative Signature', type: 'signature', required: true },
          { id: 'supervisorOnLocation', label: 'Supervisor on location?', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'supervisorName', label: 'Supervisor Name', type: 'text' },
          { id: 'supervisorSignature', label: 'Supervisor Signature', type: 'signature' },
          { id: 'supervisorNotPresentNotes', label: 'Supervisor not present / notified notes', type: 'multiline' },
        ],
      },
    ],
  },
  {
    id: 'electrical-employee-vehicle-tool-ppe-audit',
    title: 'Electrical Employee Vehicle / Tool / PPE Audit',
    categoryId: 'electrical-safety-audits',
    audience: ['hse'],
    description: 'Safety representative audit for electricians working from separate vehicles, including truck/unit checks, PPE, meters, tools, housekeeping, and employee acknowledgements.',
    sourceMockupFamily: 'ZiaWellNav electrical safety audit form template',
    sections: [
      {
        id: 'inspection-details',
        title: 'Employee / Vehicle Inspection Details',
        fields: [
          { id: 'dateTime', label: 'Inspection Date & Time', type: 'datetime', required: true },
          { id: 'locationFacility', label: 'Location / Facility', type: 'text', required: true },
          { id: 'crewCompany', label: 'Crew / Contractor Company', type: 'text', required: true },
          { id: 'safetyRepresentative', label: 'Safety Representative', type: 'text', required: true },
          { id: 'inspectedEmployeeVehicleList', label: 'Inspected Employees / Truck-Unit List', type: 'multiline', required: true, helperText: 'List each employee, role, truck or unit number, and work area.' },
        ],
      },
      {
        id: 'ppe-tool-checks',
        title: 'Electrical PPE / Tool Checks',
        fields: [
          { id: 'ppeChecked', label: 'Required PPE present and worn correctly?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'voltageRatedGlovesChecked', label: 'Voltage-rated gloves and leather protectors checked?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'meterTestEquipmentChecked', label: 'Meter / test equipment inspected and rated?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'insulatedToolsChecked', label: 'Insulated / rated tools inspected?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'extensionCordsChecked', label: 'Extension cords / temporary power acceptable?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'laddersLiftsChecked', label: 'Ladders / lifts acceptable when present?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'vehicle-readiness',
        title: 'Vehicle / Unit Readiness',
        fields: [
          { id: 'fireExtinguisherFirstAid', label: 'Fire extinguisher / first aid present?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'vehicleHousekeeping', label: 'Vehicle housekeeping acceptable?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'materialsSecurement', label: 'Materials and tools secured for transport?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'vehicleDeficiencies', label: 'Vehicle / tool deficiencies', type: 'multiline' },
          { id: 'evidenceAttachment', label: 'Photos / Evidence', type: 'attachment' },
        ],
      },
      {
        id: 'employee-acknowledgement',
        title: 'Employee Acknowledgement / Sign-Off',
        fields: [
          { id: 'employeeAcknowledgements', label: 'Inspected Employee Acknowledgements', type: 'participants', required: true },
          { id: 'safetyRepresentativeSignature', label: 'Safety Representative Signature', type: 'signature', required: true },
          { id: 'supervisorOnLocation', label: 'Supervisor on location?', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'supervisorName', label: 'Supervisor Name', type: 'text' },
          { id: 'supervisorSignature', label: 'Supervisor Signature', type: 'signature' },
          { id: 'supervisorNotPresentNotes', label: 'Supervisor not present / notified notes', type: 'multiline' },
        ],
      },
    ],
  },
  {
    id: 'overhead-underground-electrical-worksite-audit',
    title: 'Overhead / Underground Electrical Worksite Audit',
    categoryId: 'electrical-safety-audits',
    audience: ['hse'],
    description: 'Safety representative audit for overhead-line exposure, underground electrical work, utility locates, approach distance, barricades, excavation controls, and sign-off.',
    sourceMockupFamily: 'ZiaWellNav electrical safety audit form template',
    sections: [
      {
        id: 'worksite-details',
        title: 'Worksite Details',
        fields: [
          { id: 'dateTime', label: 'Audit Date & Time', type: 'datetime', required: true },
          { id: 'locationFacility', label: 'Location / Facility', type: 'text', required: true },
          { id: 'lineOwnerOperator', label: 'Line Owner / Operator', type: 'text', required: true },
          { id: 'workScope', label: 'Work Scope', type: 'multiline', required: true },
          { id: 'safetyRepresentative', label: 'Safety Representative', type: 'text', required: true },
          { id: 'employeesObserved', label: 'Employees / Equipment Observed', type: 'participants' },
        ],
      },
      {
        id: 'line-utility-controls',
        title: 'Line / Utility Controls',
        fields: [
          { id: 'oneCallLocatesVerified', label: 'One-call / locates verified?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'minimumApproachDistanceReviewed', label: 'Minimum approach distance reviewed?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'spotterAssigned', label: 'Spotter assigned when required?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'barricadesSet', label: 'Barricades / exclusion zone set?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'equipmentClearanceControlled', label: 'Equipment clearance controlled?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'groundingBondingReviewed', label: 'Grounding / bonding reviewed when applicable?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'excavation-weather-briefing',
        title: 'Excavation / Weather / Crew Briefing',
        fields: [
          { id: 'excavationControlsReviewed', label: 'Excavation controls reviewed?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'weatherLightningReviewed', label: 'Weather / lightning risk reviewed?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'crewBriefingCompleted', label: 'Crew briefing completed?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          {
            id: 'riskSeverity',
            label: 'Highest Finding Severity',
            type: 'select',
            required: true,
            options: [
              { value: 'low', label: 'Low' },
              { value: 'medium', label: 'Medium' },
              { value: 'high', label: 'High' },
              { value: 'critical', label: 'Critical' },
            ],
          },
          { id: 'findings', label: 'Findings / Worksite Notes', type: 'multiline' },
          { id: 'correctiveActionNeeded', label: 'Corrective Action Needed?', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'recommendedCorrectiveAction', label: 'Recommended Corrective Action', type: 'multiline' },
          { id: 'evidenceAttachment', label: 'Photos / Evidence', type: 'attachment' },
        ],
      },
      {
        id: 'signoff',
        title: 'Safety Representative / Supervisor Sign-Off',
        fields: [
          { id: 'safetyRepresentativeSignature', label: 'Safety Representative Signature', type: 'signature', required: true },
          { id: 'supervisorOnLocation', label: 'Supervisor on location?', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'supervisorName', label: 'Supervisor Name', type: 'text' },
          { id: 'supervisorSignature', label: 'Supervisor Signature', type: 'signature' },
          { id: 'supervisorNotPresentNotes', label: 'Supervisor not present / notified notes', type: 'multiline' },
        ],
      },
    ],
  },
  {
    id: 'oxy-lock-out-tag-out',
    title: 'OXY Lock Out Tag Out',
    categoryId: 'occidental',
    audience: ['crew', 'foreman'],
    description: 'Occidental location lock out tag out record for electrical energy isolation, zero-energy verification, release, and signatures.',
    sourceMockupFamily: 'Occidental Electrical Safety Standard form',
    sections: [
      {
        id: 'job-details',
        title: 'Lock Out Tag Out - Job Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'oxyLocation', label: 'OXY Location / Facility', type: 'text', required: true },
          { id: 'workOrderOrJob', label: 'Work Order / Job Number', type: 'text' },
          { id: 'preparedBy', label: 'Prepared By', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company Working For', type: 'text' },
          { id: 'equipmentDescription', label: 'Equipment / Circuit Description', type: 'multiline', required: true },
          { id: 'systemVoltage', label: 'System Voltage', type: 'text' },
        ],
      },
      {
        id: 'isolation-details',
        title: 'Isolation Details',
        fields: [
          {
            id: 'energySources',
            label: 'Energy Sources Controlled',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'electrical', label: 'Electrical' },
              { value: 'mechanical', label: 'Mechanical' },
              { value: 'hydraulic', label: 'Hydraulic' },
              { value: 'pneumatic', label: 'Pneumatic' },
              { value: 'stored-energy', label: 'Stored energy' },
              { value: 'other', label: 'Other' },
            ],
          },
          { id: 'isolationPoints', label: 'Isolation Points / Device Locations', type: 'multiline', required: true },
          { id: 'lockTagNumbers', label: 'Lock / Tag Numbers', type: 'multiline', required: true },
          { id: 'affectedPersonsNotified', label: 'Affected persons notified?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'lotoProcedureReviewed', label: 'LOTO procedure reviewed?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'testEquipmentVerified', label: 'Test equipment verified before use?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'tryVerifyZeroEnergy', label: 'Try / test verified zero energy?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
        ],
      },
      {
        id: 'release-restoration',
        title: 'Release / Restoration',
        fields: [
          { id: 'workComplete', label: 'Work complete before release?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'guardsRestored', label: 'Guards / covers restored?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'locksRemovedBy', label: 'Locks removed by authorized person(s)', type: 'multiline' },
          { id: 'operationsAccepted', label: 'Operations accepted equipment back to service?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'attachments', label: 'Attachments', type: 'attachment' },
        ],
      },
      {
        id: 'signoff',
        title: 'Authorized Sign-Off',
        fields: [
          { id: 'authorizedEmployeeSignature', label: 'Authorized Employee Signature', type: 'signature', required: true },
          { id: 'operationsRepresentativeSignature', label: 'Operations Representative Signature', type: 'signature', required: true },
        ],
      },
    ],
  },
  {
    id: 'oxy-energized-electrical-work-permit',
    title: 'OXY Energized Electrical Work Permit',
    categoryId: 'occidental',
    audience: ['foreman'],
    description: 'Occidental location permit for energized electrical work justification, voltage exposure, PPE, boundaries, approvals, and sign-off.',
    sourceMockupFamily: 'Occidental Electrical Safety Standard form',
    sections: [
      {
        id: 'permit-details',
        title: 'Energized Electrical Work Permit - Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'oxyLocation', label: 'OXY Location / Facility', type: 'text', required: true },
          { id: 'permitNumber', label: 'Permit Number', type: 'text' },
          { id: 'requestorName', label: 'Requestor Name', type: 'text', required: true },
          { id: 'qualifiedElectricalWorker', label: 'Qualified Electrical Worker', type: 'text', required: true },
          { id: 'workDescription', label: 'Description of Energized Work', type: 'multiline', required: true },
        ],
      },
      {
        id: 'energized-work-review',
        title: 'Energized Work Review',
        fields: [
          { id: 'equipmentDescription', label: 'Equipment / Circuit Description', type: 'multiline', required: true },
          { id: 'nominalVoltage', label: 'Nominal Voltage', type: 'text', required: true },
          { id: 'cannotDeEnergizeReason', label: 'Reason equipment cannot be de-energized', type: 'multiline', required: true },
          { id: 'shockBoundary', label: 'Shock Protection Boundary', type: 'text' },
          { id: 'arcFlashBoundary', label: 'Arc Flash Boundary', type: 'text' },
          { id: 'incidentEnergyOrPpeCategory', label: 'Incident Energy / PPE Category', type: 'text', required: true },
          { id: 'jobSafetyPlanCompleted', label: 'Job Safety Plan completed?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'lotoConsidered', label: 'LOTO / de-energized alternative considered?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
        ],
      },
      {
        id: 'controls-ppe',
        title: 'Controls / PPE',
        fields: [
          {
            id: 'requiredControls',
            label: 'Required Controls',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'barricades', label: 'Barricades / restricted area' },
              { value: 'qualified-standby', label: 'Qualified standby person' },
              { value: 'insulated-tools', label: 'Insulated tools' },
              { value: 'arc-rated-ppe', label: 'Arc-rated PPE' },
              { value: 'rubber-gloves', label: 'Rubber insulating gloves' },
              { value: 'test-instrument', label: 'Verified test instrument' },
            ],
          },
          { id: 'ppeDescription', label: 'PPE Description', type: 'multiline' },
          { id: 'rescueEmergencyPlan', label: 'Rescue / emergency plan reviewed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'attachments', label: 'Attachments', type: 'attachment' },
        ],
      },
      {
        id: 'approvals',
        title: 'Approvals / Signatures',
        fields: [
          { id: 'oxySupervisorApproval', label: 'OXY Supervisor Approval', type: 'signature', required: true },
          { id: 'electricalAuthorityApproval', label: 'Electrical Authority Approval', type: 'signature', required: true },
          { id: 'workerSignature', label: 'Worker Signature', type: 'signature', required: true },
        ],
      },
    ],
  },
  {
    id: 'oxy-arc-flash-deviation-approval',
    title: 'OXY Arc Flash Deviation Approval',
    categoryId: 'occidental',
    audience: ['foreman', 'hse'],
    description: 'Occidental location approval record for arc flash control deviations, justification, compensating controls, and authorization.',
    sourceMockupFamily: 'Occidental Electrical Safety Standard form',
    sections: [
      {
        id: 'deviation-request',
        title: 'Arc Flash Deviation Approval - Request',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'oxyLocation', label: 'OXY Location / Facility', type: 'text', required: true },
          { id: 'requestorName', label: 'Requestor Name', type: 'text', required: true },
          { id: 'equipmentOrArea', label: 'Equipment / Area', type: 'text', required: true },
          { id: 'taskDescription', label: 'Task Description', type: 'multiline', required: true },
        ],
      },
      {
        id: 'deviation-details',
        title: 'Deviation Details',
        fields: [
          { id: 'standardRequirement', label: 'Standard Requirement / Expected Control', type: 'multiline', required: true },
          { id: 'deviationRequested', label: 'Deviation Requested', type: 'multiline', required: true },
          { id: 'deviationReason', label: 'Reason for Deviation', type: 'multiline', required: true },
          { id: 'durationOfDeviation', label: 'Expected Duration', type: 'text' },
          { id: 'affectedWorkers', label: 'Affected Workers', type: 'participants' },
        ],
      },
      {
        id: 'risk-controls',
        title: 'Risk Review / Compensating Controls',
        fields: [
          { id: 'hazardsReviewed', label: 'Arc flash and shock hazards reviewed?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'compensatingControls', label: 'Compensating Controls', type: 'multiline', required: true },
          { id: 'residualRiskAccepted', label: 'Residual risk accepted?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'attachments', label: 'Attachments', type: 'attachment' },
        ],
      },
      {
        id: 'approval',
        title: 'Approval',
        fields: [
          { id: 'approvalName', label: 'Approval Name / Title', type: 'text', required: true },
          { id: 'approvalSignature', label: 'Approval Signature', type: 'signature', required: true },
        ],
      },
    ],
  },
  {
    id: 'oxy-job-safety-plan-checklist',
    title: 'OXY Job Safety Plan Checklist',
    categoryId: 'occidental',
    audience: ['crew', 'foreman', 'company_man'],
    description: 'Occidental location job safety planning checklist for electrical tasks, permits, hazards, controls, emergency planning, and crew acknowledgement.',
    sourceMockupFamily: 'Occidental Electrical Safety Standard form',
    sections: [
      {
        id: 'job-scope',
        title: 'Job Safety Plan Checklist - Job Scope',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'oxyLocation', label: 'OXY Location / Facility', type: 'text', required: true },
          { id: 'jobLead', label: 'Job Lead', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company Working For', type: 'text' },
          { id: 'workScope', label: 'Work Scope', type: 'multiline', required: true },
          { id: 'crewMembers', label: 'Crew Members', type: 'participants', required: true },
        ],
      },
      {
        id: 'hazards-controls',
        title: 'Hazards / Controls',
        fields: [
          {
            id: 'hazardsIdentified',
            label: 'Hazards Identified',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'electrical', label: 'Electrical' },
              { value: 'arc-flash', label: 'Arc flash' },
              { value: 'stored-energy', label: 'Stored energy' },
              { value: 'line-of-fire', label: 'Line of fire' },
              { value: 'working-at-heights', label: 'Working at heights' },
              { value: 'simultaneous-operations', label: 'Simultaneous operations' },
            ],
          },
          { id: 'criticalControls', label: 'Critical Controls', type: 'multiline', required: true },
          { id: 'permitsReviewed', label: 'Permits / authorizations reviewed?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'lotoApplicable', label: 'LOTO applicable?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'emergencyPlanReviewed', label: 'Emergency plan reviewed?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
        ],
      },
      {
        id: 'communication',
        title: 'Communication / Authorization',
        fields: [
          { id: 'operationsCommunication', label: 'Operations communication completed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'stopWorkAuthorityReviewed', label: 'Stop-work authority reviewed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'additionalNotes', label: 'Additional Notes', type: 'multiline' },
          { id: 'attachments', label: 'Attachments', type: 'attachment' },
        ],
      },
      {
        id: 'signoff',
        title: 'Crew Sign-Off',
        fields: [
          { id: 'jobLeadSignature', label: 'Job Lead Signature', type: 'signature', required: true },
        ],
      },
    ],
  },
  {
    id: 'oxy-field-electrical-risk-assessment',
    title: 'OXY Field Electrical Risk Assessment',
    categoryId: 'occidental',
    audience: ['foreman', 'hse'],
    description: 'Occidental location field risk assessment for electrical task hazards, exposure rating, controls, PPE, and residual risk.',
    sourceMockupFamily: 'Occidental Electrical Safety Standard form',
    sections: [
      {
        id: 'assessment-details',
        title: 'Field Electrical Risk Assessment - Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'oxyLocation', label: 'OXY Location / Facility', type: 'text', required: true },
          { id: 'assessorName', label: 'Assessor Name', type: 'text', required: true },
          { id: 'taskDescription', label: 'Task Description', type: 'multiline', required: true },
          { id: 'equipmentDescription', label: 'Equipment / Circuit Description', type: 'multiline', required: true },
          { id: 'nominalVoltage', label: 'Nominal Voltage', type: 'text', required: true },
        ],
      },
      {
        id: 'hazard-assessment',
        title: 'Electrical Hazard Assessment',
        fields: [
          { id: 'shockHazardPresent', label: 'Shock hazard present?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'arcFlashHazardPresent', label: 'Arc flash hazard present?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'approachBoundaries', label: 'Approach / arc flash boundaries', type: 'multiline' },
          { id: 'taskRiskLevel', label: 'Task Risk Level', type: 'select', required: true, options: [
            { value: 'low', label: 'Low' },
            { value: 'medium', label: 'Medium' },
            { value: 'high', label: 'High' },
            { value: 'critical', label: 'Critical' },
          ] },
          { id: 'environmentalConditions', label: 'Work Area / Environmental Conditions', type: 'multiline' },
        ],
      },
      {
        id: 'controls',
        title: 'Controls / Residual Risk',
        fields: [
          { id: 'requiredPpe', label: 'Required PPE', type: 'multiline', required: true },
          { id: 'toolsAndEquipment', label: 'Tools / Test Equipment', type: 'multiline', required: true },
          { id: 'controlsImplemented', label: 'Controls Implemented', type: 'multiline', required: true },
          { id: 'residualRisk', label: 'Residual Risk After Controls', type: 'select', required: true, options: [
            { value: 'acceptable', label: 'Acceptable' },
            { value: 'needs-additional-control', label: 'Needs additional control' },
            { value: 'stop-work', label: 'Stop work' },
          ] },
          { id: 'attachments', label: 'Attachments', type: 'attachment' },
        ],
      },
      {
        id: 'signoff',
        title: 'Assessment Sign-Off',
        fields: [
          { id: 'assessorSignature', label: 'Assessor Signature', type: 'signature', required: true },
        ],
      },
    ],
  },
  {
    id: 'oxy-field-audit-checklist',
    title: 'OXY Field Audit Checklist',
    categoryId: 'occidental',
    audience: ['hse'],
    description: 'Occidental location field audit checklist for electrical work observations, permits, LOTO, PPE, boundaries, and corrective actions.',
    sourceMockupFamily: 'Occidental Electrical Safety Standard form',
    sections: [
      {
        id: 'audit-details',
        title: 'Field Audit Checklist - Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'oxyLocation', label: 'OXY Location / Facility', type: 'text', required: true },
          { id: 'auditorName', label: 'Auditor Name', type: 'text', required: true },
          { id: 'companyObserved', label: 'Company / Crew Observed', type: 'text' },
          { id: 'workObserved', label: 'Work Observed', type: 'multiline', required: true },
        ],
      },
      {
        id: 'audit-items',
        title: 'Electrical Field Audit Items',
        fields: [
          { id: 'jobSafetyPlanAvailable', label: 'Job safety plan available and reviewed', type: 'pass-fail-na', required: true, options: passFailNaOptions },
          { id: 'permitsAvailable', label: 'Required permits / authorizations available', type: 'pass-fail-na', required: true, options: passFailNaOptions },
          { id: 'lotoControlsVerified', label: 'LOTO controls verified when applicable', type: 'pass-fail-na', required: true, options: passFailNaOptions },
          { id: 'energizedWorkControlsVerified', label: 'Energized work controls verified when applicable', type: 'pass-fail-na', required: true, options: passFailNaOptions },
          { id: 'arcFlashPpeVerified', label: 'Arc flash / shock PPE verified', type: 'pass-fail-na', required: true, options: passFailNaOptions },
          { id: 'boundariesBarricadesVerified', label: 'Boundaries / barricades established', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'qualifiedWorkersVerified', label: 'Qualified workers verified', type: 'pass-fail-na', options: passFailNaOptions },
        ],
      },
      {
        id: 'findings',
        title: 'Findings / Corrective Actions',
        fields: [
          { id: 'findingsSummary', label: 'Findings Summary', type: 'multiline' },
          { id: 'correctiveActionsRequired', label: 'Corrective actions required?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'correctiveActionOwner', label: 'Corrective Action Owner', type: 'text' },
          { id: 'correctiveActionDueDate', label: 'Corrective Action Due Date', type: 'date' },
          { id: 'attachments', label: 'Attachments', type: 'attachment' },
        ],
      },
      {
        id: 'signoff',
        title: 'Audit Sign-Off',
        fields: [
          { id: 'auditorSignature', label: 'Auditor Signature', type: 'signature', required: true },
        ],
      },
    ],
  },
  {
    id: 'fall-protection-equipment-inspection',
    title: 'Fall Protection Equipment Inspection',
    categoryId: 'equipment-inspections',
    audience: ['crew', 'hse'],
    description: 'Inspection record for full body harness, lanyard, self-retracting lifeline, disposition, and inspector sign-off.',
    sourceMockupFamily: 'ZiaWellNav field safety form template',
    sections: [
      {
        id: 'general-details',
        title: 'General Details',
        fields: [
          {
            id: 'inspectionTypes',
            label: 'Fall Protection Inspection Types',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'full-body-harness', label: 'Full Body Harness' },
              { value: 'lanyard', label: 'Lanyard' },
              { value: 'srl', label: 'Self Retracting Lifeline (SRL)' },
            ],
          },
          { id: 'harnessManufactureDate', label: 'Harness Manufacture Date', type: 'date' },
          { id: 'harnessSerial', label: 'Harness Serial #', type: 'text' },
          { id: 'harnessModel', label: 'Harness Model', type: 'text' },
          { id: 'lanyardManufactureDate', label: 'Lanyard Manufacture Date', type: 'date' },
          { id: 'lanyardSerial', label: 'Lanyard Serial #', type: 'text' },
          { id: 'srlSerial', label: 'SRL Serial #', type: 'text' },
        ],
      },
      {
        id: 'full-body-harness',
        title: 'Full Body Harness Inspection Details',
        fields: [
          { id: 'dRings', label: 'D-rings', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'buckles', label: 'Buckles', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'keepers', label: 'Keepers', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'backPads', label: 'Back pads', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'webbing', label: 'Webbing', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'stitching', label: 'Stitching', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'labels', label: 'Labels secure and legible', type: 'pass-fail-na', options: passFailNaOptions },
        ],
      },
      {
        id: 'lanyard-details',
        title: 'Lanyard Inspection Details',
        description: 'Inspect hardware, webbing, stitching, rope, energy absorber, and labels.',
        fields: [
          { id: 'lanyardSnapHooks', label: 'Snap hooks', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'lanyardCarabiners', label: 'Carabiners', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'lanyardAdjusters', label: 'Adjusters', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'lanyardKeepers', label: 'Keepers', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'lanyardThimbles', label: 'Thimbles', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'lanyardDRings', label: 'D-rings', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'lanyardWebbing', label: 'Webbing', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'lanyardStitching', label: 'Stitching', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'syntheticRope', label: 'Synthetic rope', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'wireRope', label: 'Wire rope', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'energyAbsorbingComponent', label: 'Energy absorbing component', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'lanyardLabels', label: 'Labels secure and legible', type: 'pass-fail-na', options: passFailNaOptions },
        ],
      },
      {
        id: 'srl-details',
        title: 'Self Retracting Lifeline Inspection Details',
        description: 'Inspect impact indicator, fasteners, housing, lifeline, locking action, retraction, hooks, reserve lifeline, and labels.',
        fields: [
          { id: 'srlManufactureDate', label: 'SRL Manufacture Date', type: 'date' },
          { id: 'srlPurchaseDate', label: 'SRL Purchase Date', type: 'date' },
          { id: 'srlModel', label: 'SRL Model', type: 'text' },
          { id: 'impactIndicator', label: 'Impact indicator', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'screwsFasteners', label: 'Screws / fasteners', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'housing', label: 'Housing', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'lifeline', label: 'Lifeline', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'lockingAction', label: 'Locking action', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'retractionExtension', label: 'Retraction / extension', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'srlHooksCarabiners', label: 'Hooks / carabiners', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'reserveLifeline', label: 'Reserve lifeline', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'srlLabels', label: 'Labels secure and legible', type: 'pass-fail-na', options: passFailNaOptions },
        ],
      },
      {
        id: 'summary',
        title: 'Overall Summary',
        fields: [
          { id: 'harnessDisposition', label: 'Full Body Harness Overall Disposition', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'lanyardDisposition', label: 'Lanyard Overall Disposition', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'srlDisposition', label: 'SRL Overall Disposition', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'additionalComments', label: 'Additional Comments', type: 'multiline' },
          { id: 'dateTime', label: 'Date / Time', type: 'datetime', required: true },
          { id: 'inspectionBy', label: 'Inspection By', type: 'text', required: true },
          { id: 'signature', label: 'Signature', type: 'signature', required: true },
          { id: 'attachments', label: 'Attachments', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'auto-crane-inspection-checklist',
    title: 'Auto Crane Inspection Checklist',
    categoryId: 'equipment-inspections',
    audience: ['crew'],
    description: 'Daily crane inspection checklist for controls, hydraulics, safety devices, load line, pins, and outriggers.',
    sourceMockupFamily: 'ZiaWellNav field safety form template',
    sections: [
      {
        id: 'general-details',
        title: 'General Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time Inspected', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'companyWorkingFor', label: 'Company Working For', type: 'text' },
          { id: 'locationFacilityName', label: 'Location / Facility Name', type: 'text' },
          { id: 'unitNumber', label: 'Unit #', type: 'text', required: true },
          { id: 'inspectedBy', label: 'Inspected By', type: 'text', required: true },
          { id: 'inspectorSignature', label: 'Inspector Signature', type: 'signature', required: true },
        ],
      },
      {
        id: 'inspection-details',
        title: 'Inspection Details',
        description: 'Sat = Satisfactory, Unsat = Unsatisfactory, N/A = Not Applicable.',
        fields: [
          { id: 'loadChartsLabels', label: 'All load charts, safety and warning labels, and control labels are present and legible', type: 'sat-unsat-na', options: satUnsatNaOptions, required: true },
          { id: 'safetyDevices', label: 'Check all safety devices for proper operation', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'controlMechanisms', label: 'Control mechanisms operate properly with no leaks or cracks', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'hydraulicSystem', label: 'Hydraulic system hoses, tubes, fittings, and oil level', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'presenceOperationSafetyLatches', label: 'Presence and proper operation of hook safety latches', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'properEngagement', label: 'Proper engagement of connecting pins and retaining devices', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'craneDamage', label: 'Overall observation of crane damage or missing parts', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'abnormalOperation', label: 'During operation, observe crane for abnormal performance or unusual wear', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'remoteControls', label: 'Remote control devices operate properly', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'antiTwoBlock', label: 'Anti-two-block device checked for proper operation', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'pinsInstallation', label: 'All pins for proper installation and condition', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'loadLineCondition', label: 'Condition of load line', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'operatorManual', label: 'Operator manual present with unit', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'loadLineAttachment', label: 'Load line for proper attachment to drum', type: 'sat-unsat-na', options: satUnsatNaOptions },
        ],
      },
      {
        id: 'summary',
        title: 'Summary',
        fields: [
          { id: 'attachments', label: 'Attachments', type: 'attachment' },
          { id: 'summaryComments', label: 'Summary / Comments', type: 'multiline' },
          { id: 'operatorName', label: 'Operator', type: 'text', required: true },
          { id: 'operatorSignature', label: 'Operator Signature', type: 'signature', required: true },
          { id: 'anyUnsatisfactory', label: 'Any Unsatisfactory Items Checked?', type: 'yes-no', required: true, options: yesNoOptions },
        ],
      },
    ],
  },
  {
    id: 'general-equipment-inspection',
    title: 'General Equipment Inspection',
    categoryId: 'equipment-inspections',
    audience: ['crew', 'driver'],
    description: 'General equipment inspection for warning devices, tires, fluids, battery, hoses, chassis, hydraulics, boom, and safety equipment.',
    sourceMockupFamily: 'ZiaWellNav field safety form template',
    sections: [
      {
        id: 'general-details',
        title: 'General Details',
        fields: [
          { id: 'equipmentNumber', label: 'Equipment #', type: 'text', required: true },
          { id: 'equipmentType', label: 'Equipment Type', type: 'text', required: true },
          { id: 'customer', label: 'Customer', type: 'text' },
          { id: 'location', label: 'Location', type: 'text', required: true },
          { id: 'department', label: 'Department', type: 'text' },
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'inspector', label: 'Inspector', type: 'text', required: true },
        ],
      },
      {
        id: 'inspection-details',
        title: 'Inspection Details',
        description: 'Sat = Satisfactory, Unsat = Unsatisfactory, N/A = Not Applicable.',
        fields: [
          { id: 'warningDevicesLights', label: 'Warning Devices / Lights', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'tires', label: 'Tires', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'fuelOilCoolant', label: 'Fuel, Oil, Coolant', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'batteryChargeLevel', label: 'Battery Charge Level', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'hoseCondition', label: 'Hose Condition', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'chassis', label: 'Chassis', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'hydraulicFluidLeaks', label: 'Hydraulic Fluid Level & Component Leaks', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'labelsCapacityPlatesManual', label: 'Labels, Capacity Plates, Operator Manual', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'operatorStation', label: "Bucket Operator's Station", type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'bucketLiner', label: 'Bucket Liner', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'boomComponents', label: 'Boom & Associated Components', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'electricalWires', label: 'Electrical Wires', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'fallProtectionGear', label: 'Fall Protection Gear', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'fireExtinguisher', label: 'Fire Extinguisher', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'firstAidKit', label: 'First Aid Kit - Fully Stocked', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'dielectricTest', label: 'Dielectric Test Current (Within 1 Year)', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'lowerControls', label: 'Lower Controls Functional', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'outriggerDeployment', label: 'Outrigger Deployment', type: 'sat-unsat-na', options: satUnsatNaOptions },
          { id: 'winchLinesCables', label: 'Winch Lines / Cables', type: 'sat-unsat-na', options: satUnsatNaOptions },
        ],
      },
      {
        id: 'summary',
        title: 'Summary',
        fields: [
          { id: 'anyUnsatisfactory', label: 'Any Unsatisfactory Items Checked?', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'otherComments', label: 'Other Comments', type: 'multiline' },
          { id: 'inspectorName', label: 'Inspector', type: 'text' },
          { id: 'signature', label: 'Signature', type: 'signature', required: true },
          { id: 'dateTimeSigned', label: 'Date / Time Signed', type: 'datetime' },
        ],
      },
    ],
  },
  {
    id: 'pump-hose-pre-op-checklist',
    title: 'Pump / Hose Pre-Op Checklist',
    categoryId: 'equipment-inspections',
    audience: ['crew', 'driver'],
    description: 'Daily pre-op checklist for pump condition, pressure, hoses, fittings, wand, and nozzle.',
    sourceMockupFamily: 'ZiaWellNav field safety form template',
    sections: [
      {
        id: 'unit-information',
        title: 'Unit Information',
        fields: [
          { id: 'unitNumber', label: 'Unit Number', type: 'text', required: true },
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'operatorName', label: 'Operator Name', type: 'text', required: true },
          { id: 'location', label: 'Location', type: 'text' },
        ],
      },
      {
        id: 'pump',
        title: 'Pump',
        fields: [
          { id: 'pumpLeaks', label: 'Any leaks at the pump?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'pumpSecurelyMounted', label: 'Is the pump securely mounted?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'abnormalNoises', label: 'Any abnormal noises?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'abnormalVibrations', label: 'Are there any abnormal vibrations?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'pressureBuildHold', label: 'Does pressure build and hold correctly?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
        ],
      },
      {
        id: 'hoses',
        title: 'Hoses',
        fields: [
          { id: 'hoseReelAttached', label: 'Is the hose reel securely attached?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'hoseLeaks', label: 'Are there any leaks at the pump?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'hoseRatingLegible', label: 'Is the hose rating legible and visible on the hose?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'hoseCutsLeaksWear', label: 'Does the hose have any cuts, leaks or worn outer jacket?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'hoseBulgesKinks', label: 'Are there any bulges, soft spots, kinks or twists?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'hoseRoutedCorrectly', label: 'Is the hose routed correctly, rubbing or pinched?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'fittingsTightSecure', label: 'Are the fittings tight and secure?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'wandCondition', label: 'Is the wand free of cracks, leaks or damage?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'nozzleCondition', label: 'Is the nozzle free of cracks, leaks and acceptable damage?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'summary',
        title: 'Summary',
        fields: [
          { id: 'comments', label: 'Comments', type: 'multiline' },
          { id: 'attachments', label: 'Photos / Documents', type: 'attachment' },
          { id: 'completedBy', label: 'Completed By', type: 'text', required: true },
          { id: 'signature', label: 'Signature', type: 'signature', required: true },
        ],
      },
    ],
  },
  {
    id: 'vehicle-equipment-maintenance-request',
    title: 'Vehicle or Equipment Maintenance Request',
    categoryId: 'equipment-inspections',
    audience: ['crew', 'foreman', 'driver'],
    description: 'Maintenance request form for driver, supervisor, unit, field office, description, photos, and completion signature.',
    sourceMockupFamily: 'ZiaWellNav field safety form template',
    sections: [
      {
        id: 'request-details',
        title: 'Maintenance Request / Solicitud de Mantenimiento',
        fields: [
          { id: 'date', label: 'Date / Fecha', type: 'date', required: true },
          { id: 'driverName', label: 'Driver Name / Nombre del Conductor', type: 'text', required: true },
          { id: 'driverPhone', label: 'Driver Phone Number / Numero de Telefono', type: 'text' },
          { id: 'foremanSupervisor', label: 'Foreman / Supervisor', type: 'text', required: true },
          {
            id: 'assetId',
            label: 'Registered Asset / Unidad Registrada',
            type: 'select',
            helperText: 'Choose the truck or equipment from your company fleet when available. Use Unit Number for unregistered units.',
            options: [],
          },
          { id: 'unitNumber', label: 'Unit Number / Numero de Unidad', type: 'text', required: true },
          { id: 'fieldOffice', label: 'Field Office / Oficina Regional', type: 'text' },
          { id: 'division', label: 'Division', type: 'text' },
          { id: 'description', label: 'Description / Descripcion', type: 'multiline', required: true },
          { id: 'photos', label: 'Photos / Fotos', type: 'attachment' },
          { id: 'completedBy', label: 'Completed By / Completado Por', type: 'text', required: true },
          { id: 'signature', label: 'Signature / Firma', type: 'signature', required: true },
        ],
      },
    ],
  },
  {
    id: 'incident-near-miss-report',
    title: 'Field Event Report',
    categoryId: 'incident-near-miss',
    audience: ['crew', 'foreman', 'company_man', 'driver'],
    description: 'Company-owned incident and near-miss intake for event details, actual and potential severity, immediate actions, evidence, and recommended corrective actions.',
    sourceMockupFamily: 'ZiaWellNav field safety form template',
    sections: [
      {
        id: 'event-details',
        title: 'Event Details',
        fields: [
          { id: 'eventDate', label: 'Event Date & Time', type: 'datetime', required: true },
          { id: 'locationFacility', label: 'Location / Facility', type: 'text', required: true },
          { id: 'crew', label: 'Crew', type: 'text', required: true },
          { id: 'reporter', label: 'Reporter', type: 'text', required: true },
        ],
      },
      {
        id: 'event-classification',
        title: 'Event Classification',
        fields: [
          {
            id: 'eventType',
            label: 'Event Type',
            type: 'select',
            required: true,
            options: [
              { value: 'near-miss', label: 'Near miss' },
              { value: 'first-aid', label: 'First aid' },
              { value: 'injury', label: 'Injury' },
              { value: 'spill-release', label: 'Spill / release' },
              { value: 'property-damage', label: 'Property damage' },
              { value: 'vehicle', label: 'Vehicle' },
              { value: 'equipment', label: 'Equipment' },
              { value: 'fire', label: 'Fire' },
              { value: 'other', label: 'Other' },
            ],
          },
          {
            id: 'actualSeverity',
            label: 'Actual Severity',
            type: 'select',
            required: true,
            options: [
              { value: 'low', label: 'Low' },
              { value: 'medium', label: 'Medium' },
              { value: 'high', label: 'High' },
              { value: 'critical', label: 'Critical' },
            ],
          },
          {
            id: 'potentialSeverity',
            label: 'Potential Severity',
            type: 'select',
            required: true,
            options: [
              { value: 'low', label: 'Low' },
              { value: 'medium', label: 'Medium' },
              { value: 'high', label: 'High' },
              { value: 'critical', label: 'Critical' },
            ],
          },
        ],
      },
      {
        id: 'event-narrative',
        title: 'Event Narrative',
        fields: [
          { id: 'eventDescription', label: 'Event Description', type: 'multiline', required: true },
          { id: 'immediateActionTaken', label: 'Immediate Action Taken', type: 'multiline', required: true },
          { id: 'peopleInvolved', label: 'People Involved', type: 'multiline' },
          { id: 'witnesses', label: 'Witnesses', type: 'multiline' },
          { id: 'evidenceAttachment', label: 'Photos / Evidence Attachment', type: 'attachment' },
        ],
      },
      {
        id: 'corrective-action-recommendation',
        title: 'Corrective Action Recommendation',
        fields: [
          { id: 'correctiveActionNeeded', label: 'Corrective Action Needed?', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'recommendedCorrectiveAction', label: 'Recommended Corrective Action', type: 'multiline' },
          { id: 'recommendedDueDate', label: 'Recommended Due Date', type: 'date' },
        ],
      },
      {
        id: 'reporter-signoff',
        title: 'Reporter Sign-Off',
        fields: [
          { id: 'reporterSignature', label: 'Reporter Signature', type: 'signature', required: true },
        ],
      },
    ],
  },
  {
    id: 'near-miss-report',
    title: 'Near Miss / Safety Concern',
    categoryId: 'incident-near-miss',
    audience: ['crew', 'foreman', 'company_man', 'driver'],
    description: 'Near-miss and safety concern report for job task, potential outcome, corrective action, photos, and sign-off.',
    sourceMockupFamily: 'ZiaWellNav field safety form template',
    sections: [
      {
        id: 'general-details',
        title: 'General Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'reportedBy', label: 'Reported By', type: 'text', required: true },
          { id: 'department', label: 'Department', type: 'text' },
          { id: 'companyWorkingFor', label: 'Company Working For', type: 'text' },
          { id: 'locationOfEvent', label: 'Location of Event', type: 'text', required: true },
          { id: 'jobTask', label: 'Job Task / Work Being Performed', type: 'multiline', required: true },
        ],
      },
      {
        id: 'category-severity',
        title: 'Near-Miss Category / Potential Severity',
        fields: [
          {
            id: 'conditions',
            label: 'Check All Appropriate Conditions',
            type: 'checkbox-group',
            options: [
              { value: 'near-miss', label: 'Near Miss' },
              { value: 'safety-concern', label: 'Safety Concern' },
              { value: 'safety-suggestion', label: 'Safety Suggestion' },
              { value: 'other', label: 'Other' },
            ],
          },
          {
            id: 'concernType',
            label: 'Type of Near Miss / Safety Concern',
            type: 'checkbox-group',
            options: [
              { value: 'fall-from-height', label: 'Fall from Height' },
              { value: 'trip-same-level', label: 'Trip / Fall Same Level' },
              { value: 'falling-object', label: 'Falling Object' },
              { value: 'hazardous-substance', label: 'Hazardous Substance' },
              { value: 'electrical-shock', label: 'Electric Shock' },
              { value: 'caught-between', label: 'Caught Between / Underneath' },
              { value: 'other', label: 'Other' },
            ],
          },
          {
            id: 'severity',
            label: 'Severity Level of Potential Incident / Hazard / Concern',
            type: 'select',
            required: true,
            options: [
              { value: 'low', label: 'Low' },
              { value: 'moderate', label: 'Moderate' },
              { value: 'high', label: 'High' },
            ],
          },
          { id: 'potentialInjuryDamage', label: 'Potential Injury / Damage if Not Corrected', type: 'multiline' },
        ],
      },
      {
        id: 'description-corrective-action',
        title: 'Description / Corrective Action',
        fields: [
          { id: 'description', label: 'Description of Potential Incident / Hazard / Concern', type: 'multiline', required: true },
          { id: 'rootCauseIdentified', label: 'Root Cause Identified?', type: 'yes-no' },
          { id: 'immediateAction', label: 'Immediate Action Taken', type: 'multiline', required: true },
          { id: 'correctiveAction', label: 'Corrective Action for Event', type: 'multiline' },
          { id: 'followUpOwner', label: 'Follow-Up Owner', type: 'text' },
          { id: 'followUpDueDate', label: 'Follow-Up Due Date', type: 'date' },
        ],
      },
      {
        id: 'photos-signoff',
        title: 'Photos & Sign-Off',
        fields: [
          { id: 'attachments', label: 'Documents / Photos', type: 'attachment' },
          { id: 'reportedBySignature', label: 'Reported By Signature', type: 'signature', required: true },
          { id: 'supervisorName', label: 'Supervisor / Foreman Name', type: 'text' },
          { id: 'supervisorSignature', label: 'Supervisor / Foreman Signature', type: 'signature' },
          { id: 'reviewDate', label: 'Review Date', type: 'date' },
        ],
      },
    ],
  },
  {
    id: 'first-report-of-incident',
    title: 'First Report of Incident',
    categoryId: 'incident-near-miss',
    audience: ['crew', 'foreman', 'company_man', 'driver'],
    description: 'Initial incident report for injury, illness, equipment, property, vehicle, environmental release, response, and sign-off.',
    sourceMockupFamily: 'ZiaWellNav field safety form template',
    sections: [
      {
        id: 'employee-contractor-details',
        title: 'Employee / Contractor Details',
        fields: [
          { id: 'employeeName', label: 'Employee / Contractor Name', type: 'text', required: true },
          { id: 'employeePhone', label: 'Phone Number', type: 'text' },
          { id: 'company', label: 'Company', type: 'text' },
          { id: 'jobTitle', label: 'Job Title / Craft', type: 'text' },
          { id: 'supervisorName', label: 'Supervisor / Foreman', type: 'text', required: true },
          { id: 'supervisorPhone', label: 'Supervisor Phone', type: 'text' },
        ],
      },
      {
        id: 'incident-details',
        title: 'Incident Details',
        fields: [
          { id: 'incidentDateTime', label: 'Incident Date & Time', type: 'datetime', required: true },
          { id: 'reportedDateTime', label: 'Reported Date & Time', type: 'datetime' },
          { id: 'locationOfIncident', label: 'Location of Incident', type: 'text', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'department', label: 'Department', type: 'text' },
          {
            id: 'incidentTypes',
            label: 'Incident Type',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'injury', label: 'Injury' },
              { value: 'illness', label: 'Illness' },
              { value: 'first-aid', label: 'First Aid' },
              { value: 'property-damage', label: 'Property Damage' },
              { value: 'vehicle', label: 'Vehicle' },
              { value: 'equipment', label: 'Equipment' },
              { value: 'environmental-release', label: 'Environmental Release' },
              { value: 'fire', label: 'Fire' },
              { value: 'other', label: 'Other' },
            ],
          },
          { id: 'weatherLighting', label: 'Weather / Lighting Conditions', type: 'text' },
        ],
      },
      {
        id: 'injury-illness-details',
        title: 'Injury / Illness Details',
        fields: [
          { id: 'injuryOccurred', label: 'Did an injury or illness occur?', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'bodyPartsAffected', label: 'Body Part(s) Affected', type: 'text' },
          { id: 'injuryDescription', label: 'Injury / Illness Description', type: 'multiline' },
          {
            id: 'treatmentProvided',
            label: 'Treatment Provided',
            type: 'checkbox-group',
            options: [
              { value: 'none', label: 'None' },
              { value: 'first-aid', label: 'First Aid' },
              { value: 'clinic', label: 'Clinic / Occupational Medicine' },
              { value: 'emergency-room', label: 'Emergency Room' },
              { value: 'ambulance', label: 'Ambulance / EMS' },
            ],
          },
          { id: 'medicalProvider', label: 'Medical Provider / Facility', type: 'text' },
          { id: 'returnedToWork', label: 'Returned to Work?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'equipment-property-environmental',
        title: 'Equipment / Property / Environmental',
        fields: [
          { id: 'propertyDamage', label: 'Property or equipment damage?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'vehicleEquipmentInvolved', label: 'Vehicle / Equipment Involved', type: 'text' },
          { id: 'unitNumber', label: 'Unit / Asset Number', type: 'text' },
          { id: 'damageDescription', label: 'Damage Description', type: 'multiline' },
          { id: 'environmentalRelease', label: 'Environmental release?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'materialReleased', label: 'Material Released', type: 'text' },
          { id: 'estimatedVolume', label: 'Estimated Volume', type: 'text' },
          { id: 'notificationsMade', label: 'Notifications Made', type: 'multiline' },
        ],
      },
      {
        id: 'description-response',
        title: 'Description / Immediate Actions',
        fields: [
          { id: 'whatHappened', label: 'What Happened?', type: 'multiline', required: true },
          { id: 'immediateActions', label: 'Immediate Actions Taken', type: 'multiline', required: true },
          { id: 'unsafeConditions', label: 'Unsafe Conditions / Contributing Factors', type: 'multiline' },
          { id: 'correctiveActions', label: 'Corrective Actions Needed', type: 'multiline' },
          { id: 'attachments', label: 'Photos / Documents', type: 'attachment' },
        ],
      },
      {
        id: 'signoff',
        title: 'Employee / Supervisor Sign-Off',
        fields: [
          { id: 'employeeSignature', label: 'Employee / Contractor Signature', type: 'signature' },
          { id: 'supervisorSignature', label: 'Supervisor / Foreman Signature', type: 'signature', required: true },
          { id: 'safetyReviewSignature', label: 'Safety Review Signature', type: 'signature' },
          { id: 'dateCompleted', label: 'Date Completed', type: 'date' },
        ],
      },
    ],
  },
  {
    id: 'incident-witness-statement',
    title: 'Incident Witness Statement',
    categoryId: 'incident-near-miss',
    audience: ['crew', 'driver'],
    description: 'Witness statement form for incident reference, witness location, statement, people/equipment involved, and signature.',
    sourceMockupFamily: 'ZiaWellNav field safety form template',
    sections: [
      {
        id: 'witness-details',
        title: 'Witness Details',
        fields: [
          { id: 'witnessName', label: 'Witness Name', type: 'text', required: true },
          { id: 'phoneNumber', label: 'Phone Number', type: 'text' },
          { id: 'company', label: 'Company', type: 'text' },
          { id: 'jobTitle', label: 'Job Title / Craft', type: 'text' },
        ],
      },
      {
        id: 'incident-reference',
        title: 'Incident Reference',
        fields: [
          { id: 'incidentDateTime', label: 'Incident Date & Time', type: 'datetime', required: true },
          { id: 'incidentLocation', label: 'Incident Location', type: 'text', required: true },
          { id: 'involvedEmployee', label: 'Involved Employee / Contractor', type: 'text' },
          { id: 'reportNumber', label: 'Incident / Report Number', type: 'text' },
        ],
      },
      {
        id: 'statement',
        title: 'Witness Location / Statement',
        fields: [
          { id: 'whereLocated', label: 'Where were you located when the incident occurred?', type: 'multiline', required: true },
          { id: 'whatSaw', label: 'What did you see?', type: 'multiline', required: true },
          { id: 'whatHeard', label: 'What did you hear?', type: 'multiline' },
          { id: 'peopleEquipmentInvolved', label: 'People / Equipment Involved', type: 'multiline' },
          { id: 'additionalStatement', label: 'Additional Statement', type: 'multiline' },
          { id: 'willingFollowUp', label: 'Available for follow-up?', type: 'yes-no', options: yesNoOptions },
        ],
      },
      {
        id: 'certification-signature',
        title: 'Statement Certification',
        fields: [
          { id: 'statementAccurate', label: 'I certify this statement is accurate to the best of my knowledge.', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'witnessSignature', label: 'Witness Signature', type: 'signature', required: true },
          { id: 'statementDate', label: 'Statement Date', type: 'date', required: true },
        ],
      },
    ],
  },
  {
    id: 'full-incident-investigation',
    title: 'Full Incident Investigation',
    categoryId: 'incident-near-miss',
    audience: ['hse'],
    description: 'Detailed investigation form for incident facts, employee information, cause, witnesses, corrective actions, summary, signature, and attachments.',
    sourceMockupFamily: 'ZiaWellNav field safety form template',
    sections: [
      {
        id: 'general-details',
        title: 'General Details',
        fields: [
          { id: 'dateReported', label: 'Date Reported', type: 'datetime', required: true },
          { id: 'dateOfIncident', label: 'Date of Incident', type: 'datetime', required: true },
          {
            id: 'incidentType',
            label: 'Incident Type',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'injury', label: 'Injury' },
              { value: 'illness', label: 'Illness' },
              { value: 'motor-vehicle-accident', label: 'Motor Vehicle Accident' },
              { value: 'near-miss', label: 'Near Miss' },
              { value: 'property-damage', label: 'Property Damage' },
              { value: 'spill-release', label: 'Spill / Release' },
              { value: 'other', label: 'Other' },
            ],
          },
          { id: 'companyName', label: 'Company Name', type: 'text', required: true },
          { id: 'locationOfIncident', label: 'Location of Incident', type: 'multiline', required: true },
          { id: 'whatHappened', label: 'What happened?', type: 'multiline', required: true },
        ],
      },
      {
        id: 'employee-information',
        title: 'Employee Information',
        fields: [
          { id: 'employeeInvolved', label: 'Employee Involved in the Incident', type: 'text', required: true },
          { id: 'employeeCellPhone', label: 'Employee Involved in the Incident - Cell Phone', type: 'text' },
          { id: 'employeeHireDate', label: 'Employee Involved in the Incident - Hire Date', type: 'date' },
          { id: 'employeeHireDuration', label: 'Employee Involved in the Incident - Hire Duration', type: 'text' },
          { id: 'employeeJobTitle', label: 'Employee Involved in the Incident - Job Title', type: 'text' },
          { id: 'employeeSupervisor', label: 'Employee Involved in the Incident - Supervisor', type: 'text' },
          { id: 'employeeLineOfBusiness', label: 'Employee Involved in the Incident - Line of Business', type: 'text' },
          { id: 'employeeLocation', label: 'Employee Involved in the Incident - Location', type: 'text' },
          { id: 'reportingLocation', label: 'Reporting Location (Establishment)', type: 'text' },
          { id: 'department', label: 'Department', type: 'text' },
          { id: 'employeeAddress', label: "Employee's Address", type: 'text' },
          { id: 'city', label: 'City', type: 'text' },
          { id: 'state', label: 'State', type: 'text' },
          { id: 'zip', label: 'Zip', type: 'text' },
          {
            id: 'gender',
            label: 'Gender',
            type: 'select',
            options: [
              { value: 'male', label: 'Male' },
              { value: 'female', label: 'Female' },
            ],
          },
          { id: 'dateOfBirth', label: 'Date of Birth', type: 'date' },
          { id: 'preIncidentActivity', label: 'What was the employee doing just before the incident occurred?', type: 'multiline' },
        ],
      },
      {
        id: 'cause-of-incident',
        title: 'Cause of Incident',
        fields: [
          {
            id: 'incidentCause',
            label: 'Incident Cause',
            type: 'checkbox-group',
            options: [
              { value: 'unsafe-act', label: 'Unsafe Act' },
              { value: 'unsafe-condition', label: 'Unsafe Condition' },
            ],
          },
          { id: 'additionalCauseDetails', label: 'Additional Cause of Incident Details', type: 'multiline' },
        ],
      },
      {
        id: 'witness-statements',
        title: 'Witness Statements',
        fields: [
          { id: 'witnessCount', label: '# of Witnesses', type: 'number' },
        ],
      },
      {
        id: 'root-cause-corrective-actions',
        title: 'Root Cause & Corrective Actions',
        fields: [
          { id: 'primaryRootCause', label: 'Primary Root Cause', type: 'multiline' },
          {
            id: 'correctiveActionsImplemented',
            label: '# of Corrective Actions implemented to eliminate potential for reoccurrence?',
            type: 'number',
          },
        ],
      },
      {
        id: 'summary',
        title: 'Summary',
        fields: [
          {
            id: 'recommendations',
            label: 'Based on the information obtained from the investigation what recommendation(s) should be proposed to prevent reoccurrence?',
            type: 'multiline',
          },
          { id: 'additionalNotes', label: 'Additional Notes / Comments', type: 'multiline' },
          { id: 'completedBy', label: 'Completed By', type: 'text', required: true },
          { id: 'jobTitle', label: 'Job Title', type: 'text' },
          { id: 'phone', label: 'Phone', type: 'text' },
          { id: 'investigatorSignature', label: 'Investigator Signature', type: 'signature', required: true },
          { id: 'attachments', label: 'Attachments (documents, photos, sketches, statements, etc.)', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'work-at-heights-fall-rescue-plan',
    title: 'Work at Heights Fall Rescue Plan',
    categoryId: 'work-at-heights',
    audience: ['foreman', 'hse'],
    description: 'Fall rescue plan for rescue team, contacts, methods, equipment, communication, timing, readiness, and sign-off.',
    sourceMockupFamily: 'ZiaWellNav field safety form template',
    sections: [
      {
        id: 'general-details',
        title: 'General Details',
        fields: [
          { id: 'companyWorkingFor', label: 'Company Working For', type: 'text', required: true },
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'preparedBy', label: 'Prepared By', type: 'text', required: true },
          { id: 'facilityName', label: 'Location / Facility Name', type: 'text', required: true },
          { id: 'workArea', label: 'Work Area / Elevation', type: 'text', required: true },
          { id: 'workDescription', label: 'Work Being Performed', type: 'multiline', required: true },
        ],
      },
      {
        id: 'rescue-team-contacts',
        title: 'Rescue Team / Contacts',
        fields: [
          { id: 'competentPerson', label: 'Competent Person', type: 'text', required: true },
          { id: 'designatedRescueLead', label: 'Designated Rescue Lead', type: 'text', required: true },
          {
            id: 'rescueService',
            label: 'Rescue Service / Team',
            type: 'select',
            required: true,
            options: [
              { value: 'on-site', label: 'On-site' },
              { value: 'off-site', label: 'Off-site' },
            ],
          },
          { id: 'contactNamePhone', label: 'Contact Name and Phone Number if Using Off-site Rescue', type: 'text' },
          { id: 'emergencyNumber', label: 'Emergency Contact / Dispatch Number', type: 'text' },
          { id: 'rescueTeamNotified', label: 'Rescue team notified before work?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
        ],
      },
      {
        id: 'rescue-methods-equipment',
        title: 'Rescue Methods / Equipment',
        fields: [
          {
            id: 'fallProtectionSystems',
            label: 'Fall Protection System(s) Used',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'full-body-harness', label: 'Full body harness' },
              { value: 'lanyard', label: 'Lanyard' },
              { value: 'srl', label: 'Self-retracting lifeline' },
              { value: 'horizontal-lifeline', label: 'Horizontal lifeline' },
              { value: 'guardrail', label: 'Guardrail / passive system' },
              { value: 'other', label: 'Other' },
            ],
          },
          { id: 'estimatedRescueMinutes', label: 'Estimated Time to Complete Rescue in Minutes', type: 'number' },
          {
            id: 'rescueMethods',
            label: 'Method(s) Used to Perform Rescue',
            type: 'checkbox-group',
            options: [
              { value: 'suspension-trauma-straps', label: 'Suspension trauma straps' },
              { value: 'self-rescue', label: 'Self-rescue' },
              { value: 'mewp', label: 'Mobile elevated work platform' },
              { value: 'ladder', label: 'Ladder' },
              { value: 'other', label: 'Other' },
            ],
          },
          { id: 'rescueEquipment', label: 'Rescue Equipment Staged', type: 'multiline', required: true },
          { id: 'equipmentInspected', label: 'Has the equipment been inspected prior to use?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'rescuePersonnelCompetent', label: 'Are designated rescue personnel trained and competent?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
        ],
      },
      {
        id: 'communication-access-plan',
        title: 'Communication / Access Plan',
        fields: [
          { id: 'communicationMethod', label: 'Communication Method', type: 'text', required: true },
          { id: 'accessRoute', label: 'Rescue Access Route / Lift Access', type: 'multiline', required: true },
          { id: 'loweringLandingArea', label: 'Lowering / Landing Area', type: 'text' },
          { id: 'areaBarricaded', label: 'Rescue / drop zone barricaded?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'weatherConcerns', label: 'Weather / Site Concerns', type: 'multiline' },
        ],
      },
      {
        id: 'rescue-readiness',
        title: 'Rescue Timing / Readiness',
        fields: [
          { id: 'rescuePlanDescription', label: 'Short Description of Rescue Plan', type: 'multiline', required: true },
          { id: 'estimatedSuspensionTime', label: 'Estimated Suspension Time Before Rescue', type: 'text' },
          { id: 'rescueDrillReviewed', label: 'Rescue steps reviewed with crew?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'rescueLimitations', label: 'Known Rescue Limitations / Concerns', type: 'multiline' },
        ],
      },
      {
        id: 'signoff',
        title: 'Sign-Off',
        fields: [
          { id: 'competentPersonSignature', label: 'Competent Person Signature', type: 'signature', required: true },
          { id: 'workerCrewSignatures', label: 'Worker / Crew Signatures', type: 'participants', required: true },
          { id: 'safetyStandbySignature', label: 'Safety Standby Signature', type: 'signature' },
          { id: 'dateSigned', label: 'Date Signed', type: 'date' },
        ],
      },
    ],
  },
  {
    id: 'fall-distance-clearance-calculation',
    title: 'Fall Distance / Clearance Calculation',
    categoryId: 'work-at-heights',
    audience: ['crew', 'foreman'],
    description: 'Clearance calculation for fall protection setup, anchor height, lanyard/SRL, deceleration, worker height, safety factor, and result.',
    sourceMockupFamily: 'ZiaWellNav field safety form template',
    sections: [
      {
        id: 'work-location-anchor',
        title: 'Work Location / Anchor Point',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'preparedBy', label: 'Prepared By', type: 'text', required: true },
          { id: 'location', label: 'Location / Facility', type: 'text', required: true },
          { id: 'workArea', label: 'Work Area / Elevation', type: 'text', required: true },
          { id: 'anchorPoint', label: 'Anchor Point / Anchorage', type: 'text', required: true },
          { id: 'anchorAboveDRing', label: 'Anchor Above D-Ring?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'fall-protection-system',
        title: 'Fall Protection System',
        fields: [
          {
            id: 'systemType',
            label: 'System Type',
            type: 'select',
            required: true,
            options: [
              { value: 'lanyard', label: 'Shock-absorbing lanyard' },
              { value: 'srl', label: 'Self-retracting lifeline' },
              { value: 'vertical-lifeline', label: 'Vertical lifeline' },
              { value: 'horizontal-lifeline', label: 'Horizontal lifeline' },
              { value: 'other', label: 'Other' },
            ],
          },
          { id: 'harnessInspected', label: 'Harness inspected before use?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'connectorInspected', label: 'Connector / lanyard / SRL inspected?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'manufacturerInstructionsAvailable', label: 'Manufacturer instructions available?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'clearance-inputs',
        title: 'Clearance Inputs',
        description: 'Enter distances in feet unless your crew notes another unit.',
        fields: [
          { id: 'lanyardOrSrlLength', label: 'Lanyard / SRL Length', type: 'number', required: true },
          { id: 'decelerationDistance', label: 'Deceleration Distance', type: 'number', required: true },
          { id: 'workerHeight', label: 'Worker Height', type: 'number', required: true },
          { id: 'dRingShiftHarnessStretch', label: 'D-Ring Shift / Harness Stretch', type: 'number' },
          { id: 'safetyFactor', label: 'Safety Factor', type: 'number', required: true },
          { id: 'lowerLevelClearance', label: 'Lower-Level Clearance Available', type: 'number', required: true },
        ],
      },
      {
        id: 'clearance-result',
        title: 'Clearance Result',
        fields: [
          { id: 'calculatedRequiredClearance', label: 'Calculated Required Clearance', type: 'number', required: true },
          { id: 'clearanceResult', label: 'Clearance Result', type: 'pass-fail-na', required: true, options: passFailNaOptions },
          { id: 'correctionsRequired', label: 'Corrections / Alternate Protection Required', type: 'multiline' },
          { id: 'competentPersonSignature', label: 'Competent Person Signature', type: 'signature', required: true },
          { id: 'workerSignature', label: 'Worker Signature', type: 'signature' },
        ],
      },
    ],
  },
  {
    id: 'suspension-trauma-rescue-equipment-checklist',
    title: 'Suspension Trauma / Rescue Equipment Checklist',
    categoryId: 'work-at-heights',
    audience: ['crew'],
    description: 'Checklist for suspension trauma readiness, rescue equipment staging, access, communication, and rescue crew sign-off.',
    sourceMockupFamily: 'ZiaWellNav field safety form template',
    sections: [
      {
        id: 'general-details',
        title: 'General Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'location', label: 'Location / Facility', type: 'text', required: true },
          { id: 'workArea', label: 'Work Area / Elevation', type: 'text', required: true },
          { id: 'inspectedBy', label: 'Inspected By', type: 'text', required: true },
        ],
      },
      {
        id: 'suspension-trauma-readiness',
        title: 'Suspension Trauma Readiness',
        fields: [
          { id: 'traumaStrapsAvailable', label: 'Suspension trauma straps available?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'workerTrainedToUseStraps', label: 'Worker trained to use suspension trauma straps?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'rescueTimeUnderstood', label: 'Crew understands time-sensitive suspension trauma risk?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'postFallMedicalPlan', label: 'Post-fall medical evaluation plan reviewed?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'rescue-equipment',
        title: 'Rescue Equipment',
        fields: [
          { id: 'rescueKit', label: 'Rescue kit staged and inspected', type: 'pass-fail-na', required: true, options: passFailNaOptions },
          { id: 'ladderLiftAccess', label: 'Ladder / lift / MEWP access ready', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'loweringDevice', label: 'Lowering / retrieval device ready', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'communicationDevice', label: 'Communication device tested', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'firstAidAed', label: 'First aid / AED available if required', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'barricades', label: 'Barricades / exclusion zone available', type: 'pass-fail-na', options: passFailNaOptions },
        ],
      },
      {
        id: 'access-communication',
        title: 'Access / Communication',
        fields: [
          { id: 'rescueAccessRoute', label: 'Rescue Access Route', type: 'multiline', required: true },
          { id: 'communicationPlan', label: 'Communication Plan', type: 'multiline', required: true },
          { id: 'emergencyContact', label: 'Emergency Contact / Dispatch', type: 'text' },
          { id: 'knownObstructions', label: 'Known Obstructions / Special Conditions', type: 'multiline' },
        ],
      },
      {
        id: 'signoff',
        title: 'Rescue Crew Sign-Off',
        fields: [
          { id: 'rescueLeadSignature', label: 'Rescue Lead Signature', type: 'signature', required: true },
          { id: 'competentPersonSignature', label: 'Competent Person Signature', type: 'signature', required: true },
          { id: 'crewSignatures', label: 'Worker / Crew Signatures', type: 'participants' },
        ],
      },
    ],
  },
  {
    id: 'safety-audit-field-observation',
    title: 'Safety Audit / Field Observation',
    categoryId: 'safety-programs',
    audience: ['hse', 'company_man'],
    description: 'Structured safety audit and field observation intake for safe behaviors, at-risk conditions, policy gaps, controls, evidence, and recommended corrective actions.',
    sourceMockupFamily: 'ZiaWellNav field safety form template',
    sections: [
      {
        id: 'audit-details',
        title: 'Audit / Observation Details',
        fields: [
          { id: 'auditDate', label: 'Audit Date', type: 'date', required: true },
          { id: 'locationFacility', label: 'Location / Facility', type: 'text', required: true },
          { id: 'crew', label: 'Crew', type: 'text', required: true },
          { id: 'observer', label: 'Observer', type: 'text', required: true },
          { id: 'workActivity', label: 'Work Activity', type: 'text', required: true },
        ],
      },
      {
        id: 'finding-classification',
        title: 'Finding Classification',
        fields: [
          {
            id: 'observationType',
            label: 'Observation Type',
            type: 'select',
            required: true,
            options: [
              { value: 'safe-behavior', label: 'Safe behavior' },
              { value: 'at-risk-condition', label: 'At-risk condition' },
              { value: 'policy-gap', label: 'Policy gap' },
              { value: 'housekeeping', label: 'Housekeeping' },
              { value: 'ppe', label: 'PPE' },
              { value: 'equipment', label: 'Equipment' },
              { value: 'vehicle-fleet', label: 'Vehicle / fleet' },
              { value: 'permit-control-issue', label: 'Permit / control issue' },
            ],
          },
          {
            id: 'severity',
            label: 'Severity',
            type: 'select',
            required: true,
            options: [
              { value: 'low', label: 'Low' },
              { value: 'medium', label: 'Medium' },
              { value: 'high', label: 'High' },
              { value: 'critical', label: 'Critical' },
            ],
          },
          { id: 'findingDescription', label: 'Finding Description', type: 'multiline', required: true },
          { id: 'immediateActionTaken', label: 'Immediate Action Taken', type: 'multiline' },
          { id: 'evidenceAttachment', label: 'Photo / Evidence Attachment', type: 'attachment' },
        ],
      },
      {
        id: 'corrective-action-recommendation',
        title: 'Corrective Action Recommendation',
        fields: [
          { id: 'correctiveActionNeeded', label: 'Corrective Action Needed?', type: 'yes-no', required: true },
          { id: 'recommendedCorrectiveAction', label: 'Recommended Corrective Action', type: 'multiline' },
          { id: 'recommendedDueDate', label: 'Recommended Due Date', type: 'date' },
        ],
      },
      {
        id: 'observer-signoff',
        title: 'Observer Sign-Off',
        fields: [
          { id: 'observerSignature', label: 'Observer Signature', type: 'signature', required: true },
        ],
      },
    ],
  },
  {
    id: 'behavior-based-safety-observation',
    title: 'Behavior Based Safety Observation',
    categoryId: 'safety-programs',
    audience: ['crew', 'foreman', 'hse', 'company_man', 'driver'],
    description: 'Field BBS observation form for PPE, body position, tools, mobile equipment, housekeeping, procedures, coaching, and safe or at-risk behaviors.',
    sourceMockupFamily: 'ZiaWellNav oilfield field-form review',
    sections: [
      {
        id: 'general-details',
        title: 'General Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'observerName', label: 'Observer Name', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company Working For', type: 'text', required: true },
          { id: 'locationOfObservation', label: 'Location of Observation', type: 'text', required: true },
          { id: 'department', label: 'Department / Crew', type: 'text' },
          { id: 'jobDescription', label: 'Job Description / Task', type: 'multiline', required: true },
        ],
      },
      {
        id: 'ppe-observation',
        title: 'Personal Protective Equipment',
        fields: [
          { id: 'headProtection', label: 'Head protection', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'eyeProtection', label: 'Eye protection', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'faceProtection', label: 'Face protection', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'hearingProtection', label: 'Hearing protection', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'handProtection', label: 'Hand protection', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'footProtection', label: 'Foot protection', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'respiratoryProtection', label: 'Respiratory protection', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'gasMonitor', label: 'H2S / gas monitor used when required', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'fallProtection', label: 'Fall protection used when required', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'ppeComments', label: 'PPE comments', type: 'multiline' },
        ],
      },
      {
        id: 'body-position',
        title: 'Body Position',
        fields: [
          { id: 'liftingReaching', label: 'Lifting, pushing, pulling, or reaching posture', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'lineOfFire', label: 'Line of fire avoided', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'pinchPoints', label: 'Pinch points controlled', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'handPlacement', label: 'Hands placed clear of hazards', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'bodyPositionComments', label: 'Body position comments', type: 'multiline' },
        ],
      },
      {
        id: 'tools-equipment',
        title: 'Tools & Equipment',
        fields: [
          { id: 'properToolSelection', label: 'Proper tool selected for task', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'toolsUsedProperly', label: 'Tools used properly', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'toolsGoodCondition', label: 'Tools and equipment in good condition', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'guardsBarriers', label: 'Guards or barriers in place when required', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'toolsComments', label: 'Tools and equipment comments', type: 'multiline' },
        ],
      },
      {
        id: 'mobile-housekeeping-procedures',
        title: 'Mobile Equipment / Housekeeping / Procedures',
        fields: [
          { id: 'fitForDuty', label: 'Worker fit for duty', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'movementBackingSpotter', label: 'Movement, backing, or spotter controls used', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'lockoutTagout', label: 'Lockout / tagout used when required', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'loadSecurement', label: 'Load securement acceptable', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'equipmentInspections', label: 'Required inspections completed', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'walkingWorkingSurfaces', label: 'Walking / working surfaces clear', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'materialChemicalStorage', label: 'Materials and chemicals stored correctly', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'proceduresUnderstood', label: 'Procedures known, understood, and followed', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'preJobPlanningJsa', label: 'Pre-job planning / JSA completed', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'communication', label: 'Communication clear for the task', type: 'sat-unsat-na', options: safeAtRiskNaOptions },
          { id: 'procedureComments', label: 'Housekeeping / procedure comments', type: 'multiline' },
        ],
      },
      {
        id: 'summary-signoff',
        title: 'Summary / Sign-Off',
        fields: [
          {
            id: 'overallObservation',
            label: 'Overall Observation',
            type: 'select',
            required: true,
            options: [
              { value: 'safe', label: 'Safe behavior observed' },
              { value: 'coached-corrected', label: 'At-risk behavior coached / corrected' },
              { value: 'follow-up-needed', label: 'Follow-up needed' },
            ],
          },
          { id: 'safeBehaviors', label: 'Safe Behaviors Observed', type: 'multiline' },
          { id: 'atRiskBehaviors', label: 'At-Risk Behaviors / Conditions', type: 'multiline' },
          { id: 'coachingCompleted', label: 'Coaching or conversation completed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'correctiveActionNeeded', label: 'Corrective action needed?', type: 'yes-no' },
          { id: 'additionalComments', label: 'Additional Comments', type: 'multiline' },
          { id: 'attachments', label: 'Photos / Supporting Documents', type: 'attachment' },
          { id: 'observerSignature', label: 'Observer Signature', type: 'signature', required: true },
        ],
      },
    ],
  },
  {
    id: 'on-site-excavation-checklist',
    title: 'On-Site Excavation Checklist',
    categoryId: 'safety-programs',
    audience: ['crew', 'foreman'],
    description: 'Excavation readiness checklist for one-call verification, underground line controls, crew briefing, competent-person review, and stop-work triggers.',
    sourceMockupFamily: 'ZiaWellNav oilfield field-form review',
    sections: [
      {
        id: 'inspection-details',
        title: 'Inspection Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'preparedBy', label: 'Prepared By', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company Working For', type: 'text' },
          { id: 'locationFacilityName', label: 'Location / Facility Name', type: 'text', required: true },
          { id: 'excavationDescription', label: 'Excavation Scope / Description', type: 'multiline', required: true },
        ],
      },
      {
        id: 'one-call-line-locate',
        title: 'One-Call / Line Locate',
        fields: [
          { id: 'oneCallTicketNumber', label: 'One-call / locate ticket number', type: 'text', required: true },
          { id: 'mapLocationVerified', label: 'Map or sketch verifies company name and site location?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'oneCallVerified', label: 'One-call details verified before excavation?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'companyIncluded', label: 'Company being worked for included on the locate request?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'utilityOwnersNotified', label: 'Utility / line owners notified or identified?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'hardCopyAvailable', label: 'Locate ticket or approval available to crew?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'secondarySweepCompleted', label: 'Secondary line sweep completed and reviewed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'utilityResponsesReviewed', label: 'Positive responses reviewed and open items closed?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'lineLocateAttachments', label: 'Locate tickets / maps / photos', type: 'attachment' },
        ],
      },
      {
        id: 'excavation-readiness',
        title: 'Excavation Readiness',
        fields: [
          { id: 'crewBriefed', label: 'Crew briefed on excavation plan and line hazards?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'mechanicalExcavationRestricted', label: 'Mechanical excavation restricted near known or suspected lines?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'toleranceZoneHandDigging', label: 'Tolerance-zone hand digging or soft digging plan reviewed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'competentPersonDesignated', label: 'Competent person designated for excavation conditions?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'accessEgressReady', label: 'Access / egress ready for excavation depth and conditions?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'spoilSetbackMaintained', label: 'Spoil pile and equipment setback maintained?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'barricadesInstalled', label: 'Barricades, cones, or exclusion zone installed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'waterAccumulationControlled', label: 'Water accumulation and cave-in potential reviewed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'atmosphereReviewed', label: 'Atmospheric hazards reviewed when applicable?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'stop-work-signoff',
        title: 'Stop-Work Review / Sign-Off',
        fields: [
          { id: 'unknownUtilitiesStopWork', label: 'Unknown or unmarked utilities require stop-work and re-planning?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'skippedIfPartJob', label: 'Skipped excavation is documented if excavation is not part of the job?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'noAnswersStopAndReplan', label: 'Any No answer triggers supervisor review before digging?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'comments', label: 'Comments / Notes', type: 'multiline' },
          { id: 'competentPersonSignature', label: 'Competent Person Signature', type: 'signature', required: true },
          { id: 'foremanSignature', label: 'Foreman / Supervisor Signature', type: 'signature', required: true },
        ],
      },
    ],
  },
  {
    id: 'critical-task-hot-work-checklist',
    title: 'Critical Task Hot Work Checklist',
    categoryId: 'safety-programs',
    audience: ['crew', 'foreman'],
    description: 'Critical-task checklist for energized or high-risk hot work involving line clearance, grounding, phase holders, tools, PPE, communications, and supervisor approval.',
    sourceMockupFamily: 'ZiaWellNav oilfield field-form review',
    sections: [
      {
        id: 'general-details',
        title: 'General Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'preparedBy', label: 'Prepared By', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company Working For', type: 'text', required: true },
          { id: 'facilityName', label: 'Location / Facility Name', type: 'text', required: true },
          { id: 'unitIdentifier', label: 'Unit ID or Identifier', type: 'text', required: true },
          { id: 'department', label: 'Department / Crew', type: 'text' },
          { id: 'supervisorForemanName', label: 'Supervisor / Foreman Name', type: 'text', required: true },
          { id: 'taskDescription', label: 'Critical Task Description', type: 'multiline', required: true },
        ],
      },
      {
        id: 'electrical-readiness',
        title: 'Electrical Readiness',
        fields: [
          { id: 'trucksGrounded', label: 'Trucks or equipment properly grounded?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'voltageVerified', label: 'Nominal voltage verified?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'trucksPositioned', label: 'Trucks positioned with outriggers, pads, and chocks as needed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'phaseHolderInspected', label: 'Phase holder inspected prior to use?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'groundMenGloves', label: 'Ground personnel wearing proper class gloves?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'staffQualified', label: 'Personnel qualified for the task?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'exclusionZoneSet', label: 'Barricades or exclusion zones established?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'permitComplete', label: 'Required hot-work or electrical permit complete?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'hotStickInspected', label: 'Hot stick inspected and in good condition?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'shotgunsInspected', label: 'Shotguns or live-line tools inspected?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'reclosersOneShot', label: 'Reclosers set to one-shot when required?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'properPpe', label: 'Employees wearing task-specific PPE?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'harnessTiedOff', label: 'Harness tied off before elevated work?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'line-crossing-controls',
        title: 'Line Clearance / Crossing Controls',
        fields: [
          { id: 'safeDistanceMaintained', label: 'Safe working distance maintained while boom is elevated?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'handVoiceSignals', label: 'Hand and voice signals reviewed before starting?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'affectedCrewVoltageReviewed', label: 'Affected crew members briefed on voltage and task limits?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'insulatedToolsInspected', label: 'Insulated tools inspected before use?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'correctCoverage', label: 'Correct coverage installed for each phase?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'phaseHoldersClearance', label: 'Phase holders maintained proper line clearance?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'lineVoltageOwnerVerified', label: 'Line voltage and owner verified?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'ownerPermissionToCross', label: 'Line owner permission to cross received?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'lineOwner', label: 'Line Owner / Utility Contact', type: 'text' },
          { id: 'equipmentDielectricTested', label: 'Equipment dielectric test current or available?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'wireSocksChecked', label: 'Wire socks, binders, or grips inspected?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'certificationsChecked', label: 'Required certifications checked before job start?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'oneCallRequired', label: 'One-call or excavation review required for the task?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'stop-work-signoff',
        title: 'Stop-Work Review / Sign-Off',
        fields: [
          {
            id: 'hotWorkStopAuthorization',
            label: 'Foreman / supervisor approval required before starting or resuming work?',
            type: 'yes-no-na',
            options: yesNoNaOptions,
            required: true,
            helperText: 'Use this field to document stop-work approval before beginning a high-risk critical task.',
          },
          { id: 'replanNeeded', label: 'Any No answer requires stop-work and re-planning?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'additionalComments', label: 'Additional Comments', type: 'multiline' },
          { id: 'employeesPerformingWork', label: 'Employees Performing Work', type: 'participants' },
          { id: 'attachments', label: 'Photos / Supporting Documents', type: 'attachment' },
          { id: 'foremanSignature', label: 'Foreman / Supervisor Signature', type: 'signature', required: true },
        ],
      },
    ],
  },
  {
    id: 'stop-work-authority',
    title: 'Stop Work Authority',
    categoryId: 'safety-programs',
    audience: ['crew', 'foreman', 'hse', 'company_man', 'driver'],
    description: 'Field stop-work form for documenting unsafe conditions, corrective actions, restart approval, and crew communication.',
    sourceMockupFamily: 'ZiaWellNav rewritten safety program reference',
    sections: [
      {
        id: 'stop-work-details',
        title: 'Stop Work Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'initiatedBy', label: 'Stop Work Initiated By', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company Working For', type: 'text' },
          { id: 'supervisorNotified', label: 'Supervisor / Lead Notified', type: 'text', required: true },
          { id: 'taskOrOperation', label: 'Task / Operation Stopped', type: 'text', required: true },
          { id: 'customerContactConfirmed', label: 'Customer / operator contact confirmed', type: 'text' },
          { id: 'location', label: 'Exact Location', type: 'text' },
        ],
      },
      {
        id: 'reason-controls',
        title: 'Reason / Immediate Controls',
        fields: [
          { id: 'unsafeConditionAct', label: 'Unsafe Condition or Action Observed', type: 'multiline', required: true },
          { id: 'hseRiskControlUnclear', label: 'Was the HSE risk or control unclear?', type: 'yes-no', required: true },
          { id: 'affectedPersonnelNotified', label: 'Affected personnel notified?', type: 'yes-no', required: true },
          { id: 'siteSpecificRequirementsReviewed', label: 'Site-specific requirements reviewed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'permitAuthorizationNeeded', label: 'Permit or authorization needed before restart?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'environmentalResourceControlsReviewed', label: 'Environmental or resource controls reviewed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'immediateControls', label: 'Immediate Controls Put In Place', type: 'multiline', required: true },
        ],
      },
      {
        id: 'correction-resume',
        title: 'Corrective Action / Resume',
        fields: [
          { id: 'correctiveAction', label: 'Corrective Action Completed', type: 'multiline' },
          { id: 'issueResolved', label: 'Issue resolved before restart?', type: 'yes-no' },
          { id: 'workResumed', label: 'Work resumed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'resumeTime', label: 'Resume Time', type: 'datetime' },
          { id: 'lessonsLearned', label: 'Lessons Learned / Crew Communication', type: 'multiline' },
        ],
      },
      {
        id: 'review-signoff',
        title: 'Review / Sign-Off',
        fields: [
          { id: 'employeeSignature', label: 'Employee Signature', type: 'signature', required: true },
          { id: 'supervisorReview', label: 'Supervisor Review Notes', type: 'multiline' },
          { id: 'supervisorSignature', label: 'Supervisor Signature', type: 'signature', required: true },
          { id: 'attachments', label: 'Photos / Supporting Documents', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'aerial-lift-safety-check',
    title: 'Aerial Lift Safety Check',
    categoryId: 'safety-programs',
    audience: ['crew'],
    description: 'Pre-use aerial lift safety form covering controls, load limits, overhead clearance, fall protection, and operator sign-off.',
    sourceMockupFamily: 'ZiaWellNav rewritten safety program reference',
    sections: [
      {
        id: 'lift-site-details',
        title: 'Lift / Site Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'operatorName', label: 'Operator Name', type: 'text', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'liftType', label: 'Lift Type', type: 'text', required: true },
          { id: 'unitNumber', label: 'Unit Number', type: 'text' },
          { id: 'manufacturerLoadLimit', label: 'Manufacturer Load Limit', type: 'text' },
          { id: 'taskLocation', label: 'Task Location / Work Area', type: 'text' },
        ],
      },
      {
        id: 'pre-use-checks',
        title: 'Pre-Use Safety Checks',
        fields: [
          { id: 'loadLimitsPosted', label: 'Load limits posted and understood', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'controlsTested', label: 'Controls tested before use', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'trainedOperator', label: 'Operator trained for this lift', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'backupAlarmSpotter', label: 'Backup alarm or spotter available', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'overheadClearance', label: 'Overhead clearance confirmed', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'fallProtectionReady', label: 'Fall arrest / travel restraint ready', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'basketFloorClear', label: 'Basket floor clear and stable footing maintained', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'noAdjacentTieOff', label: 'No tie-off to adjacent structures', type: 'pass-fail-na', options: passFailNaOptions },
        ],
      },
      {
        id: 'hazards-controls',
        title: 'Hazards / Controls',
        fields: [
          { id: 'overheadLinesPresent', label: 'Overhead power lines present?', type: 'yes-no', required: true },
          { id: 'minimumClearanceMaintained', label: 'Minimum power-line clearance maintained?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'groundConditions', label: 'Ground / Surface Conditions', type: 'multiline' },
          { id: 'weatherConditions', label: 'Weather / Wind Conditions', type: 'text' },
          { id: 'additionalControls', label: 'Additional Controls', type: 'multiline' },
        ],
      },
      {
        id: 'authorization-signoff',
        title: 'Authorization / Sign-Off',
        fields: [
          { id: 'operatorSignature', label: 'Operator Signature', type: 'signature', required: true },
          { id: 'supervisorSignature', label: 'Supervisor Signature', type: 'signature' },
          { id: 'attachments', label: 'Photos / Supporting Documents', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'welding-cutting-hot-work-permit',
    title: 'Welding / Cutting / Hot Work Permit',
    categoryId: 'safety-programs',
    audience: ['foreman'],
    description: 'Hot-work permit for controlling sparks, combustibles, fire watch, PPE, atmospheric checks, approvals, and closeout.',
    sourceMockupFamily: 'ZiaWellNav rewritten safety program reference',
    sections: [
      {
        id: 'hot-work-details',
        title: 'Hot Work Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'requestedBy', label: 'Requested By', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company Working For', type: 'text' },
          { id: 'exactLocation', label: 'Exact Hot Work Location', type: 'text', required: true },
          { id: 'workDescription', label: 'Work Description', type: 'multiline', required: true },
          {
            id: 'hotWorkType',
            label: 'Hot Work Type',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'welding', label: 'Welding' },
              { value: 'cutting', label: 'Cutting' },
              { value: 'brazing', label: 'Brazing / Soldering' },
              { value: 'grinding', label: 'Grinding' },
              { value: 'thawing', label: 'Thawing pipes' },
              { value: 'other', label: 'Other spark or flame work' },
            ],
          },
        ],
      },
      {
        id: 'fire-prevention',
        title: 'Fire Prevention',
        fields: [
          { id: 'equipmentInspected', label: 'Welding / cutting equipment inspected', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'defectiveEquipmentRemoved', label: 'Defective equipment removed from service', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'combustiblesControlled', label: 'Combustibles removed or protected', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'sparksControlled', label: 'Sparks, slag, and hot material controlled', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'extinguisherAvailable', label: 'Fire extinguisher available', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'ventilationAdequate', label: 'Ventilation adequate', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'atmosphereAcceptable', label: 'Atmosphere checked and acceptable if required', type: 'pass-fail-na', options: passFailNaOptions },
        ],
      },
      {
        id: 'fire-watch-ppe',
        title: 'Fire Watch / PPE',
        fields: [
          { id: 'fireWatchRequired', label: 'Fire watch required?', type: 'yes-no', required: true },
          { id: 'fireWatchName', label: 'Fire Watch Name', type: 'text' },
          { id: 'fireWatchDuration', label: 'Fire Watch Duration After Work', type: 'text' },
          {
            id: 'ppeUsed',
            label: 'PPE Used',
            type: 'checkbox-group',
            options: [
              { value: 'welding-hood', label: 'Welding hood / face shield' },
              { value: 'gloves', label: 'Welding gloves' },
              { value: 'fr-clothing', label: 'FR clothing' },
              { value: 'respiratory', label: 'Respiratory protection' },
              { value: 'hearing', label: 'Hearing protection' },
              { value: 'other', label: 'Other task-specific PPE' },
            ],
          },
        ],
      },
      {
        id: 'approval-closeout',
        title: 'Approval / Closeout',
        fields: [
          { id: 'permitStart', label: 'Permit Start', type: 'datetime', required: true },
          { id: 'permitEnd', label: 'Permit End', type: 'datetime' },
          { id: 'approverName', label: 'Approver Name', type: 'text', required: true },
          { id: 'finalAreaInspection', label: 'Final area inspection complete?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'approverSignature', label: 'Approver Signature', type: 'signature', required: true },
          { id: 'attachments', label: 'Photos / Supporting Documents', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'crane-signaling-checklist',
    title: 'Crane Signaling Checklist',
    categoryId: 'safety-programs',
    audience: ['crew'],
    description: 'Crane signaling checklist for signal-person assignment, communication method, visibility limits, and stop-work controls.',
    sourceMockupFamily: 'ZiaWellNav rewritten safety program reference',
    sections: [
      {
        id: 'lift-signal-details',
        title: 'Lift / Signal Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'craneHoistId', label: 'Crane / Hoist ID', type: 'text', required: true },
          { id: 'operatorName', label: 'Operator Name', type: 'text', required: true },
          { id: 'signalPersonName', label: 'Signal Person Name', type: 'text', required: true },
          {
            id: 'communicationMethod',
            label: 'Primary Communication Method',
            type: 'select',
            required: true,
            options: [
              { value: 'hand', label: 'Hand signals' },
              { value: 'voice', label: 'Voice signals' },
              { value: 'radio', label: 'Radio' },
              { value: 'other', label: 'Other' },
            ],
          },
          { id: 'loadDescription', label: 'Load Description', type: 'text' },
        ],
      },
      {
        id: 'signal-need-visibility',
        title: 'Signal Need / Visibility',
        fields: [
          {
            id: 'signalReason',
            label: 'Why a Signal Person Is Needed',
            type: 'checkbox-group',
            options: [
              { value: 'load-path-not-visible', label: 'Load path not fully visible' },
              { value: 'travel-view-obstructed', label: 'Travel view obstructed' },
              { value: 'site-safety', label: 'Site safety concerns' },
              { value: 'other', label: 'Other lift condition' },
            ],
          },
          { id: 'signalPersonQualified', label: 'Signal person qualified for the task?', type: 'yes-no', required: true },
          { id: 'oneSignalPersonDesignated', label: 'One primary signal person designated?', type: 'yes-no', required: true },
          { id: 'emergencyStopUnderstood', label: 'Emergency stop signal understood by crew?', type: 'yes-no', required: true },
        ],
      },
      {
        id: 'communication-controls',
        title: 'Communication Controls',
        fields: [
          { id: 'communicationDeviceTested', label: 'Communication device tested if used', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'signalsClearReliable', label: 'Signals are clear and reliable', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'backupMethodDefined', label: 'Backup communication method defined', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'stopIfInterrupted', label: 'Lift stops if signals are interrupted', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'preJobMeetingHeld', label: 'Operator and signal person completed pre-job meeting', type: 'pass-fail-na', options: passFailNaOptions },
        ],
      },
      {
        id: 'signoff',
        title: 'Signal Crew Sign-Off',
        fields: [
          { id: 'operatorSignature', label: 'Operator Signature', type: 'signature', required: true },
          { id: 'signalPersonSignature', label: 'Signal Person Signature', type: 'signature', required: true },
          { id: 'supervisorSignature', label: 'Supervisor Signature', type: 'signature' },
          { id: 'attachments', label: 'Photos / Supporting Documents', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'vacuum-truck-operations-checklist',
    title: 'Vacuum Truck Operations Checklist',
    categoryId: 'safety-programs',
    audience: ['crew', 'driver'],
    description: 'Vacuum truck safety checklist for vapor hazards, air monitoring, equipment readiness, spill prevention, and emergency controls.',
    sourceMockupFamily: 'ZiaWellNav rewritten safety program reference',
    sections: [
      {
        id: 'operation-details',
        title: 'Operation Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'operatorName', label: 'Operator Name', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company Working For', type: 'text' },
          { id: 'truckUnit', label: 'Truck / Trailer Unit', type: 'text', required: true },
          { id: 'materialFluid', label: 'Material / Fluid Being Handled', type: 'text', required: true },
          { id: 'sourceLocation', label: 'Source Location', type: 'text' },
          { id: 'destination', label: 'Destination / Disposal Location', type: 'text' },
        ],
      },
      {
        id: 'hazards-monitoring',
        title: 'Hazards / Monitoring',
        fields: [
          {
            id: 'hazardsPresent',
            label: 'Hazards Present',
            type: 'checkbox-group',
            options: [
              { value: 'toxic-vapors', label: 'Toxic vapors' },
              { value: 'flammable-atmosphere', label: 'Flammable atmosphere' },
              { value: 'h2s', label: 'H2S potential' },
              { value: 'spill-release', label: 'Spill / release potential' },
              { value: 'traffic', label: 'Traffic / backing exposure' },
              { value: 'slips-trips', label: 'Slips, trips, or uneven ground' },
              { value: 'other', label: 'Other' },
            ],
          },
          { id: 'continuousAirMonitoring', label: 'Continuous air monitoring required or used?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'firstAidAvailable', label: 'First aid / emergency equipment available?', type: 'yes-no', required: true },
          { id: 'emergencyPlanReviewed', label: 'Emergency plan reviewed with crew?', type: 'yes-no', required: true },
        ],
      },
      {
        id: 'equipment-readiness',
        title: 'Equipment Readiness',
        fields: [
          { id: 'equipmentSuitable', label: 'Vacuum equipment suitable for material', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'tankFilterClean', label: 'Tank, filter, and separator condition checked', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'hosesFittingsInspected', label: 'Hoses and fittings inspected', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'groundingBondingSet', label: 'Grounding / bonding set when required', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'ignitionSourcesControlled', label: 'Ignition sources controlled', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'ventingAreaMonitored', label: 'Venting area monitored and controlled', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'ppeAvailable', label: 'Required PPE available', type: 'pass-fail-na', options: passFailNaOptions, required: true },
        ],
      },
      {
        id: 'authorization-signoff',
        title: 'Authorization / Sign-Off',
        fields: [
          { id: 'supervisorApproval', label: 'Supervisor Approval', type: 'signature' },
          { id: 'operatorSignature', label: 'Operator Signature', type: 'signature', required: true },
          { id: 'safetyRepSignature', label: 'Safety Rep Signature', type: 'signature' },
          { id: 'attachments', label: 'Photos / Supporting Documents', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'work-near-overhead-lines-checklist',
    title: 'Work Near Overhead Lines Checklist',
    categoryId: 'safety-programs',
    audience: ['crew', 'foreman', 'company_man'],
    description: 'Checklist for working on or near overhead lines with structure support, approach distance, PPE, and qualified-person review.',
    sourceMockupFamily: 'ZiaWellNav rewritten safety program reference',
    sections: [
      {
        id: 'work-details',
        title: 'Work Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'crewLead', label: 'Crew Lead', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company Working For', type: 'text' },
          { id: 'poleLineLocation', label: 'Pole / Line Location', type: 'text', required: true },
          { id: 'workDescription', label: 'Work Description', type: 'multiline', required: true },
        ],
      },
      {
        id: 'structure-load-assessment',
        title: 'Structure / Load Assessment',
        fields: [
          { id: 'structureInspected', label: 'Pole, tower, or structure inspected', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'structureSupportsLoad', label: 'Structure can support expected load', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'braceSupportInstalled', label: 'Brace or support installed if needed', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'addedStressReviewed', label: 'Added or unbalanced stress reviewed', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'weatherAcceptable', label: 'Weather acceptable for work', type: 'pass-fail-na', options: passFailNaOptions },
        ],
      },
      {
        id: 'electrical-protection',
        title: 'Electrical Protection',
        fields: [
          { id: 'approachDistanceConfirmed', label: 'Minimum approach distance confirmed', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'electricalPpeSelected', label: 'Electrical PPE selected for exposure', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'insulatingDevicesAvailable', label: 'Insulating devices available if required', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'headProtectionNonconductive', label: 'Nonconductive head protection used if needed', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'equipmentInspectedTested', label: 'Tools and equipment inspected / tested', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'energizedControlsReviewed', label: 'Energized conductor controls reviewed', type: 'pass-fail-na', options: passFailNaOptions },
        ],
      },
      {
        id: 'authorization-signoff',
        title: 'Authorization / Sign-Off',
        fields: [
          { id: 'qualifiedPerson', label: 'Qualified Person', type: 'text', required: true },
          { id: 'crewLeadSignature', label: 'Crew Lead Signature', type: 'signature', required: true },
          { id: 'supervisorSignature', label: 'Supervisor Signature', type: 'signature' },
          { id: 'attachments', label: 'Photos / Supporting Documents', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'medical-exposure-records-access-request',
    title: 'Medical / Exposure Records Access Request',
    categoryId: 'safety-programs',
    audience: ['hse'],
    description: 'Request form for employee medical or exposure records, designated representative authorization, privacy handling, and completion notes.',
    sourceMockupFamily: 'ZiaWellNav rewritten safety program reference',
    sections: [
      {
        id: 'request-details',
        title: 'Request Details',
        fields: [
          { id: 'requestDate', label: 'Request Date', type: 'date', required: true },
          { id: 'employeeName', label: 'Employee Name', type: 'text', required: true },
          { id: 'employeePhone', label: 'Employee Phone', type: 'text' },
          { id: 'companyWorkingFor', label: 'Company Working For', type: 'text' },
          { id: 'jobTitle', label: 'Job Title', type: 'text' },
          { id: 'workLocation', label: 'Work Location', type: 'text' },
        ],
      },
      {
        id: 'record-access',
        title: 'Record Access',
        fields: [
          {
            id: 'requestType',
            label: 'Records Requested',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'medical-records', label: 'Medical records' },
              { value: 'exposure-records', label: 'Exposure records' },
              { value: 'exposure-analysis', label: 'Exposure analysis' },
              { value: 'representative-authorization', label: 'Designated representative authorization' },
              { value: 'other', label: 'Other related records' },
            ],
          },
          { id: 'recordsRequested', label: 'Specific Records Requested', type: 'multiline', required: true },
          { id: 'dateRange', label: 'Date Range', type: 'text' },
          {
            id: 'preferredDelivery',
            label: 'Preferred Delivery',
            type: 'select',
            options: [
              { value: 'review-onsite', label: 'Review onsite' },
              { value: 'copy', label: 'Copy' },
              { value: 'email', label: 'Email if approved' },
              { value: 'other', label: 'Other' },
            ],
          },
        ],
      },
      {
        id: 'representative-privacy',
        title: 'Representative / Privacy',
        fields: [
          { id: 'designatedRepresentative', label: 'Designated Representative', type: 'text' },
          { id: 'writtenAuthorizationAttached', label: 'Written authorization attached?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'confidentialHandlingAcknowledged', label: 'Confidential handling acknowledged?', type: 'yes-no', required: true },
        ],
      },
      {
        id: 'completion-signoff',
        title: 'Completion / Sign-Off',
        fields: [
          { id: 'requestedBySignature', label: 'Requested By Signature', type: 'signature', required: true },
          { id: 'companyReviewer', label: 'Company Reviewer', type: 'text' },
          { id: 'dateCompleted', label: 'Date Completed', type: 'date' },
          { id: 'notes', label: 'Completion Notes', type: 'multiline' },
          { id: 'attachments', label: 'Supporting Documents', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'silica-exposure-control-checklist',
    title: 'Silica Exposure Control Checklist',
    categoryId: 'safety-programs',
    audience: ['crew', 'foreman'],
    description: 'Field checklist for silica-generating work, dust controls, respiratory protection, training, sampling, and corrective actions.',
    sourceMockupFamily: 'ZiaWellNav rewritten safety program reference',
    sections: [
      {
        id: 'job-exposure-details',
        title: 'Job / Exposure Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'supervisorName', label: 'Supervisor / Lead', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company Working For', type: 'text' },
          { id: 'taskGeneratingDust', label: 'Task Generating Dust', type: 'text', required: true },
          { id: 'materialWorked', label: 'Material Being Cut / Drilled / Disturbed', type: 'text' },
          { id: 'crewMembers', label: 'Crew Members', type: 'participants' },
        ],
      },
      {
        id: 'exposure-controls',
        title: 'Exposure Controls',
        fields: [
          {
            id: 'controlsUsed',
            label: 'Controls Used',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'wet-methods', label: 'Wet methods' },
              { value: 'local-exhaust', label: 'Local exhaust' },
              { value: 'hepa-vacuum', label: 'HEPA vacuum' },
              { value: 'isolation', label: 'Isolation / restricted area' },
              { value: 'substitution', label: 'Substitution or lower-dust method' },
              { value: 'other', label: 'Other control' },
            ],
          },
          { id: 'controlsAvailable', label: 'Controls available before work started?', type: 'yes-no', required: true },
          { id: 'controlsUsedCorrectly', label: 'Controls used correctly during work?', type: 'yes-no', required: true },
          { id: 'coordinationPlanReviewed', label: 'Coordination plan reviewed with affected crews?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'respiratory-ppe-training',
        title: 'Respiratory / PPE / Training',
        fields: [
          { id: 'respiratorSelected', label: 'Respirator selected if required', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'fitTestCurrent', label: 'Fit test current if respirator is used', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'trainingComplete', label: 'Silica awareness / task training complete', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'ppeAvailable', label: 'Required PPE available', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'toolsInspected', label: 'Dust-control tools inspected', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'recordsMaintained', label: 'Training / inspection records maintained', type: 'pass-fail-na', options: passFailNaOptions },
        ],
      },
      {
        id: 'monitoring-followup',
        title: 'Monitoring / Follow-Up',
        fields: [
          { id: 'airSamplingRequired', label: 'Air sampling required?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'samplingNotes', label: 'Sampling / Exposure Notes', type: 'multiline' },
          { id: 'correctiveActions', label: 'Corrective Actions', type: 'multiline' },
          { id: 'supervisorSignature', label: 'Supervisor Signature', type: 'signature', required: true },
          { id: 'attachments', label: 'Photos / Supporting Documents', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'silica-exposure-control-plan-review',
    title: 'Silica Exposure Control Plan Review',
    categoryId: 'safety-programs',
    audience: ['hse'],
    description: 'Plan-level silica exposure review for ECP availability, control methods, sampling, respiratory records, and improvement tracking.',
    sourceMockupFamily: 'ZiaWellNav rewritten safety program reference',
    sections: [
      {
        id: 'plan-review-details',
        title: 'Plan Review Details',
        fields: [
          { id: 'reviewDate', label: 'Review Date', type: 'date', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'reviewerName', label: 'Reviewer Name', type: 'text', required: true },
          { id: 'projectName', label: 'Project / Job Name', type: 'text' },
          { id: 'contractorName', label: 'Contractor / Company', type: 'text' },
          { id: 'taskScope', label: 'Task Scope Reviewed', type: 'multiline', required: true },
        ],
      },
      {
        id: 'plan-elements',
        title: 'Plan Elements',
        fields: [
          { id: 'jobSpecificEcpAvailable', label: 'Job-specific exposure control plan available', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'controlMethodsDocumented', label: 'Control methods documented', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'respiratoryLevelDocumented', label: 'Respiratory protection level documented', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'coordinationPlanIncluded', label: 'Coordination plan included', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'trainingRecordsAvailable', label: 'Training records available', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'fitTestRecordsAvailable', label: 'Fit-test records available', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'inspectionRecordsAvailable', label: 'Inspection records available', type: 'pass-fail-na', options: passFailNaOptions },
        ],
      },
      {
        id: 'effectiveness-review',
        title: 'Effectiveness Review',
        fields: [
          { id: 'dustControlsEffective', label: 'Dust controls effective?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'nonstandardWorkPractices', label: 'Nonstandard work practices observed?', type: 'yes-no' },
          { id: 'samplingInitiated', label: 'Sampling initiated or reviewed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'improvementOpportunities', label: 'Improvement Opportunities', type: 'multiline' },
        ],
      },
      {
        id: 'approval',
        title: 'Approval',
        fields: [
          { id: 'reviewerSignature', label: 'Reviewer Signature', type: 'signature', required: true },
          { id: 'supervisorSignature', label: 'Supervisor Signature', type: 'signature' },
          { id: 'nextReviewDate', label: 'Next Review Date', type: 'date' },
          { id: 'attachments', label: 'Supporting Documents', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'ammonia-awareness-work-review',
    title: 'Ammonia Awareness Work Review',
    categoryId: 'safety-programs',
    audience: ['foreman', 'hse'],
    description: 'Work review for ammonia awareness, exposure hazards, PPE readiness, emergency response, SDS access, and crew acknowledgement.',
    sourceMockupFamily: 'ZiaWellNav rewritten safety program reference',
    sections: [
      {
        id: 'work-review-details',
        title: 'Work Review Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'supervisorName', label: 'Supervisor / Lead', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company Working For', type: 'text' },
          { id: 'taskDescription', label: 'Task Description', type: 'multiline', required: true },
          { id: 'ammoniaLocation', label: 'Known or Potential Ammonia Location', type: 'text' },
        ],
      },
      {
        id: 'hazard-awareness',
        title: 'Hazard Awareness',
        fields: [
          { id: 'ammoniaLocationsReviewed', label: 'Ammonia locations reviewed with affected personnel', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'propertiesReviewed', label: 'Odor, pressure, and physical properties reviewed', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'exposureHazardsReviewed', label: 'Toxic and corrosive exposure hazards reviewed', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'waterAttractionReviewed', label: 'Water attraction and pressure-release hazards reviewed', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'sdsAvailable', label: 'SDS available to crew', type: 'pass-fail-na', options: passFailNaOptions },
        ],
      },
      {
        id: 'controls-emergency-readiness',
        title: 'Controls / Emergency Readiness',
        fields: [
          { id: 'ppeSelected', label: 'Task-specific PPE selected', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'monitoringVentilationReviewed', label: 'Monitoring / ventilation reviewed', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'emergencyResponseReviewed', label: 'Emergency response reviewed', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'eyewashShowerAvailable', label: 'Eyewash / shower or decon method available', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'affectedEmployeesTrained', label: 'Affected employees trained or briefed', type: 'pass-fail-na', options: passFailNaOptions, required: true },
        ],
      },
      {
        id: 'signoff',
        title: 'Crew Acknowledgement / Sign-Off',
        fields: [
          { id: 'supervisorSignature', label: 'Supervisor Signature', type: 'signature', required: true },
          { id: 'employeeAcknowledgement', label: 'Employee Acknowledgement', type: 'participants' },
          { id: 'notes', label: 'Notes / Exceptions', type: 'multiline' },
          { id: 'attachments', label: 'Supporting Documents', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'power-pole-utility-pole-transportation-safety',
    title: 'Power Pole / Utility Pole Transportation Safety',
    categoryId: 'safety-programs',
    audience: ['crew', 'driver'],
    description: 'Transportation safety form for utility-pole loads, overhang, warning flags, lighting, permits, route hazards, and securement.',
    sourceMockupFamily: 'ZiaWellNav rewritten safety program reference',
    sections: [
      {
        id: 'trip-load-details',
        title: 'Trip / Load Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'driverName', label: 'Driver Name', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company Working For', type: 'text' },
          { id: 'vehicleUnit', label: 'Vehicle Unit', type: 'text', required: true },
          { id: 'trailerUnit', label: 'Trailer Unit', type: 'text' },
          { id: 'origin', label: 'Origin', type: 'text', required: true },
          { id: 'destination', label: 'Destination', type: 'text', required: true },
          {
            id: 'routeStates',
            label: 'Route States',
            type: 'checkbox-group',
            options: [
              { value: 'new-mexico', label: 'New Mexico' },
              { value: 'texas', label: 'Texas' },
              { value: 'other', label: 'Other' },
            ],
          },
          { id: 'poleLength', label: 'Pole Length', type: 'text' },
          { id: 'rearOverhang', label: 'Rear Overhang', type: 'text' },
        ],
      },
      {
        id: 'load-marking-legal',
        title: 'Load Marking / Legal',
        fields: [
          { id: 'overhangChecked', label: 'Overhang checked against route requirements', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'flagsInstalled', label: 'Red or orange warning flags installed', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'nightLightingInstalled', label: 'Night lamps / reflectors installed if required', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'permitsChecked', label: 'Permits checked if required', type: 'pass-fail-na', options: passFailNaOptions },
          { id: 'loadSecurementInspected', label: 'Load securement inspected before travel', type: 'pass-fail-na', options: passFailNaOptions, required: true },
        ],
      },
      {
        id: 'driving-controls',
        title: 'Driving Controls',
        fields: [
          { id: 'followingDistanceReviewed', label: 'Following distance reviewed', type: 'yes-no', required: true },
          { id: 'speedWeatherConsidered', label: 'Speed adjusted for load, weather, and route?', type: 'yes-no', required: true },
          { id: 'escortSpotterRequired', label: 'Escort / spotter required?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'routeHazards', label: 'Route Hazards / Restrictions', type: 'multiline' },
        ],
      },
      {
        id: 'driver-signoff',
        title: 'Driver Sign-Off',
        fields: [
          { id: 'driverSignature', label: 'Driver Signature', type: 'signature', required: true },
          { id: 'supervisorSignature', label: 'Supervisor Signature', type: 'signature' },
          { id: 'attachments', label: 'Photos / Supporting Documents', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'daily-field-report',
    title: 'Daily Field Report',
    categoryId: 'daily-field-reports',
    audience: ['crew', 'foreman', 'company_man', 'driver'],
    description: 'Daily field report for customer, job, crew, equipment, work performed, delays, safety notes, photos, and sign-off.',
    sourceMockupFamily: 'ZiaWellNav field safety form template',
    sections: [
      {
        id: 'general-details',
        title: 'General Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'customer', label: 'Customer / Company', type: 'text', required: true },
          { id: 'location', label: 'Location / Facility / Lease', type: 'text', required: true },
          { id: 'jobNumber', label: 'Job / Work Order #', type: 'text' },
          { id: 'foremanLead', label: 'Foreman / Lead', type: 'text', required: true },
          { id: 'reportPreparedBy', label: 'Report Prepared By', type: 'text', required: true },
          { id: 'customerRequirementsReviewed', label: 'Customer / operator requirements reviewed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'siteSpecificRulesReviewed', label: 'Site-specific rules reviewed?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'crew-equipment',
        title: 'Crew / Equipment',
        fields: [
          { id: 'crewOnSite', label: 'Crew On Site', type: 'participants', required: true },
          { id: 'subcontractors', label: 'Subcontractors / Visitors', type: 'participants' },
          { id: 'equipmentUsed', label: 'Equipment Used', type: 'multiline' },
          { id: 'materialsUsed', label: 'Materials / Parts Used', type: 'multiline' },
        ],
      },
      {
        id: 'work-performed',
        title: 'Work Performed',
        fields: [
          { id: 'workPerformed', label: 'Work Performed Today', type: 'multiline', required: true },
          { id: 'workCompleted', label: 'Work Completed / Milestones', type: 'multiline' },
          { id: 'workRemaining', label: 'Work Remaining / Next Steps', type: 'multiline' },
          { id: 'customerNotes', label: 'Customer / Site Notes', type: 'multiline' },
        ],
      },
      {
        id: 'delays-issues-safety',
        title: 'Delays / Issues / Safety',
        fields: [
          { id: 'delays', label: 'Delays / Downtime', type: 'multiline' },
          { id: 'issues', label: 'Issues / Needs Attention', type: 'multiline' },
          { id: 'safetyMeetingHeld', label: 'Safety meeting / tailgate held?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'safetyTopic', label: 'Safety Topic / JSA Topic Discussed', type: 'text' },
          { id: 'hazardsObserved', label: 'Hazards Observed / Controls Used', type: 'multiline' },
          { id: 'environmentalControlsReviewed', label: 'Environmental / resource controls reviewed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'specialSiteConditions', label: 'Special Site Conditions', type: 'multiline' },
          { id: 'weatherConditions', label: 'Weather / Site Conditions', type: 'text' },
        ],
      },
      {
        id: 'photos-signoff',
        title: 'Photos & Sign-Off',
        fields: [
          { id: 'attachments', label: 'Photos / Documents', type: 'attachment' },
          { id: 'completedBy', label: 'Completed By', type: 'text', required: true },
          { id: 'completedBySignature', label: 'Completed By Signature', type: 'signature', required: true },
          { id: 'supervisorSignature', label: 'Supervisor / Foreman Signature', type: 'signature' },
        ],
      },
    ],
  },
  {
    id: 'crew-sign-off',
    title: 'Crew Sign-Off',
    categoryId: 'crew-sign-off',
    audience: ['foreman'],
    description: 'Crew acknowledgement and sign-off for job task, site controls, participant signatures, and foreman approval.',
    sourceMockupFamily: 'ZiaWellNav field safety form template',
    sections: [
      {
        id: 'job-task-details',
        title: 'Job / Task Details',
        fields: [
          { id: 'dateTime', label: 'Date & Time', type: 'datetime', required: true },
          { id: 'location', label: 'Site / Location', type: 'text', required: true },
          { id: 'customer', label: 'Customer / Company', type: 'text' },
          { id: 'jobTask', label: 'Job / Task / Topic', type: 'text', required: true },
          { id: 'foremanLead', label: 'Foreman / Lead', type: 'text', required: true },
        ],
      },
      {
        id: 'acknowledgement',
        title: 'Acknowledgement',
        fields: [
          {
            id: 'acknowledgementItems',
            label: 'Crew Acknowledges',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'understand-task', label: 'Understands the task and work scope' },
              { value: 'reviewed-hazards', label: 'Reviewed hazards and controls' },
              { value: 'ppe-ready', label: 'Has required PPE and tools' },
              { value: 'stop-work', label: 'Understands stop-work authority' },
              { value: 'questions-answered', label: 'Questions were answered before work started' },
            ],
          },
          { id: 'acknowledgementText', label: 'Acknowledgement / Notes', type: 'multiline' },
          { id: 'fitForDuty', label: 'Crew fit for duty?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'questionsResolved', label: 'Questions / concerns resolved?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'crew-members',
        title: 'Crew Members',
        fields: [
          { id: 'participants', label: 'Crew Members and Signatures', type: 'participants', required: true },
        ],
      },
      {
        id: 'foreman-supervisor-signoff',
        title: 'Foreman / Supervisor Sign-Off',
        fields: [
          { id: 'foremanSignature', label: 'Foreman / Supervisor Signature', type: 'signature', required: true },
          { id: 'signoffDate', label: 'Sign-Off Date', type: 'date', required: true },
          { id: 'notes', label: 'Final Notes', type: 'multiline' },
        ],
      },
    ],
  },
  {
    id: 'pre-lift-checklist',
    title: 'Pre-Lift Checklist',
    categoryId: 'lifting-rigging',
    audience: ['crew', 'foreman'],
    description: 'Pre-lift meeting checklist for crane and equipment lifts — pre-lift, personnel, load handling equipment, and rigging checks with multi-role sign-off (SAF-STD-0019-FOR-01).',
    sourceMockupFamily: 'Critical/Standard Lift Plan and Pre-Lift Checklist',
    sections: [
      {
        id: 'lift-details',
        title: 'Lift Details',
        fields: [
          { id: 'locationOfLift', label: 'Location of Lift', type: 'text', required: true },
          { id: 'payloadName', label: 'Payload Name', type: 'text', required: true },
          { id: 'liftDate', label: 'Date of Lift', type: 'date', required: true },
        ],
      },
      {
        id: 'pre-lift-check',
        title: 'Pre-Lift Check',
        description: 'Complete and sign on the day of the lift.',
        fields: [
          { id: 'payloadWeightCgVerified', label: 'Payload weight / CG verified?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'liftCorrectlyCategorized', label: 'Lift correctly categorized?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'planInPlace', label: 'Plan in place?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'fieldConditionsMatchPlan', label: 'Field conditions match plan?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'requiredApprovalsPermits', label: 'Required approvals / permits?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'weatherWindAcceptable', label: 'Weather / wind acceptable?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'powerLinesUndergrounds', label: 'Power lines / undergrounds identified?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'siteControlBarricaded', label: 'Site control / area barricaded?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'contingencyPlanNeeded', label: 'Contingency plan needed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'emergencyPlanNeeded', label: 'Emergency plan needed?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'personnel-check',
        title: 'Personnel Check',
        fields: [
          { id: 'liftDirectorInPlace', label: 'Lift director in place?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'qualifiedRiggerInPlace', label: 'Qualified rigger in place?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'qualifiedSignalPerson', label: 'Qualified signal person(s)?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'communicationHandRadio', label: 'Communication (hand / radio)?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'operatorCertifiedQualified', label: 'Operator certified / qualified?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'nonessentialPersonsOut', label: 'Nonessential persons out?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'riggerRolesIdentified', label: 'Rigger roles identified?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'otherRolesIdentified', label: 'Other roles identified?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'qualifiedPersonPresent', label: 'Qualified person present?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'preLiftMeetingHeld', label: 'Pre-lift meeting held?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
        ],
      },
      {
        id: 'load-handling-equipment-check',
        title: 'Load Handling Equipment Check',
        fields: [
          { id: 'annualInspection', label: 'Annual inspection?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'preOperationalCheck', label: 'Pre-operational check?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'adequatelySupported', label: 'Adequately supported?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'matsWhereRequired', label: 'Mats where required?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'setupCheckedLevel', label: 'Setup checked / level?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'adequatePartsOfLine', label: 'Adequate parts of line?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'obstructionsClearance', label: 'Obstructions / clearance?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'configurationsCorrect', label: 'Configuration(s) correct?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'controlsFunctionsOk', label: 'Controls / functions OK?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'servicesAncillaryEquipment', label: 'Services / ancillary equipment?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'rigging-check',
        title: 'Rigging Check',
        fields: [
          { id: 'correctRigging', label: 'Correct rigging?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'riggingInspectionCurrent', label: 'Rigging inspection current?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'inspectedBeforeUse', label: 'Inspected before use?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'riggingCorrectlyAssembled', label: 'Rigging correctly assembled?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'softenersUsedAsNeeded', label: 'Softeners used as needed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'riggingProperlyTagged', label: 'Rigging properly tagged?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'slingAnglesAcceptable', label: 'Sling angles acceptable?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'slingTensionAcceptable', label: 'Sling tension acceptable?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'lengthOfRiggingAcceptable', label: 'Length of rigging acceptable?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'obstructionClearanceRigging', label: 'Obstruction / clearance?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'ddRatioAcceptable', label: 'D/d ratio acceptable?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'liftPointsIdentified', label: 'Lift points identified?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'liftPointsVisuallyInspected', label: 'Lift points visually inspected?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'tagLines', label: 'Tag lines?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'heightOfRiggingAcceptable', label: 'Height of rigging acceptable?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'riggingNotes', label: 'Notes', type: 'multiline' },
        ],
      },
      {
        id: 'lift-sign-off',
        title: 'Sign-Off',
        description: 'Signed immediately prior to the lift. The lift director / rigger / signal person can be the same individual if qualified for all duties.',
        fields: [
          { id: 'liftDirectorName', label: 'Lift Director Name', type: 'text', required: true },
          { id: 'liftDirectorSignature', label: 'Lift Director Signature', type: 'signature', required: true },
          { id: 'craneOperatorName', label: 'Crane Operator Name', type: 'text' },
          { id: 'craneOperatorSignature', label: 'Crane Operator Signature', type: 'signature' },
          { id: 'riggerName', label: 'Rigger Name', type: 'text' },
          { id: 'riggerSignature', label: 'Rigger Signature', type: 'signature' },
          { id: 'signalPersonName', label: 'Signal Person Name', type: 'text' },
          { id: 'signalPersonSignature', label: 'Signal Person Signature', type: 'signature' },
          { id: 'supervisorSignature', label: 'Supervisor / Designee Signature', type: 'signature' },
          { id: 'safetySignature', label: 'Safety / Designee Signature', type: 'signature' },
        ],
      },
    ],
  },
  {
    id: 'standard-lift-plan-equipment',
    title: 'Standard Lift Plan (Equipment)',
    categoryId: 'lifting-rigging',
    audience: ['foreman'],
    description: 'Gathering & processing standard lift plan for non-crane and multi-equipment lifts — lifting equipment capacities, payload verification, and setup checks (SAF-STD-0019-FOR-03).',
    sourceMockupFamily: 'Standard Lift Plan (Equipment) and Pre-Lift Checklist',
    sections: [
      {
        id: 'lift-details',
        title: 'Lift Details',
        fields: [
          { id: 'locationOfLift', label: 'Location of Lift', type: 'text', required: true },
          { id: 'payloadName', label: 'Payload Name', type: 'text', required: true },
          { id: 'liftDate', label: 'Date of Lift', type: 'date', required: true },
        ],
      },
      {
        id: 'lift-type',
        title: 'Type of Standard Lift',
        description: 'Check all that apply.',
        fields: [
          {
            id: 'standardLiftType',
            label: 'Type of standard lift',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'exceeds-80-capacity', label: 'Exceeds 80% of capacity (non-crane equipment)' },
              { value: 'multi-equipment-lift', label: 'Multi-equipment lift' },
              { value: 'multi-equipment-winching', label: 'Multi-equipment winching operation' },
              { value: 'over-in-service-lines', label: 'Over in-service lines' },
            ],
          },
          { id: 'equipmentInvolvedType', label: 'Equipment involved in lift (list all)', type: 'multiline', helperText: 'List all equipment involved in the lift.' },
        ],
      },
      {
        id: 'lifting-equipment',
        title: 'Lifting Equipment',
        fields: [
          { id: 'liftingEquipmentList', label: 'Lifting equipment', type: 'multiline', required: true, helperText: 'For each: make & model | anticipated reach (ft) | max lifting capacity at reach (lbs).' },
          { id: 'totalLiftCapacity', label: 'Total lift capacity of ALL equipment (lbs)', type: 'number', required: true },
          { id: 'totalPayloadWeight', label: 'Total payload weight (lbs)', type: 'number', required: true },
          { id: 'capacityExceedsPayload', label: 'Total lift capacity > total payload weight?', type: 'yes-no', options: yesNoOptions, required: true, helperText: 'Verified by the lift director.' },
        ],
      },
      {
        id: 'pre-lift-check',
        title: 'Pre-Lift Check',
        fields: [
          { id: 'payloadWeightCgVerified', label: 'Payload weight / CG verified?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'liftCorrectlyCategorized', label: 'Lift correctly categorized?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'planInPlace', label: 'Plan in place?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'fieldConditionsMatchPlan', label: 'Field conditions match plan?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'requiredApprovalsPermits', label: 'Required approvals / permits?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'weatherWindAcceptable', label: 'Weather / wind acceptable?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'powerLinesUndergrounds', label: 'Power lines / undergrounds identified?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'siteControlBarricaded', label: 'Site control / area barricaded?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'contingencyPlanNeeded', label: 'Contingency plan needed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'emergencyPlanNeeded', label: 'Emergency plan needed?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'personnel-check',
        title: 'Personnel Check',
        fields: [
          { id: 'liftDirectorInPlace', label: 'Lift director in place?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'qualifiedRiggerInPlace', label: 'Qualified rigger in place?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'qualifiedSignalPerson', label: 'Qualified signal person(s)?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'communicationHandRadio', label: 'Communication (hand / radio)?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'operatorCertifiedQualified', label: 'Operator certified / qualified?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'nonessentialPersonsOut', label: 'Nonessential persons out?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'riggerRolesIdentified', label: 'Rigger roles identified?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'otherRolesIdentified', label: 'Other roles identified?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'qualifiedPersonPresent', label: 'Qualified person present?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'preLiftMeetingHeld', label: 'Pre-lift meeting held?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
        ],
      },
      {
        id: 'lift-director-setup-check',
        title: 'Lift Director Setup Check',
        fields: [
          { id: 'preOperationalCheck', label: 'Pre-operational check?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'adequatelySupported', label: 'Adequately supported?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'matsWhereRequired', label: 'Mats where required?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'setupCheckedLevel', label: 'Setup checked / level?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'obstructionsClearance', label: 'Obstructions / clearance?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'configurationsCorrect', label: 'Configurations correct?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'controlsFunctionsOk', label: 'Controls / functions OK?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'servicesAncillaryEquipment', label: 'Services / ancillary equipment?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'liftEquipmentProperlyDistributed', label: 'Lift equipment properly distributed?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'rigging-check',
        title: 'Rigging Check',
        fields: [
          { id: 'correctRigging', label: 'Correct rigging?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'riggingInspectionCurrent', label: 'Rigging inspection current?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'inspectedBeforeUse', label: 'Inspected before use?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'riggingCorrectlyAssembled', label: 'Rigging correctly assembled?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'softenersUsedAsNeeded', label: 'Softeners used as needed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'riggingProperlyTagged', label: 'Rigging properly tagged?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'slingAnglesAcceptable', label: 'Sling angles acceptable?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'slingTensionAcceptable', label: 'Sling tension acceptable?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'lengthOfRiggingAcceptable', label: 'Length of rigging acceptable?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'obstructionClearanceRigging', label: 'Obstruction / clearance?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'ddRatioAcceptable', label: 'D/d ratio acceptable?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'liftPointsIdentified', label: 'Lift points identified?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'liftPointsVisuallyInspected', label: 'Lift points visually inspected?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'tagLines', label: 'Tag lines?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'riggingNotes', label: 'Notes', type: 'multiline' },
        ],
      },
      {
        id: 'lift-plan-approval',
        title: 'Approval',
        fields: [
          { id: 'liftDirectorName', label: 'Lift Director Name', type: 'text', required: true },
          { id: 'liftDirectorSignature', label: 'Lift Director Signature', type: 'signature', required: true },
          { id: 'approvalDate', label: 'Date', type: 'date', required: true },
        ],
      },
    ],
  },
  {
    id: 'excavation-trenching-entry-checklist',
    title: 'Excavation / Trenching Entry Checklist',
    categoryId: 'excavation',
    audience: ['crew', 'foreman'],
    description: 'Competent-person trenching entry checklist — soil classification, protective system, and OSHA 1926 Subpart P hazard checks before entry.',
    sourceMockupFamily: 'Excavation Trenching Entry Checklist',
    sections: [
      {
        id: 'site-details',
        title: 'Site Details',
        fields: [
          { id: 'employerName', label: 'Employer Name', type: 'text', required: true },
          { id: 'siteAddress', label: 'Site Address', type: 'text' },
          { id: 'cityZip', label: 'City / Zip', type: 'text' },
          { id: 'entryDate', label: 'Date', type: 'date', required: true },
          { id: 'entryTime', label: 'Time', type: 'text' },
          { id: 'competentPerson', label: 'Competent Person / Foreman Name', type: 'text', required: true },
          { id: 'projectDescription', label: 'Project / Site Description & Comments', type: 'multiline' },
        ],
      },
      {
        id: 'soil-excavation',
        title: 'Soil & Excavation',
        fields: [
          {
            id: 'soilClassification',
            label: 'Soil Classification',
            type: 'select',
            required: true,
            options: [
              { value: 'a', label: 'Type A' },
              { value: 'b', label: 'Type B' },
              { value: 'c', label: 'Type C' },
            ],
          },
          {
            id: 'manualTestsPerformed',
            label: 'Manual tests performed',
            type: 'checkbox-group',
            options: [
              { value: 'plasticity', label: 'Plasticity' },
              { value: 'dry-strength', label: 'Dry strength test' },
              { value: 'thumb-penetration', label: 'Thumb penetration test' },
              { value: 'wet-thread', label: 'Plasticity / wet thread test' },
            ],
          },
          { id: 'excavationWidthTop', label: 'Excavation width — top (ft)', type: 'text' },
          { id: 'excavationWidthBottom', label: 'Excavation width — bottom (ft)', type: 'text' },
          { id: 'excavationDepth', label: 'Excavation depth (ft)', type: 'text', required: true },
          { id: 'slopeOrAngle', label: 'Slope or angle', type: 'text' },
          { id: 'excavationLength', label: 'Excavation length (ft)', type: 'text' },
          {
            id: 'protectiveSystem',
            label: 'Protective system in place',
            type: 'select',
            required: true,
            options: [
              { value: 'trench-box', label: 'Trench box' },
              { value: 'shoring', label: 'Shoring' },
              { value: 'sloping-benching', label: 'Sloping / benching' },
              { value: 'rpe-design', label: 'RPE design' },
            ],
          },
        ],
      },
      {
        id: 'entry-safety-checks',
        title: 'Entry Safety Checks',
        description: 'Per OSHA 1926 Subpart P.',
        fields: [
          { id: 'utilityLocationsRequested', label: 'Were utility locations requested?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'utilityLocationsVerified', label: 'Were utility locations verified?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'exposedUtilitiesSupported', label: 'Are exposed utilities supported / protected?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'safeAccessEgress', label: 'Is there safe access / egress?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'employeesWithin25ftEgress', label: 'Are employees within 25 ft of egress?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'excavationConfinedSpace', label: 'Is excavation a confined space?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'atmosphericTestingDone', label: 'Atmospheric testing done?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'exposedToVehicularTraffic', label: 'Is excavation exposed to vehicular traffic?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'employeesProtectedFromTraffic', label: 'Are employees protected from vehicular traffic?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'spoilPilesSetback', label: 'Are spoil piles set back?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'soilPreviouslyDisturbed', label: 'Is soil previously disturbed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'excavationFaceCracked', label: 'Is excavation face cracked / fissuring / spalling?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'soilLayered', label: 'Is soil layered?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'excavationSubjectToVibration', label: 'Is excavation subject to vibration?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'waterInExcavation', label: 'Is there water in the excavation?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'dewateringEquipmentUsed', label: 'Is de-watering equipment being used?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'equipmentSetbackSupported', label: 'Is equipment set back, blocked, or supported?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'protectedFromLooseFallingRocks', label: 'Are employees protected from loose / falling rocks?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'protectedFromSuspendedLoads', label: 'Are employees protected from suspended loads?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'surfaceEncumbrancesProtected', label: 'Are employees protected from surface encumbrances?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'adjacentStructuresAffected', label: 'Are adjacent structures affected?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'protectedFromCaveIns', label: 'Are employees protected from cave-ins?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'allPredictableHazardsControlled', label: 'Are all reasonably predictable hazards controlled?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'entry-sign-off',
        title: 'Sign-Off',
        fields: [
          { id: 'competentPersonSignature', label: 'On-Site Competent Person / Foreman Signature', type: 'signature', required: true },
          { id: 'signoffDate', label: 'Date', type: 'date', required: true },
          { id: 'attachments', label: 'Photos / Supporting Documents', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'excavation-approval-form',
    title: 'Excavation Approval Form',
    categoryId: 'excavation',
    audience: ['foreman', 'hse', 'company_man'],
    description: 'Excavation permit approval — attaches to the safe work permit, identifies underground encumbrances, and manages activity preparation, one-call, and sign-offs to begin and close work.',
    sourceMockupFamily: 'Excavation Approval Form',
    sections: [
      {
        id: 'permit-details',
        title: 'Permit Details',
        fields: [
          { id: 'permitBegins', label: 'Permit begins', type: 'datetime', required: true },
          { id: 'permitEnds', label: 'Permit ends', type: 'datetime' },
          { id: 'contractorCompany', label: 'Name of contractor company or employee', type: 'text', required: true },
          { id: 'employeesOnSite', label: '# of employees on site', type: 'number' },
          { id: 'excavationType', label: 'Type of excavation to be performed', type: 'text' },
          { id: 'locationArea', label: 'Location / area', type: 'text', required: true },
          { id: 'neededEquipmentTools', label: 'Needed equipment & tools', type: 'multiline' },
          { id: 'scopeOfWork', label: 'Scope of work / special instructions', type: 'multiline' },
          { id: 'amendsSafeWorkPermit', label: 'Amends safe work permit number', type: 'text' },
        ],
      },
      {
        id: 'confined-space-trigger',
        title: 'Confined Space Trigger',
        description: 'Yes to either question indicates the excavation is a confined space — confined space permitting requirements must be followed.',
        fields: [
          { id: 'trenchBoxFourSides', label: 'Does the trench box have 4 sides?', type: 'yes-no', options: yesNoOptions },
          { id: 'containsContaminatedSoil', label: 'Does the excavation contain, or did it contain, contaminated soil?', type: 'yes-no', options: yesNoOptions },
        ],
      },
      {
        id: 'activity-preparation',
        title: 'Excavation Activity Preparation',
        fields: [
          { id: 'drawingsReviewed', label: 'Drawings reviewed', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'previousExcavationRecords', label: 'Previous excavation records', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'processKnowledgeInterviews', label: 'Process knowledge / operations interviews', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'undergroundDrawings', label: 'Underground drawings', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'undergroundElectricalChecked', label: 'Underground electrical checked (hand dig within 3 feet)', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'oneCallDigAlertCompleted', label: '811 One-Call / Dig Alert completed', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'oneCallConfirmationNumber', label: 'One-call confirmation #', type: 'text' },
          { id: 'adjacentStructuresSecured', label: 'Adjacent structures secured', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'waterAccumulationControlled', label: 'Water accumulation controlled', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'materialsTwoFeetFromEdge', label: 'All materials > 2 feet from edge', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'signsBarricadesPosted', label: 'Signs / barricades posted', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'electromagneticMethod', label: 'Electromagnetic (EM / radio detection) method used', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'groundPenetratingRadar', label: 'Ground penetrating radar (GPR) used', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'potHoling', label: 'Pot holing performed', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'undergroundPipingIdentified', label: 'Identified underground piping', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'properOrganizationsNotified', label: 'Proper organizations notified (local utilities)', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'shoringMethodsDetermined', label: 'Shoring methods determined', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'soilClassificationCompleted', label: 'Soil classification completed', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'trafficConcernsAddressed', label: 'Traffic concerns addressed / detours marked', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'accessEgressEstablished', label: 'Proper means of access / egress established', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'previous-excavations',
        title: 'Previous Excavations',
        fields: [
          { id: 'previousExcavationsInArea', label: 'Have previous excavations been done in this area?', type: 'yes-no', options: yesNoOptions },
          { id: 'previousActivityDescription', label: 'If yes, describe the previous activity', type: 'multiline' },
        ],
      },
      {
        id: 'approval-to-begin',
        title: 'Approval to Begin Work',
        description: 'Signature confirms the job site has been inspected and this permit has been discussed with the recipient.',
        fields: [
          { id: 'responsiblePersonSignature', label: 'Responsible Person Signature', type: 'signature', required: true },
          { id: 'competentPersonSignature', label: 'Competent Person Signature', type: 'signature', required: true },
          { id: 'operatorSignature', label: 'Operator Representative Signature', type: 'signature', required: true },
        ],
      },
      {
        id: 'close-permit',
        title: 'Close Permit / End Work',
        fields: [
          {
            id: 'closeStatus',
            label: 'Close status',
            type: 'checkbox-group',
            options: [
              { value: 'work-complete', label: 'Work is complete' },
              { value: 'reissued-permit', label: 'Work will continue under new / reissued permit (debriefing required)' },
            ],
          },
          { id: 'closeProblems', label: 'Describe any problems', type: 'multiline' },
          { id: 'permitIssuerSignature', label: 'Permit Issuer Signature', type: 'signature' },
          { id: 'actualPermitEnd', label: 'Actual permit end', type: 'datetime' },
        ],
      },
    ],
  },
  {
    id: 'lockout-tagout',
    title: 'Lockout / Tagout (LOTO)',
    categoryId: 'energy-control',
    audience: ['crew', 'foreman'],
    description: 'Energy-control lockout/tagout form per OSHA 1910.147 — energy sources, isolation points, lock/tag records, zero-energy verification, and restoration.',
    sourceMockupFamily: 'Energy control (lockout/tagout) procedure',
    sections: [
      {
        id: 'equipment-authorization',
        title: 'Equipment & Authorization',
        fields: [
          { id: 'lotoDate', label: 'Date', type: 'date', required: true },
          { id: 'facilityLocation', label: 'Facility / Location', type: 'text', required: true },
          { id: 'equipmentLockedOut', label: 'Equipment / machine to be locked out', type: 'text', required: true },
          { id: 'descriptionOfWork', label: 'Description of work', type: 'multiline', required: true },
          { id: 'authorizedEmployee', label: 'Authorized employee (applying LOTO)', type: 'text', required: true },
          { id: 'shiftTime', label: 'Shift / time', type: 'text' },
        ],
      },
      {
        id: 'energy-sources',
        title: 'Energy Sources',
        fields: [
          {
            id: 'energySourcesPresent',
            label: 'Energy sources present',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'electrical', label: 'Electrical' },
              { value: 'hydraulic', label: 'Hydraulic' },
              { value: 'pneumatic', label: 'Pneumatic' },
              { value: 'mechanical', label: 'Mechanical' },
              { value: 'chemical', label: 'Chemical' },
              { value: 'thermal', label: 'Thermal' },
              { value: 'gravity-stored', label: 'Gravity / stored energy' },
              { value: 'water-steam', label: 'Water / steam' },
            ],
          },
          { id: 'isolationPoints', label: 'Energy magnitudes & isolation points', type: 'multiline', required: true, helperText: 'For each source: magnitude | isolation device / point | lock & tag #.' },
        ],
      },
      {
        id: 'lockout-steps',
        title: 'Lockout Sequence',
        description: 'Per OSHA 1910.147 lockout sequence.',
        fields: [
          { id: 'affectedEmployeesNotified', label: 'Affected employees notified?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'equipmentShutDownNormal', label: 'Equipment shut down using normal stopping procedure?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'energyIsolatingDevicesOperated', label: 'All energy-isolating devices operated to isolate equipment?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'locksTagsApplied', label: 'Locks and tags applied by each authorized employee?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'storedEnergyRelieved', label: 'Stored / residual energy relieved, blocked, or bled down?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'zeroEnergyVerified', label: 'Zero-energy state verified (try-to-start / test with instrument)?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
        ],
      },
      {
        id: 'locks-employees',
        title: 'Locks & Employees',
        fields: [
          { id: 'numberOfLocksApplied', label: 'Number of locks applied', type: 'number' },
          { id: 'lockTagNumbers', label: 'Lock / tag numbers', type: 'multiline' },
          { id: 'authorizedEmployees', label: 'Authorized employees', type: 'participants' },
          { id: 'affectedEmployeesNotifiedList', label: 'Affected employees notified', type: 'participants' },
        ],
      },
      {
        id: 'restoration',
        title: 'Restoration to Service',
        fields: [
          { id: 'workAreaCheckedToolsRemoved', label: 'Work area checked, tools removed?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'employeesClear', label: 'Employees safely positioned / clear?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'locksTagsRemovedByApplier', label: 'Locks / tags removed by the employee who applied them?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'affectedEmployeesNotifiedRestore', label: 'Affected employees notified before re-energizing?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'equipmentSafeToReenergize', label: 'Equipment verified safe to re-energize?', type: 'yes-no-na', options: yesNoNaOptions },
        ],
      },
      {
        id: 'loto-sign-off',
        title: 'Sign-Off',
        fields: [
          { id: 'authorizedEmployeeSignature', label: 'Authorized Employee Signature', type: 'signature', required: true },
          { id: 'supervisorSignature', label: 'Supervisor Signature', type: 'signature' },
          { id: 'signoffDate', label: 'Date', type: 'date', required: true },
        ],
      },
    ],
  },

  // ── Tier 1 field ops permits ──────────────────────────────────────────
  {
    id: 'confined-space-entry-permit',
    title: 'Confined Space Entry Permit',
    categoryId: 'confined-space',
    audience: ['foreman', 'hse'],
    description: 'Permit-required confined space entry — space ID, atmospheric testing, isolation, attendant, entrants, rescue, and close-out per OSHA 1910.146.',
    sourceMockupFamily: 'Confined space entry permit (field oil & gas)',
    sections: [
      {
        id: 'permit-header',
        title: 'Permit Header',
        fields: [
          { id: 'dateTime', label: 'Permit date / time issued', type: 'datetime', required: true },
          { id: 'permitExpires', label: 'Permit expires', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'facilityName', label: 'Location / facility / equipment', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company working for', type: 'text', required: true },
          { id: 'crewCompany', label: 'Entry contractor / crew company', type: 'text', },
          { id: 'permitNumber', label: 'Permit / work order #', type: 'text', },
          {
            id: 'relatedForms',
            label: 'Linked forms (H₂S log / LOTO / JSA #)',
            type: 'linked-forms',
            placeholder: 'H₂S gas log · LOTO # · JSA #',
            helperText: 'Tap a linked form to open it now, or type reference numbers.',
            linkedTemplateIds: [
              'h2s-atmospheric-gas-testing-log',
              'lockout-tagout',
              'oxy-lock-out-tag-out',
              'oxy-job-safety-plan-checklist',
            ],
          },
          { id: 'entryPurpose', label: 'Purpose of entry', type: 'multiline', required: true, placeholder: 'Inspect, clean, repair, sample…' },
        ],
      },
      {
        id: 'space-identification',
        title: 'Space Identification',
        fields: [
          { id: 'spaceDescription', label: 'Confined space description', type: 'multiline', required: true, helperText: 'Tank, vessel, pit, vault, cellar, trench box, etc.' },
          {
            id: 'spaceType',
            label: 'Space type',
            type: 'checkbox-group',
            options: [
              { value: 'tank-vessel', label: 'Tank / vessel' },
              { value: 'pit-sump', label: 'Pit / sump / cellar' },
              { value: 'vault-manhole', label: 'Vault / manhole' },
              { value: 'trench-excavation', label: 'Trench / excavation' },
              { value: 'pipeline-pig', label: 'Pipeline / pig trap' },
              { value: 'other', label: 'Other (describe above)' },
            ],
          },
          { id: 'permitRequiredSpace', label: 'Is this a permit-required confined space?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'knownHazards', label: 'Known / potential hazards', type: 'multiline', required: true, placeholder: 'Atmospheric, engulfment, configuration, energy, chemical, H2S…' },
        ],
      },
      {
        id: 'isolation-controls',
        title: 'Isolation & Controls',
        fields: [
          { id: 'energyIsolatedLoto', label: 'All energy sources isolated / LOTO complete?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'linesBlankedBlinded', label: 'Lines blanked / blinded / disconnected?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'purgedVentilated', label: 'Space purged / ventilated as required?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'ventilationType', label: 'Ventilation type / equipment', type: 'text', placeholder: 'Forced air blower, eductor, natural…' },
          { id: 'lockTagNumbers', label: 'Lock / tag / blind numbers', type: 'multiline', },
          { id: 'isolationPoints', label: 'Isolation points summary', type: 'multiline', },
          { id: 'barricadesSignsPosted', label: 'Barricades / signs posted at entry?', type: 'yes-no-na', options: yesNoNaOptions, },
        ],
      },
      {
        id: 'atmospheric-testing',
        title: 'Atmospheric Testing',
        description: 'Test outside → opening → mid → bottom before entry. Use H₂S / Atmospheric Gas Testing Log for continuous/interval readings during occupation.',
        fields: [
          { id: 'monitorMakeModel', label: 'Gas monitor make / model / serial', type: 'text', required: true },
          { id: 'monitorCalibrated', label: 'Monitor calibrated / bump-tested today?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'preEntryTestTime', label: 'Pre-entry test time', type: 'datetime', required: true },
          { id: 'readingLocations', label: 'Sample locations tested (pre-entry)', type: 'multiline', placeholder: 'Outside / opening / middle / bottom', helperText: 'Document each sample point before entry.' },
          { id: 'o2Percent', label: 'O₂ % (pre-entry)', type: 'text', required: true, placeholder: 'e.g. 20.9' },
          { id: 'lelPercent', label: 'LEL % (pre-entry)', type: 'text', required: true, placeholder: 'e.g. 0' },
          { id: 'h2sPpm', label: 'H₂S ppm (pre-entry)', type: 'text', required: true, placeholder: 'e.g. 0' },
          { id: 'coPpm', label: 'CO ppm (pre-entry)', type: 'text', required: true, placeholder: 'e.g. 0' },
          {
            id: 'gasReadings',
            label: 'Interval / continuous gas readings table',
            type: 'gas-readings-log',
            helperText: 'Add rows during occupation — time, location, O₂ / LEL / H₂S / CO, notes.',
          },
          { id: 'otherGasReadings', label: 'Other gas / VOC readings', type: 'multiline' },
          { id: 'atmosphereAcceptable', label: 'Atmosphere acceptable for entry?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'continuousMonitoring', label: 'Continuous monitoring while occupied?', type: 'yes-no-na', options: yesNoNaOptions, },
          { id: 'retestSchedule', label: 'Retest schedule / interval', type: 'text', placeholder: 'Every 15 min / continuous / after break' },
        ],
      },
      {
        id: 'entry-team',
        title: 'Entry Team',
        fields: [
          { id: 'entrySupervisor', label: 'Entry supervisor', type: 'text', required: true },
          { id: 'attendantName', label: 'Attendant (hole watch)', type: 'text', required: true },
          { id: 'entrants', label: 'Authorized entrants', type: 'participants', required: true },
          { id: 'entrantTrainingVerified', label: 'Entrant / attendant training verified current?', type: 'yes-no', options: yesNoOptions, },
          { id: 'communicationMethod', label: 'Communication method (attendant ↔ entrants)', type: 'text', placeholder: 'Radio channel, voice, hand signals…' },
          { id: 'requiredPpe', label: 'Required PPE for this entry', type: 'multiline', },
        ],
      },
      {
        id: 'rescue-emergency',
        title: 'Rescue & Emergency',
        fields: [
          {
            id: 'rescueType',
            label: 'Rescue type',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'non-entry', label: 'Non-entry rescue (tripod / winch / retrieval)' },
              { value: 'entry-rescue-team', label: 'Entry rescue team on standby' },
              { value: 'external-emergency-services', label: 'External emergency services pre-notified' },
            ], helperText: 'Acceptable typically ~19.5–23.5% unless procedure differs.',
          },
          { id: 'rescueEquipmentOnSite', label: 'Rescue / retrieval equipment on site and inspected?', type: 'yes-no', options: yesNoOptions, },
          { id: 'rescueEquipmentList', label: 'Rescue equipment list', type: 'multiline', placeholder: 'Tripod, SRL, harnesses, SCBA…' },
          { id: 'emergencyContact', label: 'Emergency contact / dispatch', type: 'text', required: true, },
          { id: 'emergencyNumber', label: 'Emergency phone / radio channel', type: 'text', },
          { id: 'rescuePlanDescription', label: 'Rescue plan summary', type: 'multiline', },
          { id: 'hospitalClinic', label: 'Nearest medical facility', type: 'text', },
        ],
      },
      {
        id: 'authorization-entry',
        title: 'Authorization to Enter',
        fields: [
          { id: 'preEntryBriefingComplete', label: 'Pre-entry briefing complete with all roles?', type: 'yes-no', required: true, options: yesNoOptions, },
          { id: 'entrySupervisorSignature', label: 'Entry Supervisor Signature', type: 'signature', required: true },
          { id: 'attendantSignature', label: 'Attendant Signature', type: 'signature', required: true, },
          { id: 'entrantLeadSignature', label: 'Entrant Lead Signature', type: 'signature', required: true, },
          { id: 'operatorSignature', label: 'Operator / company man signature', type: 'signature' },
        ],
      },
      {
        id: 'entry-log-closeout',
        title: 'Entry Log & Close-Out',
        description: 'Log every entry/exit. Attach gas-log photos if continuous monitoring used.',
        fields: [
          { id: 'actualEntryStart', label: 'Actual entry start', type: 'datetime', },
          { id: 'actualEntryEnd', label: 'Actual entry end / last exit', type: 'datetime' },
          { id: 'entryLogNotes', label: 'Entry / exit log notes', type: 'multiline', helperText: 'Who entered/exited and when; any atmospheric retests.' },
          { id: 'problemsDuringEntry', label: 'Problems / near misses during entry', type: 'multiline' },
          { id: 'spaceSecuredAfter', label: 'Space secured / returned to service safely?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'permitClosedBy', label: 'Permit closed by', type: 'signature', },
          { id: 'attachments', label: 'Gas logs / photos / supporting docs', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'ground-disturbance-one-call',
    title: 'Ground Disturbance / One-Call',
    categoryId: 'excavation',
    audience: ['foreman', 'company_man'],
    description: 'Ground disturbance authorization — 811 one-call tickets, line locates, hand-dig zones, daylighting, and approval before mechanical excavation.',
    sourceMockupFamily: 'Ground disturbance / one-call field permit',
    sections: [
      {
        id: 'job-header',
        title: 'Job Header',
        fields: [
          { id: 'dateTime', label: 'Date / time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'facilityName', label: 'Location / facility name', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company working for', type: 'text', required: true },
          { id: 'crewCompany', label: 'Contractor / excavating company', type: 'text', required: true },
          { id: 'competentPerson', label: 'Competent person', type: 'text', required: true },
          { id: 'workOrderOrJob', label: 'Work order / job #', type: 'text' },
          { id: 'scopeOfWork', label: 'Scope of ground disturbance', type: 'multiline', required: true },
        ],
      },
      {
        id: 'disturbance-details',
        title: 'Disturbance Details',
        fields: [
          {
            id: 'disturbanceMethod',
            label: 'Disturbance method(s)',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'hand-dig', label: 'Hand dig' },
              { value: 'hydrovac', label: 'Hydrovac / daylighting' },
              { value: 'mechanical-excavator', label: 'Mechanical excavator' },
              { value: 'trencher', label: 'Trencher' },
              { value: 'auger-drill', label: 'Auger / boring / HDD' },
              { value: 'post-hole', label: 'Post hole / fence' },
              { value: 'grading', label: 'Grading / scraping' },
              { value: 'other', label: 'Other (describe in scope)' },
            ],
          },
          { id: 'maxDepth', label: 'Maximum planned depth', type: 'text', required: true, placeholder: 'e.g. 4 ft' },
          { id: 'disturbanceAreaSize', label: 'Area / length of disturbance', type: 'text', placeholder: 'e.g. 20 ft trench, 10×10 pad' },
          { id: 'gpsOrLegalDescription', label: 'GPS / legal description / API if known', type: 'text' },
        ],
      },
      {
        id: 'one-call-locates',
        title: 'One-Call & Locates',
        description: '811 ticket must be valid for the work date. Do not dig if marks are missing or expired.',
        fields: [
          { id: 'oneCallDigAlertCompleted', label: '811 One-Call / Dig Alert completed?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'oneCallConfirmationNumber', label: 'One-call confirmation / ticket #', type: 'text', required: true },
          { id: 'oneCallCallDate', label: 'One-call request date', type: 'date', required: true },
          { id: 'oneCallValidThrough', label: 'Ticket valid through', type: 'date', required: true },
          { id: 'positiveResponseReceived', label: 'Positive response received from all utilities?', type: 'yes-no-na', required: true, options: yesNoNaOptions, },
          { id: 'marksVisibleOnSite', label: 'Locate marks visible and understood on site?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'lineOwnersNotified', label: 'Line owners / operators notified (list)', type: 'multiline', required: true },
          { id: 'privateLocatesCompleted', label: 'Private / facility locates completed (beyond 811)?', type: 'yes-no-na', required: true, options: yesNoNaOptions, },
          {
            id: 'locateMethods',
            label: 'Locate methods used',
            type: 'checkbox-group',
            options: [
              { value: 'electromagnetic', label: 'Electromagnetic / radio detection' },
              { value: 'gpr', label: 'Ground penetrating radar (GPR)' },
              { value: 'as-builts', label: 'As-builts / drawings' },
              { value: 'pot-hole', label: 'Pothole / soft dig expose' },
              { value: 'probe', label: 'Probe / rod' },
            ], helperText: 'Enter full 811 / Dig Alert ticket number.',
          },
        ],
      },
      {
        id: 'known-encumbrances',
        title: 'Known Lines & Encumbrances',
        fields: [
          {
            id: 'knownUtilities',
            label: 'Known utilities / lines in area',
            type: 'checkbox-group',
            options: [
              { value: 'electric', label: 'Electric' },
              { value: 'gas', label: 'Gas' },
              { value: 'oil-product', label: 'Oil / product' },
              { value: 'produced-water', label: 'Produced water' },
              { value: 'water', label: 'Fresh water' },
              { value: 'fiber-comm', label: 'Fiber / communications' },
              { value: 'sewer', label: 'Sewer' },
              { value: 'unknown', label: 'Unknown / congested corridor' },
              { value: 'none-identified', label: 'None identified after locate' },
            ],
          },
          { id: 'lineOwnerOperator', label: 'Primary line owner / operator', type: 'text' },
          { id: 'toleranceZonePlan', label: 'Tolerance zone / hand-dig plan', type: 'multiline', required: true, helperText: 'Hand dig / soft dig within X feet of marks before mechanical.' },
          { id: 'daylightingRequired', label: 'Daylighting / positive identification required before mechanical dig?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          {
            id: 'relatedForms',
            label: 'Linked forms (hydrovac / excavation / JSA)',
            type: 'linked-forms',
            placeholder: 'Hydrovac checklist · Excavation entry · JSA #',
            helperText: 'If soft dig is used, open Hydrovac / Daylighting Checklist from the chips.',
            linkedTemplateIds: [
              'hydrovac-daylighting-checklist',
              'excavation-trenching-entry-checklist',
              'oxy-job-safety-plan-checklist',
            ],
          },
        ],
      },
      {
        id: 'controls-safety',
        title: 'Controls & Site Safety',
        fields: [
          { id: 'spotterAssigned', label: 'Spotter assigned for mechanical equipment?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'barricadesTraffic', label: 'Barricades / traffic control in place?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'spoilPlacementSafe', label: 'Spoil placement keeps edge clear / stable?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'h2sOrAtmosphereConcern', label: 'H₂S / atmospheric concern in area?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'emergencyContact', label: 'Emergency contact / dispatch', type: 'text', required: true, },
          { id: 'requiredPpe', label: 'Required PPE', type: 'multiline', required: true },
          { id: 'controlNotes', label: 'Additional controls / notes', type: 'multiline' },
        ],
      },
      {
        id: 'authorization',
        title: 'Authorization',
        fields: [
          { id: 'drawingsReviewed', label: 'Drawings / maps reviewed with crew?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'crewBriefedOnMarks', label: 'Crew briefed on marks, tolerance zones, and stop-work?', type: 'yes-no', required: true, options: yesNoOptions, },
          { id: 'competentPersonSignature', label: 'Competent Person Signature', type: 'signature', required: true },
          { id: 'supervisorSignature', label: 'Supervisor / foreman signature', type: 'signature', required: true, },
          { id: 'operatorSignature', label: 'Operator / company man signature', type: 'signature' },
          { id: 'excavatorOperatorSignature', label: 'Equipment operator signature', type: 'signature' },
          { id: 'attachments', label: 'Ticket screenshots / photos of marks / maps', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'spill-environmental-release-report',
    title: 'Spill / Environmental Release Report',
    categoryId: 'environmental',
    audience: ['crew', 'foreman', 'company_man', 'driver'],
    description: 'Field spill and environmental release report — material, estimated volume, media affected, notifications, containment, and cleanup status.',
    sourceMockupFamily: 'Spill / environmental release field report',
    sections: [
      {
        id: 'report-header',
        title: 'Report Header',
        fields: [
          { id: 'dateTime', label: 'Date / time of discovery', type: 'datetime', required: true },
          { id: 'releaseStartIfKnown', label: 'Release start (if known)', type: 'datetime' },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'facilityName', label: 'Location / facility / well', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Operator / company working for', type: 'text', required: true },
          { id: 'crewCompany', label: 'Company discovering / reporting', type: 'text' },
          { id: 'reportedBy', label: 'Reported by', type: 'text', required: true },
          { id: 'reporterPhone', label: 'Reporter phone', type: 'text', required: true },
          { id: 'gpsOrLegalDescription', label: 'GPS / legal description', type: 'text' },
          {
            id: 'relatedForms',
            label: 'Linked incident / JSA / work order',
            type: 'linked-forms',
            placeholder: 'Incident report · JSA #',
            helperText: 'If injuries occurred, open First Report of Incident from the chips.',
            linkedTemplateIds: [
              'first-report-of-incident',
              'near-miss-report',
              'oxy-job-safety-plan-checklist',
            ],
          },
        ],
      },
      {
        id: 'release-description',
        title: 'Release Description',
        fields: [
          {
            id: 'materialReleased',
            label: 'Material(s) released',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'crude-oil', label: 'Crude oil' },
              { value: 'condensate', label: 'Condensate' },
              { value: 'produced-water', label: 'Produced water / brine' },
              { value: 'diesel-fuel', label: 'Diesel / fuel' },
              { value: 'lube-oil', label: 'Lube oil / hydraulic' },
              { value: 'chemical', label: 'Chemical / additive' },
              { value: 'drilling-mud', label: 'Drilling mud / cuttings' },
              { value: 'other', label: 'Other (describe below)' },
            ],
          },
          { id: 'materialDescription', label: 'Material description / product name', type: 'text', required: true },
          { id: 'estimatedVolume', label: 'Estimated volume released', type: 'text', required: true, placeholder: 'e.g. 5 bbl, 20 gal' },
          { id: 'volumeRecovered', label: 'Volume recovered so far', type: 'text' },
          { id: 'releaseOngoing', label: 'Is the release ongoing / controlled?', type: 'select', required: true, options: [
            { value: 'ongoing', label: 'Ongoing — not yet controlled' },
            { value: 'controlled', label: 'Source controlled / stopped' },
            { value: 'unknown', label: 'Unknown' },
          ] },
          { id: 'whatHappened', label: 'What happened? (sequence of events)', type: 'multiline', required: true },
          { id: 'sourceEquipment', label: 'Source equipment / line / tank', type: 'text', required: true },
        ],
      },
      {
        id: 'media-impact',
        title: 'Media & Impact',
        fields: [
          {
            id: 'mediaAffected',
            label: 'Media affected',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'soil', label: 'Soil' },
              { value: 'containment', label: 'Secondary containment' },
              { value: 'pad-gravel', label: 'Pad / gravel' },
              { value: 'surface-water', label: 'Surface water / creek' },
              { value: 'groundwater', label: 'Groundwater (suspected)' },
              { value: 'vegetation', label: 'Vegetation' },
              { value: 'wildlife', label: 'Wildlife impact observed' },
              { value: 'air-vapor', label: 'Air / vapor' },
              { value: 'off-lease', label: 'Off-lease / third-party property' },
            ], helperText: 'Use best field estimate; update when measured.',
          },
          { id: 'areaAffected', label: 'Approximate area affected', type: 'text', placeholder: 'e.g. 20 ft × 30 ft' },
          { id: 'nearestWaterDistance', label: 'Distance to nearest water body / drainage', type: 'text' },
          { id: 'injuriesOrExposure', label: 'Injuries or human exposure?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'injuryDescription', label: 'If yes, describe injury / exposure', type: 'multiline' },
          { id: 'potentialInjuryDamage', label: 'Potential environmental damage if not corrected', type: 'multiline' },
        ],
      },
      {
        id: 'immediate-response',
        title: 'Immediate Response',
        fields: [
          { id: 'immediateActionTaken', label: 'Immediate actions taken', type: 'multiline', required: true },
          { id: 'sourceIsolated', label: 'Source isolated / shut in?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'containmentInPlace', label: 'Containment / berms / absorbent deployed?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'cleanupContractorCalled', label: 'Cleanup contractor called?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'cleanupContractorName', label: 'Cleanup contractor / vac truck company', type: 'text' },
          { id: 'wasteDestination', label: 'Waste / recovered fluids destination', type: 'text', placeholder: 'SWD, landfill, recycler…' },
        ],
      },
      {
        id: 'notifications',
        title: 'Notifications',
        description: 'Record company and agency notifications with times. Follow operator notification matrix.',
        fields: [
          { id: 'companyManNotified', label: 'Company man / operator notified?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'supervisorNotified', label: 'Supervisor / EHS notified?', type: 'yes-no', required: true, options: yesNoOptions, },
          { id: 'agencyNotified', label: 'Agency notified (NRC / state / local)?', type: 'yes-no-na', required: true, options: yesNoNaOptions, },
          { id: 'notificationsMade', label: 'Notifications made (who / when / #)', type: 'multiline', required: true },
          { id: 'reportableQuantitySuspect', label: 'Suspect reportable quantity / regulatory report required?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'nrcOrAgencyCaseNumber', label: 'NRC / agency case or report #', type: 'text' },
        ],
      },
      {
        id: 'follow-up-signoff',
        title: 'Follow-Up & Sign-Off',
        fields: [
          { id: 'cleanupStatus', label: 'Cleanup status', type: 'select', required: true, options: [
            { value: 'in-progress', label: 'In progress' },
            { value: 'complete', label: 'Complete' },
            { value: 'pending-contractor', label: 'Pending contractor' },
            { value: 'monitoring', label: 'Monitoring / sampling pending' },
          ] },
          { id: 'recommendedCorrectiveAction', label: 'Recommended corrective / preventive actions', type: 'multiline' },
          { id: 'followUpOwner', label: 'Follow-up owner', type: 'text' },
          { id: 'additionalComments', label: 'Additional comments', type: 'multiline' },
          { id: 'reporterSignature', label: 'Reporter Signature', type: 'signature', required: true },
          { id: 'supervisorSignature', label: 'Supervisor / EHS Signature', type: 'signature' },
          { id: 'attachments', label: 'Photos / maps / sample labels', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'working-alone-check-in-plan',
    title: 'Working Alone / Check-In Plan',
    categoryId: 'safety-programs',
    audience: ['crew', 'foreman', 'driver'],
    description: 'Lone-worker plan — task, location, check-in schedule, emergency contacts, and overdue response steps.',
    sourceMockupFamily: 'Working alone / journey-adjacent check-in plan',
    sections: [
      {
        id: 'worker-task',
        title: 'Worker & Task',
        fields: [
          { id: 'dateTime', label: 'Plan start date / time', type: 'datetime', required: true },
          { id: 'expectedEndTime', label: 'Expected end date / time', type: 'datetime', required: true },
          { id: 'employeeName', label: 'Worker name', type: 'text', required: true },
          { id: 'employeePhone', label: 'Worker phone / radio', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company working for', type: 'text', required: true },
          { id: 'crewCompany', label: 'Employer / contractor company', type: 'text' },
          { id: 'jobTitle', label: 'Job title / craft', type: 'text' },
          { id: 'workActivity', label: 'Work activity while alone', type: 'multiline', required: true },
          { id: 'workOrderOrJob', label: 'Work order / job #', type: 'text' },
        ],
      },
      {
        id: 'location-travel',
        title: 'Location & Travel',
        fields: [
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'facilityName', label: 'Facility / well / route details', type: 'text', required: true },
          { id: 'gpsOrLegalDescription', label: 'GPS / meeting point if overdue', type: 'text', helperText: 'Required to send a company check-in plan. Enter the pad entrance or agreed meeting point.' },
          { id: 'vehicleUnit', label: 'Vehicle / unit #', type: 'text' },
          { id: 'attachments', label: 'Route map / unit photo (optional)', type: 'attachment', helperText: 'Photo of unit and pad entrance helps welfare checks.' },
          { id: 'travelRoute', label: 'Travel route / roads', type: 'multiline', placeholder: 'From yard via lease road…' },
          { id: 'weatherConditions', label: 'Expected weather / road conditions', type: 'text' },
          { id: 'h2sOrSpecialHazards', label: 'H₂S / special site hazards', type: 'multiline' },
        ],
      },
      {
        id: 'check-in-protocol',
        title: 'Check-In Protocol',
        description: 'Monitor must accept this plan before work starts. Follow the agreed response steps manually; automatic overdue alerts are not configured.',
        fields: [
          { id: 'checkInInterval', label: 'Check-in interval', type: 'select', required: true, options: [
            { value: '30-min', label: 'Every 30 minutes' },
            { value: '60-min', label: 'Every 60 minutes' },
            { value: '2-hour', label: 'Every 2 hours' },
            { value: 'task-milestones', label: 'At task milestones only' },
            { value: 'other', label: 'Other (describe below)' },
          ] },
          { id: 'checkInMethod', label: 'Check-in method', type: 'select', required: true, options: [
            { value: 'phone-call', label: 'Phone call' },
            { value: 'text-sms', label: 'Text / SMS' },
            { value: 'radio', label: 'Radio' },
            { value: 'app-message', label: 'In-app / team message' },
            { value: 'other', label: 'Other' },
          ] },
          { id: 'checkInContactName', label: 'Check-in contact (monitor) name', type: 'text', required: true },
          { id: 'checkInContactPhone', label: 'Check-in contact phone', type: 'text', required: true },
          { id: 'backupContactName', label: 'Backup contact name', type: 'text', required: true },
          { id: 'backupContactPhone', label: 'Backup contact phone', type: 'text', required: true },
          { id: 'missedCheckInMinutes', label: 'Minutes late before escalation', type: 'number', required: true, placeholder: 'e.g. 15' },
          { id: 'escalationSteps', label: 'Overdue / no-response steps', type: 'multiline', required: true, helperText: 'Who to call, when to drive out, when to call 911 / dispatch.' },
          {
            id: 'liveCheckInLog',
            label: 'Live check-in log',
            type: 'multiline',
            helperText: 'Company confirmations are recorded here. The company check-in activity log retains monitoring and follow-up events; manual notes can be added below.',
            placeholder: 'Timestamps and OK / overdue events…',
          },
        ],
      },
      {
        id: 'controls-fitness',
        title: 'Controls & Fitness',
        fields: [
          { id: 'fitForDuty', label: 'Worker fit for duty / not fatigued?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'requiredPpe', label: 'PPE / equipment taken', type: 'multiline', required: true, },
          { id: 'communicationTested', label: 'Phone / radio coverage tested for this location?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'deadZonePlan', label: 'Dead-zone / no-signal plan', type: 'multiline', placeholder: 'Move to high ground, use satellite, pre-set return time…' },
          { id: 'stopWorkTriggers', label: 'Stop-work / return-to-base triggers', type: 'multiline' },
          { id: 'additionalComments', label: 'Additional notes', type: 'multiline' },
        ],
      },
      {
        id: 'acknowledgement',
        title: 'Acknowledgement',
        fields: [
          { id: 'workerUnderstandsPlan', label: 'Worker understands check-in and escalation plan?', type: 'yes-no', required: true, options: yesNoOptions, },
          { id: 'monitorUnderstandsPlan', label: 'Monitor understands overdue response?', type: 'yes-no', required: true, options: yesNoOptions, },
          { id: 'workerSignature', label: 'Worker Signature', type: 'signature', required: true },
          { id: 'monitorSignature', label: 'Monitor / Supervisor Signature', type: 'signature', required: true },
          { id: 'signoffDate', label: 'Date', type: 'date', required: true, },
        ],
      },
    ],
  },

  // ── Tier 2 A–I ────────────────────────────────────────────────────────
  {
    id: 'h2s-atmospheric-gas-testing-log',
    title: 'H₂S / Atmospheric Gas Testing Log',
    categoryId: 'confined-space',
    audience: ['crew'],
    description: 'Gas monitor log for H₂S and multi-gas atmospheric testing — calibration, readings over time, and actions taken.',
    sourceMockupFamily: 'H2S / atmospheric gas testing field log',
    sections: [
      {
        id: 'log-header',
        title: 'Log Header',
        fields: [
          { id: 'dateTime', label: 'Date / start time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'facilityName', label: 'Location / facility / well', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company working for', type: 'text', required: true },
          { id: 'testerName', label: 'Tester / competent person', type: 'text', required: true },
          { id: 'workActivity', label: 'Work activity requiring gas testing', type: 'multiline', required: true },
          {
            id: 'relatedForms',
            label: 'Linked CSE permit / JSA #',
            type: 'linked-forms',
            placeholder: 'Confined Space Entry Permit · JSA #',
            helperText: 'If supporting a confined space entry, open the CSE permit from the chips.',
            linkedTemplateIds: [
              'confined-space-entry-permit',
              'oxy-job-safety-plan-checklist',
            ],
          },
        ],
      },
      {
        id: 'instrument',
        title: 'Instrument',
        fields: [
          { id: 'monitorMakeModel', label: 'Monitor make / model / serial', type: 'text', required: true },
          { id: 'monitorCalibrated', label: 'Bump test / calibration current?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'calibrationGasLot', label: 'Calibration gas / lot (if applicable)', type: 'text' },
          { id: 'alarmSetPoints', label: 'Alarm set-points (H₂S / LEL / O₂ / CO)', type: 'multiline', required: true },
        ],
      },
      {
        id: 'readings-log',
        title: 'Readings Log',
        description: 'Add a row for each sample (pre-work, interval, mid, bottom, post). Required: at least one complete reading row.',
        fields: [
          {
            id: 'gasReadings',
            label: 'Gas readings table',
            type: 'gas-readings-log',
            required: true,
            helperText: 'Tap + Reading to add rows. Capture time, location, O₂ / LEL / H₂S / CO, and notes.',
          },
          { id: 'otherGasReadings', label: 'Other gas / VOC readings', type: 'multiline' },
          { id: 'atmosphereAcceptable', label: 'Atmosphere acceptable for work / entry?', type: 'yes-no', options: yesNoOptions, required: true },
        ],
      },
      {
        id: 'actions-signoff',
        title: 'Actions & Sign-Off',
        fields: [
          { id: 'alarmsOrExcursions', label: 'Alarms / excursions during work?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'immediateActionTaken', label: 'If yes, actions taken', type: 'multiline' },
          { id: 'continuousMonitoring', label: 'Continuous monitoring used?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'testerSignature', label: 'Tester Signature', type: 'signature', required: true },
          { id: 'supervisorSignature', label: 'Supervisor Signature', type: 'signature' },
          { id: 'attachments', label: 'Monitor printouts / photos', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'hydrovac-daylighting-checklist',
    title: 'Hydrovac / Daylighting Checklist',
    categoryId: 'excavation',
    audience: ['crew'],
    description: 'Soft-dig / hydrovac daylighting checklist — one-call status, spotter, spoil handling, and positive line identification.',
    sourceMockupFamily: 'Hydrovac daylighting field checklist',
    sections: [
      {
        id: 'job-header',
        title: 'Job Header',
        fields: [
          { id: 'dateTime', label: 'Date / time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'facilityName', label: 'Location / facility', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company working for', type: 'text', required: true },
          { id: 'crewCompany', label: 'Hydrovac company / crew', type: 'text', required: true },
          { id: 'operatorName', label: 'Hydrovac operator name', type: 'text', required: true },
          { id: 'scopeOfWork', label: 'Scope of daylighting', type: 'multiline', required: true },
          {
            id: 'relatedForms',
            label: 'Linked ground disturbance / one-call form',
            type: 'linked-forms',
            placeholder: 'Ground Disturbance / One-Call ticket #',
            helperText: 'Complete Ground Disturbance / One-Call before mechanical work.',
            linkedTemplateIds: [
              'ground-disturbance-one-call',
              'excavation-trenching-entry-checklist',
            ],
          },
        ],
      },
      {
        id: 'locates',
        title: 'Locates & Marks',
        description: 'Do not daylight if ticket is expired or marks are not visible.',
        fields: [
          { id: 'oneCallDigAlertCompleted', label: '811 One-Call ticket active?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'oneCallConfirmationNumber', label: 'One-call ticket #', type: 'text', required: true },
          { id: 'marksVisibleOnSite', label: 'Locate marks visible and briefed?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'lineOwnersNotified', label: 'Known lines / owners in dig area', type: 'multiline', required: true },
          { id: 'daylightingRequired', label: 'Positive ID required before mechanical dig?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
        ],
      },
      {
        id: 'operation-controls',
        title: 'Operation Controls',
        fields: [
          { id: 'spotterAssigned', label: 'Spotter assigned?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'pressureAndTempChecked', label: 'Unit pressure / water temp appropriate for soil & lines?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'spoilContainment', label: 'Spoil containment / dump location controlled?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'barricadesTraffic', label: 'Barricades / traffic control in place?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'h2sOrAtmosphereConcern', label: 'H₂S / atmospheric concern?', type: 'yes-no-na', options: yesNoNaOptions },
          { id: 'requiredPpe', label: 'Required PPE', type: 'multiline', required: true },
          { id: 'emergencyContact', label: 'Emergency contact / dispatch', type: 'text', required: true, },
        ],
      },
      {
        id: 'results-signoff',
        title: 'Results & Sign-Off',
        fields: [
          { id: 'linesExposed', label: 'Lines / facilities exposed / identified', type: 'multiline', required: true },
          { id: 'damageOrNearMiss', label: 'Damage or near miss during dig?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'problemsDuringEntry', label: 'If yes / other issues, describe', type: 'multiline' },
          { id: 'operatorSignature', label: 'Hydrovac Operator Signature', type: 'signature', required: true },
          { id: 'competentPersonSignature', label: 'Competent Person / Spotter Signature', type: 'signature', required: true },
          { id: 'attachments', label: 'Photos of marks / exposed lines', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'pressure-test-hydrotest-record',
    title: 'Pressure Test / Hydrotest Record',
    categoryId: 'equipment-inspections',
    audience: ['crew', 'foreman'],
    description: 'Hydrostatic or pressure test record — system ID, test medium, pressures, hold times, results, and sign-off.',
    sourceMockupFamily: 'Pressure test / hydrotest field record',
    sections: [
      {
        id: 'system-header',
        title: 'System & Job',
        fields: [
          { id: 'dateTime', label: 'Test date / time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'facilityName', label: 'Facility / system location', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company working for', type: 'text', required: true },
          { id: 'equipmentDescription', label: 'Equipment / line / vessel under test', type: 'multiline', required: true },
          { id: 'workOrderOrJob', label: 'Work order / job #', type: 'text' },
          {
            id: 'relatedForms',
            label: 'Linked LOTO / GD / work pack',
            type: 'linked-forms',
            placeholder: 'LOTO # · work pack #',
            helperText: 'Reference energy control completed before test.',
            linkedTemplateIds: [
              'lockout-tagout',
              'oxy-lock-out-tag-out',
              'ground-disturbance-one-call',
            ],
          },
          { id: 'testerName', label: 'Tester / lead technician', type: 'text', required: true },
        ],
      },
      {
        id: 'test-parameters',
        title: 'Test Parameters',
        fields: [
          {
            id: 'testType',
            label: 'Test type',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'hydrostatic', label: 'Hydrostatic' },
              { value: 'pneumatic', label: 'Pneumatic (air / N₂)' },
              { value: 'service-test', label: 'Service / leak test' },
              { value: 'other', label: 'Other (notes)' },
            ],
          },
          { id: 'testMedium', label: 'Test medium', type: 'text', required: true, placeholder: 'Water, inhibited water, N₂, air…' },
          { id: 'designPressure', label: 'Design / MAWP pressure', type: 'text', required: true },
          { id: 'testPressure', label: 'Test pressure', type: 'text', required: true },
          { id: 'holdTime', label: 'Hold time', type: 'text', required: true, placeholder: 'e.g. 30 min' },
          { id: 'gaugeSerial', label: 'Gauge ID / serial / calibration due', type: 'text', required: true },
          { id: 'isolationPoints', label: 'Isolation / blinds / LOTO verified', type: 'multiline', required: true },
        ],
      },
      {
        id: 'results',
        title: 'Results',
        description: 'Document start/end pressures, hold time met, and any leaks with location.',
        fields: [
          { id: 'startPressure', label: 'Start pressure', type: 'text', required: true },
          { id: 'endPressure', label: 'End pressure', type: 'text', required: true },
          { id: 'pressureDrop', label: 'Pressure drop / change', type: 'text', required: true },
          { id: 'leaksObserved', label: 'Leaks or defects observed?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'leakDescription', label: 'If yes, describe location / severity', type: 'multiline' },
          { id: 'testResult', label: 'Overall result', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'acceptanceCriteria', label: 'Acceptance criteria (max drop / hold)', type: 'text', required: true, placeholder: 'e.g. ≤5 psi drop in 30 min', helperText: 'State the pass/fail rule used.' },
          { id: 'additionalComments', label: 'Comments / acceptance criteria notes', type: 'multiline' },
        ],
      },
      {
        id: 'signoff',
        title: 'Sign-Off',
        fields: [
          { id: 'testerSignature', label: 'Tester Signature', type: 'signature', required: true },
          { id: 'supervisorSignature', label: 'Supervisor / inspector signature', type: 'signature', required: true },
          { id: 'operatorSignature', label: 'Operator representative signature', type: 'signature' },
          { id: 'attachments', label: 'Charts / photos / gauge certs', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'short-service-employee-mentorship',
    title: 'Short Service Employee (SSE) Mentorship',
    categoryId: 'crew-sign-off',
    audience: ['foreman', 'hse'],
    description: 'SSE / new or short-service employee mentorship plan — mentor assignment, restrictions, and daily check-ins.',
    sourceMockupFamily: 'SSE mentorship field form',
    sections: [
      {
        id: 'sse-identity',
        title: 'SSE Identity',
        fields: [
          { id: 'dateTime', label: 'Plan date', type: 'datetime', required: true },
          { id: 'employeeName', label: 'SSE / short-service employee name', type: 'text', required: true },
          { id: 'employeePhone', label: 'SSE phone', type: 'text' },
          { id: 'crewCompany', label: 'Employer / contractor company', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company working for (operator)', type: 'text', required: true },
          { id: 'jobTitle', label: 'Job title / craft', type: 'text', required: true },
          { id: 'daysWithCompany', label: 'Days / months with company or craft', type: 'text', required: true },
          { id: 'sseReason', label: 'Why SSE status applies', type: 'multiline', required: true, placeholder: 'New hire, new craft, returned after absence…' },
        ],
      },
      {
        id: 'mentor-assignment',
        title: 'Mentor Assignment',
        fields: [
          { id: 'mentorName', label: 'Assigned mentor name', type: 'text', required: true },
          { id: 'mentorPhone', label: 'Mentor phone / radio', type: 'text', required: true },
          { id: 'supervisorName', label: 'Supervisor overseeing SSE', type: 'text', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'workActivity', label: 'Planned work activities', type: 'multiline', required: true },
          { id: 'attachments', label: 'SSE card / training proof photos', type: 'attachment', helperText: 'Photo of SSE identifier (hard hat sticker/vest) optional but recommended.' },
        ],
      },
      {
        id: 'restrictions-controls',
        title: 'Restrictions & Controls',
        description: 'Restrictions stay in force until supervisor formally removes SSE status.',
        fields: [
          {
            id: 'sseRestrictions',
            label: 'SSE restrictions in effect',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'no-alone', label: 'May not work alone' },
              { value: 'mentor-present', label: 'Mentor present for critical tasks' },
              { value: 'no-energized', label: 'No energized electrical work' },
              { value: 'no-confined', label: 'No confined space entry without extra approval' },
              { value: 'no-critical-lift', label: 'No critical lift roles' },
              { value: 'green-hard-hat', label: 'SSE identifier (hard hat / vest) worn' },
              { value: 'other', label: 'Other (notes)' },
            ],
          },
          { id: 'requiredPpe', label: 'PPE / training verified for this work', type: 'multiline', required: true },
          { id: 'stopWorkTriggers', label: 'Stop-work / escalate if…', type: 'multiline', required: true },
          { id: 'additionalComments', label: 'Additional notes', type: 'multiline' },
        ],
      },
      {
        id: 'acknowledgement',
        title: 'Acknowledgement',
        fields: [
          { id: 'sseUnderstands', label: 'SSE understands restrictions and mentor plan?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'mentorUnderstands', label: 'Mentor accepts responsibility?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'sseSignature', label: 'SSE Signature', type: 'signature', required: true },
          { id: 'mentorSignature', label: 'Mentor Signature', type: 'signature', required: true },
          { id: 'supervisorSignature', label: 'Supervisor Signature', type: 'signature', required: true },
        ],
      },
    ],
  },
  {
    id: 'heat-cold-stress-assessment',
    title: 'Heat Stress / Cold Stress Assessment',
    categoryId: 'safety-programs',
    audience: ['crew', 'foreman', 'driver'],
    description: 'Weather-related stress assessment — conditions, workload, controls, hydration/rest, and work/rest decisions.',
    sourceMockupFamily: 'Heat / cold stress field assessment',
    sections: [
      {
        id: 'assessment-header',
        title: 'Assessment Header',
        fields: [
          { id: 'dateTime', label: 'Date / time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'facilityName', label: 'Work location', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company working for', type: 'text', required: true },
          { id: 'assessorName', label: 'Assessor / crew lead', type: 'text', required: true },
          {
            id: 'stressType',
            label: 'Stress type',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'heat', label: 'Heat stress' },
              { value: 'cold', label: 'Cold stress' },
              { value: 'both', label: 'Both / changing weather' },
            ],
          },
        ],
      },
      {
        id: 'conditions',
        title: 'Conditions & Workload',
        fields: [
          { id: 'weatherConditions', label: 'Weather / temperature / wind / humidity', type: 'text', required: true },
          { id: 'workActivity', label: 'Work activity / exertion level', type: 'multiline', required: true },
          { id: 'ppeImpact', label: 'PPE / clothing impact (FR, rain gear, arc suits…)', type: 'multiline', required: true },
          { id: 'shadeShelterAvailable', label: 'Shade / shelter / warming area available?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'waterElectrolytesAvailable', label: 'Water / electrolytes / warm fluids available?', type: 'yes-no', options: yesNoOptions, required: true },
        ],
      },
      {
        id: 'controls-decision',
        title: 'Controls & Decision',
        description: 'Reassess if weather or PPE load changes mid-shift.',
        fields: [
          {
            id: 'controlsInPlace',
            label: 'Controls in place',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'work-rest', label: 'Work / rest schedule' },
              { value: 'buddy-system', label: 'Buddy system' },
              { value: 'acclimatization', label: 'Acclimatization plan' },
              { value: 'cooling-warming', label: 'Cooling / warming breaks' },
              { value: 'task-rotation', label: 'Task rotation' },
              { value: 'reschedule', label: 'Reschedule heavy work' },
              { value: 'stop-work', label: 'Stop work / leave area' },
            ],
          },
          { id: 'workRestPlan', label: 'Work / rest plan details', type: 'multiline', required: true },
          { id: 'symptomsWatch', label: 'Symptoms to watch / stop-work triggers', type: 'multiline', required: true },
          { id: 'emergencyContact', label: 'Emergency contact / medical plan', type: 'text', required: true },
          { id: 'attachments', label: 'Weather screenshot / briefing photo', type: 'attachment' },
          { id: 'fitForDuty', label: 'Crew fit to continue under this plan?', type: 'yes-no', options: yesNoOptions, required: true },
        ],
      },
      {
        id: 'signoff',
        title: 'Sign-Off',
        fields: [
          { id: 'crewBriefed', label: 'Crew briefed on plan?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'assessorSignature', label: 'Assessor Signature', type: 'signature', required: true },
          { id: 'supervisorSignature', label: 'Supervisor Signature', type: 'signature' },
          { id: 'participants', label: 'Crew members briefed', type: 'participants' },
        ],
      },
    ],
  },
  {
    id: 'scaffold-ladder-access-inspection',
    title: 'Scaffold / Ladder / Access Inspection',
    categoryId: 'work-at-heights',
    audience: ['crew', 'hse'],
    description: 'Inspection of scaffolds, ladders, and access equipment before use — tags, defects, and competent person sign-off.',
    sourceMockupFamily: 'Scaffold ladder access inspection',
    sections: [
      {
        id: 'inspection-header',
        title: 'Inspection Header',
        fields: [
          { id: 'dateTime', label: 'Inspection date / time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'facilityName', label: 'Location / area', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company working for', type: 'text', required: true },
          { id: 'inspectorName', label: 'Competent person / inspector', type: 'text', required: true },
          {
            id: 'accessEquipmentType',
            label: 'Equipment type',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'scaffold', label: 'Scaffold' },
              { value: 'extension-ladder', label: 'Extension ladder' },
              { value: 'step-ladder', label: 'Step ladder' },
              { value: 'stair-tower', label: 'Stair tower / access platform' },
              { value: 'other', label: 'Other access equipment' },
            ],
          },
          { id: 'equipmentNumber', label: 'Equipment / tag / ID #', type: 'text', required: true },
        ],
      },
      {
        id: 'condition-checks',
        title: 'Condition Checks',
        description: 'Fail any structural item → do not use. Correct and re-inspect.',
        fields: [
          { id: 'tagCurrent', label: 'Inspection tag current / green tag present?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'structureSound', label: 'Structure / rails / planks / rungs sound?', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'baseLevelSecure', label: 'Base level, secured, on firm footing?', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'guardrailsToeboards', label: 'Guardrails / toeboards / midrails as required?', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'accessEgressClear', label: 'Access / egress clear and correct angle / setup?', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          { id: 'fallProtectionRequired', label: 'Fall protection required for this access?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'defectsFound', label: 'Defects found?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'defectDescription', label: 'If yes, describe defects / red-tag actions', type: 'multiline' },
        ],
      },
      {
        id: 'result-signoff',
        title: 'Result & Sign-Off',
        fields: [
          { id: 'inspectionResult', label: 'Safe to use?', type: 'pass-fail-na', options: passFailNaOptions, required: true },
          {
            id: 'relatedForms',
            label: 'Linked fall rescue / JSA #',
            type: 'linked-forms',
            placeholder: 'Work at Heights Fall Rescue Plan · JSA #',
            helperText: 'If elevated work proceeds, open the fall rescue plan from the chips.',
            linkedTemplateIds: [
              'work-at-heights-fall-rescue-plan',
              'oxy-job-safety-plan-checklist',
            ],
          },
          { id: 'correctionsRequired', label: 'Corrections required before use', type: 'multiline' },
          { id: 'inspectorSignature', label: 'Inspector Signature', type: 'signature', required: true },
          { id: 'supervisorSignature', label: 'Supervisor Signature', type: 'signature' },
          { id: 'attachments', label: 'Photos of setup / tags', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'critical-lift-plan',
    title: 'Critical Lift Plan',
    categoryId: 'lifting-rigging',
    audience: ['foreman', 'hse'],
    description: 'Critical lift plan — load data, crane configuration, radii, wind limits, exclusion zone, roles, and approvals beyond a standard lift.',
    sourceMockupFamily: 'Critical lift plan field form',
    sections: [
      {
        id: 'lift-header',
        title: 'Lift Header',
        fields: [
          { id: 'dateTime', label: 'Planned lift date / time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'locationOfLift', label: 'Location of lift', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company working for', type: 'text', required: true },
          { id: 'liftDirectorName', label: 'Lift director', type: 'text', required: true },
          { id: 'craneOperatorName', label: 'Crane / equipment operator', type: 'text', required: true },
          { id: 'signalPersonName', label: 'Signal person', type: 'text', required: true, },
          { id: 'whyCritical', label: 'Why this lift is critical', type: 'multiline', required: true, placeholder: '% of capacity, dual crane, over process, personnel…' },
        ],
      },
      {
        id: 'load-crane',
        title: 'Load & Crane Data',
        description: 'Verify weight and chart capacity at radius before boom is set.',
        fields: [
          { id: 'payloadName', label: 'Load description', type: 'text', required: true },
          { id: 'loadWeight', label: 'Load weight (including rigging)', type: 'text', required: true },
          { id: 'craneTypeConfig', label: 'Crane type / configuration / counterweight', type: 'multiline', required: true },
          { id: 'boomLengthRadius', label: 'Boom length / operating radius', type: 'text', required: true },
          { id: 'chartCapacityPercent', label: '% of chart capacity at radius', type: 'text', required: true },
          { id: 'riggingNotes', label: 'Rigging plan / sling config', type: 'multiline', required: true, },
          { id: 'weatherConditions', label: 'Max wind / weather limits for lift', type: 'text', required: true },
          {
            id: 'relatedForms',
            label: 'Linked pre-lift checklist / standard lift plan',
            type: 'linked-forms',
            placeholder: 'Pre-Lift Checklist completed',
            helperText: 'Open Pre-Lift Checklist or Standard Lift Plan from the chips.',
            linkedTemplateIds: [
              'pre-lift-checklist',
              'standard-lift-plan-equipment',
            ],
          },
        ],
      },
      {
        id: 'controls-roles',
        title: 'Controls & Roles',
        fields: [
          { id: 'exclusionZone', label: 'Exclusion zone / barricades plan', type: 'multiline', required: true },
          { id: 'powerLinesClearance', label: 'Power lines / overhead clearance verified?', type: 'yes-no-na', required: true, options: yesNoNaOptions, },
          { id: 'groundBearingOk', label: 'Ground bearing / outriggers / mats OK?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          { id: 'preLiftMeetingHeld', label: 'Pre-lift meeting held with all roles?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'communicationMethod', label: 'Communication method', type: 'text', required: true, },
          { id: 'emergencyContact', label: 'Emergency contact / stop-work authority', type: 'text', required: true },
        ],
      },
      {
        id: 'approvals',
        title: 'Approvals',
        fields: [
          { id: 'liftDirectorSignature', label: 'Lift Director Signature', type: 'signature', required: true },
          { id: 'operatorSignature', label: 'Crane Operator Signature', type: 'signature', required: true },
          { id: 'riggerSignature', label: 'Rigger / signal signature', type: 'signature', required: true },
          { id: 'supervisorSignature', label: 'Supervisor / company man signature', type: 'signature', required: true },
          { id: 'attachments', label: 'Load chart / drawings / photos', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'moc-field-notice',
    title: 'MOC Field Notice',
    categoryId: 'safety-programs',
    audience: ['foreman', 'hse', 'company_man'],
    description: 'Management of Change field notice — temporary or permanent change description, risks, controls, approvals, and expiration.',
    sourceMockupFamily: 'MOC field notice',
    sections: [
      {
        id: 'moc-header',
        title: 'MOC Header',
        fields: [
          { id: 'dateTime', label: 'Notice date / time', type: 'datetime', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'facilityName', label: 'Facility / system affected', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Company / operator', type: 'text', required: true },
          { id: 'requestorName', label: 'Requestor name', type: 'text', required: true },
          { id: 'mocNumber', label: 'MOC / change # (if assigned)', type: 'text' },
          {
            id: 'changeType',
            label: 'Change type',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'temporary', label: 'Temporary' },
              { value: 'permanent', label: 'Permanent' },
              { value: 'emergency', label: 'Emergency / after-hours' },
              { value: 'procedure', label: 'Procedure / process' },
              { value: 'equipment', label: 'Equipment / piping / electrical' },
              { value: 'personnel', label: 'Personnel / organization' },
            ],
          },
        ],
      },
      {
        id: 'change-details',
        title: 'Change Details',
        description: 'Temporary changes must have an expiration/review date.',
        fields: [
          { id: 'currentState', label: 'Current state / how it works today', type: 'multiline', required: true },
          { id: 'proposedChange', label: 'Proposed change', type: 'multiline', required: true },
          { id: 'reasonForChange', label: 'Reason for change', type: 'multiline', required: true },
          { id: 'expirationOrReviewDate', label: 'Temporary change expires / review date', type: 'date', required: true },
          { id: 'hazardsIntroduced', label: 'New or changed hazards', type: 'multiline', required: true },
          { id: 'controlsImplemented', label: 'Controls / mitigations for the change', type: 'multiline', required: true },
        ],
      },
      {
        id: 'impacts-approvals',
        title: 'Impacts & Approvals',
        fields: [
          {
            id: 'areasImpacted',
            label: 'Areas impacted',
            type: 'checkbox-group',
            options: [
              { value: 'operations', label: 'Operations' },
              { value: 'electrical', label: 'Electrical' },
              { value: 'process-safety', label: 'Process safety' },
              { value: 'environmental', label: 'Environmental' },
              { value: 'maintenance', label: 'Maintenance' },
              { value: 'contractors', label: 'Contractors' },
            ], helperText: 'List engineering, administrative, and PPE controls for the change.',
          },
          { id: 'trainingOrCommunication', label: 'Training / crew communication plan', type: 'multiline', required: true },
          { id: 'drawingsProceduresUpdated', label: 'Drawings / procedures to update', type: 'multiline' },
          { id: 'requestorSignature', label: 'Requestor Signature', type: 'signature', required: true },
          { id: 'supervisorSignature', label: 'Supervisor / area owner signature', type: 'signature', required: true },
          { id: 'ehsOrTechnicalSignature', label: 'EHS / technical approver signature', type: 'signature' },
          { id: 'attachments', label: 'Sketches / photos / supporting docs', type: 'attachment' },
        ],
      },
    ],
  },
  {
    id: 'visitor-site-orientation',
    title: 'Visitor / Site Orientation',
    categoryId: 'crew-sign-off',
    audience: ['foreman', 'company_man'],
    description: 'Visitor and site orientation acknowledgement — hazards, PPE, muster, emergency, and host responsibility.',
    sourceMockupFamily: 'Visitor site orientation acknowledgement',
    sections: [
      {
        id: 'visitor-info',
        title: 'Visitor Information',
        fields: [
          { id: 'dateTime', label: 'Visit date / time', type: 'datetime', required: true },
          { id: 'visitorName', label: 'Visitor name', type: 'text', required: true },
          { id: 'visitorCompany', label: 'Visitor company', type: 'text', required: true },
          { id: 'visitorPhone', label: 'Visitor phone', type: 'text', required: true },
          { id: 'purposeOfVisit', label: 'Purpose of visit', type: 'multiline', required: true },
          { id: 'hostName', label: 'Site host / escort name', type: 'text', required: true },
          { id: 'companyWorkingFor', label: 'Operator / site company', type: 'text', required: true },
          { id: 'siteConducted', label: 'Site type', type: 'text', required: true, helperText: 'Category of site (pad, battery, plant…). Specific well/API is set by Facility lookup.' },
          { id: 'facilityName', label: 'Facility / meeting location', type: 'text', required: true },
        ],
      },
      {
        id: 'orientation-topics',
        title: 'Orientation Topics Covered',
        description: 'Visitor remains with host unless site rules allow otherwise.',
        fields: [
          {
            id: 'topicsCovered',
            label: 'Topics covered',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'site-hazards', label: 'Site hazards (H₂S, traffic, SIMOPS…)' },
              { value: 'ppe', label: 'Required PPE' },
              { value: 'muster', label: 'Muster / emergency alarms' },
              { value: 'evacuation', label: 'Evacuation routes' },
              { value: 'reporting', label: 'Incident / stop-work reporting' },
              { value: 'smoking', label: 'Smoking / hot work rules' },
              { value: 'vehicles', label: 'Vehicle / speed / parking rules' },
              { value: 'restricted', label: 'Restricted areas' },
            ],
          },
          { id: 'requiredPpe', label: 'PPE visitor must wear', type: 'multiline', required: true },
          { id: 'emergencyContact', label: 'Emergency contact while on site', type: 'text', required: true },
          { id: 'musterPoint', label: 'Muster point', type: 'text', required: true },
          { id: 'specialRestrictions', label: 'Special restrictions for this visit', type: 'multiline' },
          { id: 'attachments', label: 'Site map / orientation materials photo', type: 'attachment' },
        ],
      },
      {
        id: 'acknowledgement',
        title: 'Acknowledgement',
        fields: [
          { id: 'visitorUnderstands', label: 'Visitor understands orientation and will stay with host as required?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'hostAccepts', label: 'Host accepts escort / accountability responsibility?', type: 'yes-no', options: yesNoOptions, required: true },
          { id: 'visitorSignature', label: 'Visitor Signature', type: 'signature', required: true },
          { id: 'hostSignature', label: 'Host Signature', type: 'signature', required: true },
          { id: 'signoffDate', label: 'Date', type: 'date', required: true },
        ],
      },
    ],
  },

  // ─── Hazmat & Waste Transport (hot-shot + waste management) ───────────────
  // Design rule: never ask crews to re-key an official shipping paper or EPA
  // Form 8700-22 / e-Manifest. Those legal instruments stay with the shipper /
  // generator systems. Zia Forms only do:
  //   (1) company operational tickets we own (oilfield waste trip ticket),
  //   (2) go/no-go verifications that official papers/ER info are present,
  //   (3) photo evidence + signatures for company audit.
  // Research: 49 CFR 172 Subparts C/F/G, 177.817; EPA UHWM / e-Manifest;
  // NM C-133-style oilfield liquid waste transport practice.

  {
    id: 'hazmat-shipping-paper-hotshot',
    title: 'Hazmat Load Paperwork Check',
    categoryId: 'hazmat-transport',
    audience: ['crew', 'foreman', 'driver'],
    description:
      'Verify the shipper’s official hazmat shipping paper is with the load — do not retype UN numbers or proper shipping names here. Photo the official paper for company records. This form is not a shipping paper and cannot travel as the legal copy (49 CFR 172.200–203 / 177.817).',
    sourceMockupFamily: 'DOT shipping-paper presence verification (not a substitute paper)',
    sections: [
      {
        id: 'legal-scope',
        title: 'What This Form Is',
        description:
          'The shipper (offeror) must provide the official shipping paper. The carrier must keep that paper accessible in the cab. ZiaWellNav does not create or replace that paper.',
        fields: [
          {
            id: 'driverUnderstandsNotLegalSubstitute',
            label: 'I understand this is only a company check — not the official DOT shipping paper',
            type: 'yes-no',
            required: true,
            options: yesNoOptions,
          },
          {
            id: 'willNotUseAsLegalPaper',
            label: 'I will not hand this Zia Form to inspectors as the hazmat shipping paper',
            type: 'yes-no',
            required: true,
            options: yesNoOptions,
          },
          { id: 'dateTime', label: 'Date & time of check', type: 'datetime', required: true },
          {
            id: 'relatedForms',
            label: 'Linked checks',
            type: 'linked-forms',
            helperText: 'Run hazmat go/no-go and ER info check before departing.',
            linkedTemplateIds: [
              'hazmat-driver-pretrip-checklist',
              'hazmat-emergency-response-info-check',
            ],
          },
        ],
      },
      {
        id: 'trip',
        title: 'Trip',
        fields: [
          { id: 'driverName', label: 'Driver name', type: 'text', required: true },
          { id: 'carrierName', label: 'Carrier / hot-shot company', type: 'text', required: true },
          { id: 'unitNumber', label: 'Truck unit #', type: 'text', required: true },
          { id: 'facilityName', label: 'Pickup location / facility', type: 'text', required: true },
          { id: 'destination', label: 'Destination', type: 'text', required: true },
          { id: 'companyBolOrTicket', label: 'Company dispatch / load ticket # (optional)', type: 'text' },
        ],
      },
      {
        id: 'official-paper-present',
        title: 'Official Shipping Paper Present?',
        description: 'Check the shipper’s paper. Do not retype the basic description into this app.',
        fields: [
          {
            id: 'officialPaperWithLoad',
            label: 'Official shipper shipping paper / BOL is with this load right now?',
            type: 'yes-no',
            required: true,
            options: yesNoOptions,
            helperText: 'If No — stop. Do not leave until the shipper provides the paper.',
          },
          {
            id: 'ishpVisibleOnPaper',
            label: 'Paper shows ID #, proper shipping name, hazard class, and packing group (if any)?',
            type: 'yes-no',
            required: true,
            options: yesNoOptions,
            helperText: '49 CFR 172.202 basic description — verify on the paper, do not copy fields here.',
          },
          {
            id: 'emergencyPhoneOnPaper',
            label: '24-hour emergency phone is printed on the paper?',
            type: 'yes-no',
            required: true,
            options: yesNoOptions,
          },
          {
            id: 'shipperCertificationNoted',
            label: 'Shipper certification / signature block present on the official paper?',
            type: 'yes-no-na',
            required: true,
            options: yesNoNaOptions,
          },
          {
            id: 'papersAccessibleInCab',
            label: 'I know where the paper will ride (within reach / door holder / seat per 177.817)?',
            type: 'yes-no',
            required: true,
            options: yesNoOptions,
          },
          {
            id: 'plainLanguageLoad',
            label: 'Plain-language what you are hauling (for company ops only)',
            type: 'text',
            required: true,
            placeholder: 'e.g. corrosion inhibitor totes — see shipper paper for legal description',
            helperText: 'Not a substitute for the proper shipping name on the official paper.',
          },
        ],
      },
      {
        id: 'evidence',
        title: 'Evidence & Sign-Off',
        fields: [
          {
            id: 'attachments',
            label: 'Photo of the official shipping paper (required for company records)',
            type: 'attachment',
            required: true,
            helperText: 'Clear photo of the full shipper paper. That paper stays with the load.',
          },
          { id: 'notes', label: 'Notes / issues', type: 'multiline' },
          { id: 'driverSignature', label: 'Driver signature', type: 'signature', required: true },
        ],
      },
    ],
  },

  {
    id: 'hazmat-driver-pretrip-checklist',
    title: 'Hazmat Load Go / No-Go',
    categoryId: 'hazmat-transport',
    audience: ['crew', 'foreman', 'driver'],
    description:
      'Short company go/no-go before a hazmat hot-shot leaves the pad: endorsement, official papers in cab, placards, packages, securement. Complements — does not replace — the main Pre/Post Trip at the top of Forms. Not a DOT shipping paper.',
    sourceMockupFamily: 'DOT hazmat load go/no-go company checklist',
    sections: [
      {
        id: 'trip-header',
        title: 'Trip',
        fields: [
          {
            id: 'notMainPreTrip',
            label: 'I know the main Pre/Post Trip (top of Forms) is still required separately if company policy says so',
            type: 'yes-no',
            required: true,
            options: yesNoOptions,
          },
          { id: 'dateTime', label: 'Date & time', type: 'datetime', required: true },
          { id: 'driverName', label: 'Driver name', type: 'text', required: true },
          { id: 'carrierName', label: 'Carrier / company', type: 'text', required: true },
          { id: 'unitNumber', label: 'Truck unit #', type: 'text', required: true },
          { id: 'facilityName', label: 'Origin', type: 'text', required: true },
          { id: 'destination', label: 'Destination', type: 'text', required: true },
          {
            id: 'relatedForms',
            label: 'Linked checks',
            type: 'linked-forms',
            linkedTemplateIds: [
              'hazmat-shipping-paper-hotshot',
              'hazmat-emergency-response-info-check',
            ],
          },
        ],
      },
      {
        id: 'driver-ready',
        title: 'Driver',
        fields: [
          { id: 'cdlValid', label: 'CDL valid for this vehicle?', type: 'yes-no', required: true, options: yesNoOptions },
          {
            id: 'hazmatEndorsement',
            label: 'Hazmat endorsement current if this load requires it?',
            type: 'yes-no-na',
            required: true,
            options: yesNoNaOptions,
            helperText: 'If required and No — NO-GO.',
          },
          { id: 'hoursOfServiceOk', label: 'Hours-of-service OK for this trip?', type: 'yes-no', required: true, options: yesNoOptions },
        ],
      },
      {
        id: 'papers-placards-load',
        title: 'Papers · Placards · Load',
        description: 'Do not retype legal descriptions here. Confirm presence and condition only.',
        fields: [
          { id: 'shippingPaperPresent', label: 'Official hazmat shipping paper in the cab?', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'papersReadilyAccessible', label: 'Paper easy to grab (177.817 style access)?', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'erInfoAvailable', label: 'Emergency response info available away from packages?', type: 'yes-no', required: true, options: yesNoOptions },
          {
            id: 'placardRequired',
            label: 'Placards required?',
            type: 'select',
            required: true,
            options: [
              { value: 'yes', label: 'Yes — required for this load' },
              { value: 'no', label: 'No — not required' },
              { value: 'unknown', label: 'Unknown — verify with shipper (NO-GO until known)' },
            ],
          },
          { id: 'placardsCorrectAllSides', label: 'If required: placards correct each side and each end?', type: 'yes-no-na', required: true, options: yesNoNaOptions },
          { id: 'packagesIntact', label: 'Packages / totes / cylinders intact (no leaks/damage)?', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'loadSecured', label: 'Load secured against shift?', type: 'yes-no', required: true, options: yesNoOptions },
        ],
      },
      {
        id: 'go-no-go',
        title: 'Go / No-Go',
        fields: [
          {
            id: 'goNoGo',
            label: 'Decision',
            type: 'select',
            required: true,
            options: [
              { value: 'go', label: 'GO — safe to depart' },
              { value: 'no-go', label: 'NO-GO — fix issues first' },
            ],
            helperText: 'Any required No above should be NO-GO.',
          },
          { id: 'defectsOrNotes', label: 'Holds / notes', type: 'multiline' },
          { id: 'driverSignature', label: 'Driver signature', type: 'signature', required: true },
          { id: 'attachments', label: 'Optional photos (placards / load)', type: 'attachment' },
        ],
      },
    ],
  },

  {
    id: 'hazmat-emergency-response-info-check',
    title: 'Hazmat ER Info Present Check',
    categoryId: 'hazmat-transport',
    audience: ['crew', 'foreman', 'hse', 'driver'],
    description:
      'Confirm 49 CFR 172.602 emergency response information is already with the load (on the shipping paper, SDS, ERG, or ER sheet). This checklist is not the ER information itself and does not replace SDS/ERG/shipper docs.',
    sourceMockupFamily: '49 CFR 172.602 presence verification checklist',
    sections: [
      {
        id: 'header',
        title: 'Check',
        fields: [
          {
            id: 'notTheErDocument',
            label: 'I understand this form is only a presence check — not the emergency response document',
            type: 'yes-no',
            required: true,
            options: yesNoOptions,
          },
          { id: 'dateTime', label: 'Date & time', type: 'datetime', required: true },
          { id: 'driverName', label: 'Checked by', type: 'text', required: true },
          { id: 'facilityName', label: 'Site / load reference (optional)', type: 'text' },
          {
            id: 'relatedForms',
            label: 'Linked checks',
            type: 'linked-forms',
            linkedTemplateIds: [
              'hazmat-shipping-paper-hotshot',
              'hazmat-driver-pretrip-checklist',
            ],
          },
        ],
      },
      {
        id: 'er-present',
        title: 'Is ER Info Usable in the Cab?',
        description: 'Must be in English and available away from the package (172.602). Do not retype guide text here.',
        fields: [
          {
            id: 'erInfoCarrier',
            label: 'Where is the ER info? (select all that apply)',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'shipping-paper', label: 'On the official shipping paper' },
              { value: 'sds', label: 'SDS with the papers (not sealed in a package)' },
              { value: 'erg', label: 'ERG guidebook in cab' },
              { value: 'separate', label: 'Separate ER sheet from shipper' },
            ],
          },
          { id: 'emergencyPhoneOnDocs', label: '24-hour emergency phone is on those docs?', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'canAnswerHealthFireSpill', label: 'Docs cover health hazards, fire/explosion, spill/fire actions, and first aid?', type: 'yes-no', required: true, options: yesNoOptions },
          { id: 'ergGuideNumber', label: 'ERG guide # if you use ERG (optional)', type: 'text', placeholder: 'e.g. Guide 128' },
        ],
      },
      {
        id: 'signoff',
        title: 'Pass / Fail',
        fields: [
          {
            id: 'safeToTransport',
            label: 'ER info complete enough to transport?',
            type: 'yes-no',
            required: true,
            options: yesNoOptions,
            helperText: 'If No — get shipper docs before rolling.',
          },
          { id: 'deficiencies', label: 'What’s missing (if anything)', type: 'multiline' },
          { id: 'driverSignature', label: 'Signature', type: 'signature', required: true },
          { id: 'attachments', label: 'Optional photo of SDS cover / ERG page / paper ER block', type: 'attachment' },
        ],
      },
    ],
  },

  {
    id: 'oilfield-waste-trip-ticket',
    title: 'Oilfield Waste Trip Ticket',
    categoryId: 'hazmat-transport',
    audience: ['crew', 'foreman', 'driver'],
    description:
      'Company trip ticket for produced water and non-hazardous oilfield waste (generator → transporter → SWD/recycle). This is an operational chain-of-custody record your company can keep. It is not EPA Form 8700-22 and is not for RCRA hazardous waste.',
    sourceMockupFamily: 'Oilfield non-hazardous waste / produced water company trip ticket',
    sections: [
      {
        id: 'scope',
        title: 'What This Ticket Is',
        fields: [
          {
            id: 'driverUnderstandsNotEpaManifest',
            label: 'I understand this is a company trip ticket — not an EPA hazardous waste manifest (Form 8700-22)',
            type: 'yes-no',
            required: true,
            options: yesNoOptions,
          },
          {
            id: 'wasteRegulatoryClass',
            label: 'What are you hauling?',
            type: 'select',
            required: true,
            options: [
              { value: 'produced-water', label: 'Produced water / brine → SWD or recycle' },
              { value: 'nhow', label: 'Non-hazardous oilfield waste (NHOW) / RCRA-exempt E&P' },
              { value: 'special-waste', label: 'Other non-haz industrial / special (state rules may apply)' },
              { value: 'unknown-hold', label: 'Unknown — do not haul until classified' },
              { value: 'rcra-haz', label: 'Suspect RCRA hazardous — STOP (use haz-waste process, not this ticket)' },
            ],
            helperText: 'If RCRA hazardous, stop this ticket and use your company RCRA / e-Manifest process.',
          },
          { id: 'dateTime', label: 'Date & time of load-out', type: 'datetime', required: true },
          {
            id: 'haulState',
            label: 'Primary state',
            type: 'select',
            options: [
              { value: 'NM', label: 'New Mexico' },
              { value: 'TX', label: 'Texas' },
              { value: 'both', label: 'Crosses NM / TX' },
              { value: 'other', label: 'Other' },
            ],
          },
          { id: 'ticketOrBolNumber', label: 'Company run / ticket #', type: 'text' },
        ],
      },
      {
        id: 'generator',
        title: 'Generator / Origin',
        fields: [
          { id: 'generatorName', label: 'Generator / operator', type: 'text', required: true },
          { id: 'facilityName', label: 'Origin well / facility / pad', type: 'text', required: true },
          { id: 'apiOrLease', label: 'API / lease / location ID', type: 'text' },
          { id: 'county', label: 'County', type: 'text' },
        ],
      },
      {
        id: 'transporter',
        title: 'Transporter',
        fields: [
          { id: 'transporterName', label: 'Trucking company', type: 'text', required: true },
          { id: 'driverName', label: 'Driver name', type: 'text', required: true },
          { id: 'unitNumber', label: 'Truck / tank unit #', type: 'text', required: true },
          {
            id: 'nmC133OrPermit',
            label: 'NM C-133 / state transporter permit # (if used)',
            type: 'text',
            helperText: 'Optional. Enter if your company hauls liquid oilfield waste under a state transporter approval.',
          },
        ],
      },
      {
        id: 'disposal',
        title: 'Disposal / SWD / Recycle',
        fields: [
          { id: 'disposalFacilityName', label: 'Facility name', type: 'text', required: true },
          { id: 'disposalFacilityPermit', label: 'SWD / facility permit #', type: 'text' },
          { id: 'disposalAddress', label: 'Facility address / GPS', type: 'text', required: true },
        ],
      },
      {
        id: 'load-details',
        title: 'Load',
        fields: [
          {
            id: 'wasteType',
            label: 'Waste type',
            type: 'checkbox-group',
            required: true,
            options: [
              { value: 'produced-water', label: 'Produced water / brine' },
              { value: 'flowback', label: 'Flowback' },
              { value: 'drilling-mud', label: 'Drilling mud / cuttings' },
              { value: 'tank-bottoms', label: 'Tank bottoms / BS&W' },
              { value: 'oily-solids', label: 'Oily solids / soil' },
              { value: 'workover-fluids', label: 'Workover / completion fluids' },
              { value: 'other', label: 'Other (describe below)' },
            ],
          },
          { id: 'wasteDescription', label: 'Notes (odor, solids, special handling)', type: 'multiline' },
          { id: 'estimatedVolume', label: 'Volume leaving origin', type: 'text', required: true, placeholder: 'e.g. 120 bbl' },
          { id: 'meterStart', label: 'Meter start (if metered)', type: 'text' },
          { id: 'meterEnd', label: 'Meter end (if metered)', type: 'text' },
          { id: 'h2sOrGasConcern', label: 'H₂S / vapor concern?', type: 'yes-no-na', options: yesNoNaOptions, required: true },
          {
            id: 'relatedForms',
            label: 'Linked forms',
            type: 'linked-forms',
            linkedTemplateIds: [
              'h2s-atmospheric-gas-testing-log',
              'vacuum-truck-operations-checklist',
              'spill-environmental-release-report',
            ],
          },
        ],
      },
      {
        id: 'chain-of-custody',
        title: 'Chain of Custody',
        fields: [
          { id: 'generatorSignature', label: 'Generator / lease rep (load-out)', type: 'signature', required: true },
          { id: 'driverSignature', label: 'Driver (accepted load)', type: 'signature', required: true },
          { id: 'deliveryDateTime', label: 'Delivery date & time', type: 'datetime' },
          { id: 'netVolumeReceived', label: 'Volume received at facility', type: 'text' },
          {
            id: 'disposalOutcome',
            label: 'Outcome',
            type: 'select',
            options: [
              { value: 'accepted', label: 'Accepted' },
              { value: 'partial', label: 'Partial / adjusted' },
              { value: 'rejected', label: 'Rejected' },
            ],
          },
          { id: 'disposalSignature', label: 'Facility signature', type: 'signature' },
          { id: 'notes', label: 'Notes / rejects', type: 'multiline' },
          { id: 'attachments', label: 'Photos: ticket, facility receipt', type: 'attachment' },
        ],
      },
    ],
  },

  {
    id: 'hazardous-waste-manifest-field-prep',
    title: 'RCRA Haz Waste — Do Not Haul Gate',
    categoryId: 'hazmat-transport',
    audience: ['crew', 'foreman', 'hse', 'driver'],
    description:
      'Stop-gate when waste may be RCRA hazardous. Confirms you will use EPA e-Manifest / Form 8700-22 in the legal system — this form does not create a manifest and must not be used as one. Keep required fields minimal so crews are not asked to re-key the official form.',
    sourceMockupFamily: 'RCRA hazardous waste no-haul gate (not EPA 8700-22)',
    sections: [
      {
        id: 'legal-scope',
        title: 'Legal Scope',
        description: 'Only EPA e-Manifest (or approved paper Form 8700-22 where still allowed) is the legal hazardous waste manifest.',
        fields: [
          {
            id: 'understandsEpaManifestRequired',
            label: 'I understand this Zia Form is not EPA Form 8700-22 / e-Manifest and cannot be used as the legal manifest',
            type: 'yes-no',
            required: true,
            options: yesNoOptions,
          },
          {
            id: 'willUseOfficialManifest',
            label: 'This shipment will be handled only through company RCRA / e-Manifest process (or will not haul)',
            type: 'yes-no',
            required: true,
            options: yesNoOptions,
          },
          { id: 'dateTime', label: 'Date & time', type: 'datetime', required: true },
          { id: 'preparedBy', label: 'Completed by', type: 'text', required: true },
        ],
      },
      {
        id: 'status',
        title: 'Shipment Status',
        fields: [
          {
            id: 'manifestSystem',
            label: 'Official system for this load',
            type: 'select',
            required: true,
            options: [
              { value: 'e-manifest-ready', label: 'EPA e-Manifest will be used (already set up or in progress)' },
              { value: 'e-manifest-not-ready', label: 'e-Manifest not ready — DO NOT HAUL yet' },
              { value: 'paper-8700-22', label: 'Paper Form 8700-22 only if company HSE says it is allowed' },
              { value: 'not-rcra', label: 'Confirmed not RCRA haz — use Oilfield Waste Trip Ticket instead' },
              { value: 'hold', label: 'Hold — waiting on classification / profile' },
            ],
          },
          { id: 'facilityName', label: 'Site / facility', type: 'text', required: true },
          { id: 'generatorName', label: 'Generator / operator name', type: 'text', required: true },
          {
            id: 'plainLanguageWaste',
            label: 'Plain-language waste (ops only — not the legal description)',
            type: 'text',
            required: true,
            placeholder: 'e.g. spent solvent drums — see e-Manifest for legal lines',
          },
          {
            id: 'emanifestTrackingNumber',
            label: 'e-Manifest tracking # if already created (optional)',
            type: 'text',
            helperText: 'Paste from EPA e-Manifest. Do not invent numbers here.',
          },
          {
            id: 'relatedForms',
            label: 'If not RCRA haz',
            type: 'linked-forms',
            linkedTemplateIds: ['oilfield-waste-trip-ticket'],
            helperText: 'Non-haz oilfield waste uses the trip ticket — not this gate.',
          },
        ],
      },
      {
        id: 'gate',
        title: 'Haul Decision',
        fields: [
          {
            id: 'readyToHaul',
            label: 'OK to haul under official RCRA process?',
            type: 'select',
            required: true,
            options: [
              { value: 'yes-official-ready', label: 'Yes — official manifest process is ready' },
              { value: 'no-hold', label: 'No — hold the load' },
              { value: 'not-applicable', label: 'N/A — not RCRA haz / wrong form' },
            ],
          },
          { id: 'notes', label: 'Notes for HSE / dispatcher', type: 'multiline' },
          { id: 'generatorSignature', label: 'Acknowledgment signature', type: 'signature', required: true },
          { id: 'attachments', label: 'Optional: screenshot of e-Manifest status (not a substitute manifest)', type: 'attachment' },
        ],
      },
    ],
  },
];

export const ZIA_FORM_TEMPLATES: ZiaFormTemplate[] = ZIA_FORM_TEMPLATE_DEFS.map(withSiteMediaSection);

/** Inspector / safety-guy categories — not the same stack as crew-filled pad forms. */
export const ZIA_FORMS_INSPECTOR_CATEGORY_IDS: readonly ZiaFormsCategoryId[] = [
  'safety-programs',
  'electrical-safety-audits',
] as const;

export function isZiaFormsInspectorCategory(categoryId: ZiaFormsCategoryId): boolean {
  return (ZIA_FORMS_INSPECTOR_CATEGORY_IDS as readonly string[]).includes(categoryId);
}

export function getZiaFormsCrewCategories(): ZiaFormsCategory[] {
  return ZIA_FORMS_CATEGORIES.filter((category) => !isZiaFormsInspectorCategory(category.id));
}

/** Field Safety Inspections first, then Electrical Safety Audits. */
export function getZiaFormsInspectorCategories(): ZiaFormsCategory[] {
  return ZIA_FORMS_INSPECTOR_CATEGORY_IDS.map((id) => getZiaFormsCategory(id)).filter(
    (category): category is ZiaFormsCategory => Boolean(category),
  );
}

/** Role-lens chips: EN/ES labels for the "Who's filling this out?" filter. */
export const ZIA_FORMS_AUDIENCES: Array<{
  id: ZiaFormsAudience;
  title: string;
  titleEs: string;
  hint: string;
}> = [
  { id: 'crew', title: 'Field hand', titleEs: 'Trabajador de campo', hint: 'Pre-use checks, first reports, BBS. May show up alone.' },
  { id: 'foreman', title: 'Lead', titleEs: 'Líder', hint: 'Permits, sign-offs, MOC — optional if a hand is alone' },
  { id: 'hse', title: 'Contractor safety', titleEs: 'Seguridad del contratista', hint: 'Audits, plan reviews, investigations' },
  { id: 'company_man', title: 'Company man', titleEs: 'Company man', hint: 'Operator site authority: orientation, MOC, JSA, incident' },
  { id: 'driver', title: 'Driver / hauler', titleEs: 'Chofer / acarreador', hint: 'JSA at the facility, incident, pre/post, load tickets' },
];

/** No filter, or untagged template → serves everyone. */
export function templateServesAudience(
  template: ZiaFormTemplate,
  audience: ZiaFormsAudience | null | undefined,
): boolean {
  if (!audience) return true;
  if (!template.audience || template.audience.length === 0) return true;
  return template.audience.includes(audience);
}

export function getZiaFormsByCategory(categoryId: ZiaFormsCategoryId): ZiaFormTemplate[] {
  return ZIA_FORM_TEMPLATES.filter((template) => template.categoryId === categoryId);
}

export function getZiaFormTemplate(templateId: string): ZiaFormTemplate | undefined {
  return ZIA_FORM_TEMPLATES.find((template) => template.id === templateId);
}

export function getZiaFormsCategory(categoryId: ZiaFormsCategoryId): ZiaFormsCategory | undefined {
  return ZIA_FORMS_CATEGORIES.find((category) => category.id === categoryId);
}

export function allZiaFormFields(template: ZiaFormTemplate): ZiaFormField[] {
  return template.sections.flatMap((section) => section.fields);
}

export const ZIA_FORMS_STORAGE_PREFIX = 'ziawellnav_forms_draft';

export function countZiaFormsInspectorTemplates(): number {
  return ZIA_FORM_TEMPLATES.filter((tpl) =>
    (ZIA_FORMS_INSPECTOR_CATEGORY_IDS as readonly string[]).includes(tpl.categoryId as string),
  ).length;
}

